import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../lib/api'
import { unwrapAsyncResult } from '../lib/asyncOperation'
import {
    billingStatusToneClass,
    formatBillingAuditActionLabel,
    formatBillingAuditTargetLabel,
    formatBillingModeLabel,
    formatEntitlementSourceLabel,
    formatBillingStatusLabel,
    formatPromotionRewardTargetRoleLabel,
    formatPromotionRewardTypeLabel,
    formatRefundActionLabel,
} from '../lib/billingLabels'
import { formatDate, formatDateTimeMinute } from '../lib/date'
import { ENABLE_BILLING_COUPONS, ENABLE_BILLING_POINTS } from '../config/featureFlags'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'
import PointCommerceAdminPanel from '../components/billing/PointCommerceAdminPanel'

/** 감사 로그 payload에서 구독 DTO(또는 quota 응답 내 subscription)를 꺼냅니다. */
function pickSubscriptionSnapshot(obj) {
    if (!obj || typeof obj !== 'object') return null
    if (obj.subscription && typeof obj.subscription === 'object') return obj.subscription
    if (obj.targetName != null || obj.planCode != null) return obj
    return null
}

function formatAuditFieldValue(key, val) {
    if (val == null || val === '') return '-'
    if (typeof val === 'object') {
        if (key === 'activeTrialGrant') {
            const st = val.status
            const days = val.days ?? val.daysGranted
            if (st != null) return days != null ? `${st} (${days}일)` : String(st)
            try {
                return JSON.stringify(val)
            } catch {
                return String(val)
            }
        }
        try {
            return JSON.stringify(val)
        } catch {
            return String(val)
        }
    }
    return String(val)
}

function pickAuditStatus(log) {
    return log?.afterState?.status || log?.beforeState?.status || log?.metadata?.status || ''
}

/** 구독일 부여 프리셋(일) — 복수 선택 시 합산 */
const GRANT_DAY_PRESET_OPTIONS = [7, 14, 30, 60, 90]

const SUBSCRIPTION_PAGE_SIZES = [10, 20, 50]

const BILLING_DASHBOARD_PERIOD_OPTIONS = [
    { value: 'TODAY', label: '오늘' },
    { value: 'THIS_MONTH', label: '이번달' },
    { value: 'LAST_7_DAYS', label: '최근 7일' },
    { value: 'LAST_30_DAYS', label: '최근 30일' },
    { value: 'LAST_90_DAYS', label: '최근 90일' },
    { value: 'PREVIOUS_MONTH', label: '전월' },
]

/** 운영자 과금/구독 허브(탭) 베이스 — App 라우트와 동일하게 유지 */
const BILLING_SUBSCRIPTIONS_HUB_BASE = '/subscriptions/hub'

function billingAdminHubPath(segment) {
    return `${BILLING_SUBSCRIPTIONS_HUB_BASE}/${segment}`
}

function billingDashboardQueuePath(targetMenu) {
    if (targetMenu === 'config') return '/subscriptions/config'
    if (targetMenu === 'orgs') return '/subscriptions/orgs'
    return billingAdminHubPath(targetMenu || 'status')
}
function resolveBillingAdminMenuId(pathname) {
    if (pathname === '/subscriptions/dashboard') {
        return 'dashboard'
    }
    const prefix = `${BILLING_SUBSCRIPTIONS_HUB_BASE}/`
    if (pathname.startsWith(prefix)) {
        const seg = pathname.slice(prefix.length)
        const segToId = {
            status: 'status',
            'operation-queue': 'operationQueue',
            orders: 'orders',
            refunds: 'refunds',
            'service-usage': 'serviceUsage',
            'billing-logs': 'billingLogs',
            'audit-logs': 'audit',
            coupons: 'coupons',
            points: 'points',
        }
        return segToId[seg] || 'status'
    }
    return 'status'
}

/** 목록 API만 별도 로딩·에러로 처리하는 과금 관리 탭 */
const BILLING_ADMIN_LIST_TAB_IDS = new Set(['status', 'orders', 'refunds', 'serviceUsage', 'audit', 'billingLogs'])
const SUBSCRIPTION_HUB_PLAN_CODES = new Set(['TRIAL', 'BASIC', 'ENTERPRISE'])

function billingAdminListTabActive(menuId) {
    return BILLING_ADMIN_LIST_TAB_IDS.has(menuId)
}

function hasStoreIapSignal(subscription) {
    if (!subscription) return false
    const mode = String(subscription.billingMode || '').toUpperCase()
    const source = String(subscription.entitlementSource || '').toUpperCase()
    return mode.includes('STORE')
        || mode.includes('IAP')
        || source === 'STORE_IAP'
        || Boolean(
            subscription.storeProductId
            || subscription.storeTransactionHint
            || subscription.storePurchaseTokenHint
            || subscription.storeLinkedPurchaseTokenHint
            || subscription.storeAccountIdentifierHint
            || subscription.storeProfileIdentifierHint
        )
}

function isStoreIapSubscription(subscription) {
    return Boolean(subscription && hasStoreIapSignal(subscription))
}

function formatSubscriptionBillingModeLabel(subscription) {
    if (hasStoreIapSignal(subscription)) return '인앱결제'
    return formatBillingModeLabel(subscription?.billingMode)
}

function formatSubscriptionKindLabel(subscriptionKind) {
    return String(subscriptionKind || '').toUpperCase() === 'ORG' ? '조직' : '개인'
}

function formatSubscriptionHubPlanCode(subscription) {
    const planCode = String(subscription?.planCode || '').toUpperCase()
    return SUBSCRIPTION_HUB_PLAN_CODES.has(planCode) ? planCode : '-'
}
function isStoreEntitlementEnabled(subscription) {
    return subscription?.storeEntitlementEnabled !== false
}

function isStoreEntitlementRestoreCandidate(subscription) {
    return Boolean(subscription?.storeEntitlementManaged && subscription.storeEntitlementEnabled === false)
}

function storeEntitlementMode(subscription) {
    return isStoreEntitlementEnabled(subscription) ? 'DISABLE_ENTITLEMENT' : 'ENABLE_ENTITLEMENT'
}

function storeEntitlementToneClass(subscription) {
    if (!isStoreEntitlementEnabled(subscription)) return 'text-rose-700'
    if (subscription?.storeRepresentativeEntitlement) return 'text-emerald-700'
    if (subscription?.storeDuplicateActive) return 'text-amber-700'
    return 'text-slate-600'
}

function storeLinkModeTitle(mode) {
    if (mode === 'TRANSFER') return '인앱결제 이관'
    if (mode === 'DISABLE_ENTITLEMENT') return '인앱결제 권한 제외'
    if (mode === 'ENABLE_ENTITLEMENT') return '인앱결제 권한 복구'
    return '인앱결제 연결 해제'
}

function storeLinkConfirmTextForMode(mode) {
    if (mode === 'TRANSFER') return 'TRANSFER_STORE'
    if (mode === 'DISABLE_ENTITLEMENT') return 'DISABLE_STORE_ENTITLEMENT'
    if (mode === 'ENABLE_ENTITLEMENT') return 'ENABLE_STORE_ENTITLEMENT'
    return 'UNLINK_STORE'
}

function storeLinkDefaultReason(mode) {
    if (mode === 'TRANSFER') return '서비스 계정 변경 지원'
    if (mode === 'DISABLE_ENTITLEMENT') return '중복 인앱결제 서비스 권한 제외'
    if (mode === 'ENABLE_ENTITLEMENT') return '인앱결제 서비스 권한 복구'
    return '인앱결제 계정 변경 지원'
}

function storeLinkSuccessMessage(mode) {
    if (mode === 'TRANSFER') return '인앱결제 이관이 완료되었습니다.'
    if (mode === 'DISABLE_ENTITLEMENT') return '인앱결제 권한 제외가 완료되었습니다.'
    if (mode === 'ENABLE_ENTITLEMENT') return '인앱결제 권한 복구가 완료되었습니다.'
    return '인앱결제 연결 해제가 완료되었습니다.'
}

function storeLinkEndpoint(mode, subscriptionId) {
    if (mode === 'DISABLE_ENTITLEMENT') return `/billing/admin/subscriptions/${subscriptionId}/store-entitlement/disable`
    if (mode === 'ENABLE_ENTITLEMENT') return `/billing/admin/subscriptions/${subscriptionId}/store-entitlement/enable`
    return `/billing/admin/subscriptions/${subscriptionId}/store-link/${mode === 'TRANSFER' ? 'transfer' : 'unlink'}`
}

/** axios 응답에 있으면 괄호로 덧붙여 표시 */
function pickBillingLoadDetailSuffix(e) {
    const d = e?.response?.data
    const m = (typeof d?.message === 'string' && d.message.trim())
        || (typeof d?.error === 'string' && d.error.trim())
        || ''
    if (m) return ` (${m})`
    const em = typeof e?.message === 'string' ? e.message.trim() : ''
    if (em && !/^Request failed with status code \d+$/i.test(em) && em !== 'Network Error') {
        return ` (${em})`
    }
    return ''
}

const AUDIT_SUBSCRIPTION_COMPARE_ROWS = [
    { key: 'targetType', label: '대상 유형' },
    { key: 'targetName', label: '대상 이름' },
    { key: 'orgName', label: '조직' },
    { key: 'planName', label: '플랜' },
    { key: 'planCode', label: '플랜 코드' },
    { key: 'status', label: '구독 상태', format: (v) => formatBillingStatusLabel(v, 'subscription') },
    { key: 'billingMode', label: '과금 모드', format: (v) => formatBillingModeLabel(v) },
    { key: 'startedAt', label: '시작일' },
    { key: 'endsAt', label: '종료일' },
    { key: 'nextResetAt', label: '다음 리셋' },
    { key: 'activeTrialGrant', label: 'Trial' },
]

function BillingAuditLogDetailModal({ log, onClose }) {
    if (!log) return null
    const before = log.beforeState
    const after = log.afterState
    const subBefore = pickSubscriptionSnapshot(before)
    const subAfter = pickSubscriptionSnapshot(after)
    const hasSubCompare = Boolean(subBefore || subAfter)
    const targetLine = [
        formatBillingAuditTargetLabel(log.targetType),
        log.targetId != null && log.targetId !== '' ? `#${log.targetId}` : '',
    ].filter(Boolean).join(' ')

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
            <div
                className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
                    <div>
                        <h3 className="text-lg font-semibold">이력 상세</h3>
                        <p className="mt-1 text-sm text-slate-500">{formatBillingAuditActionLabel(log.action)}</p>
                    </div>
                    <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={onClose}>
                        닫기
                    </button>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                    <InfoBlock label="시각" value={formatDateTimeMinute(log.createdAt)} />
                    <InfoBlock label="사유" value={log.reason || '-'} />
                    <div className="rounded border p-4 md:col-span-2">
                        <div className="text-slate-500">행위자</div>
                        <div className="mt-2 font-medium">{log.actorName || '-'}</div>
                        <div className="mt-1 text-xs text-slate-500 break-all">{log.actorEmail || '-'}</div>
                    </div>
                    <div className="rounded border p-4 md:col-span-2">
                        <div className="text-slate-500">대상</div>
                        <div className="mt-2 font-medium">{log.targetDisplayName || '-'}</div>
                        <div className="mt-1 text-xs text-slate-500">{targetLine || '-'}</div>
                    </div>
                </div>

                {hasSubCompare && (
                    <div className="mt-6">
                        <div className="text-sm font-semibold text-slate-800">구독 상태 (이전 → 이후)</div>
                        <p className="mt-1 text-xs text-slate-500">감사 시점에 저장된 스냅샷이며, 현재 DB 상태와 다를 수 있습니다.</p>
                        <div className="mt-3 overflow-x-auto rounded border">
                            <table className="min-w-full text-xs md:text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-3 py-2 text-left">항목</th>
                                        <th className="px-3 py-2 text-left">이전</th>
                                        <th className="px-3 py-2 text-left">이후</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const rows = AUDIT_SUBSCRIPTION_COMPARE_ROWS
                                            .map((row) => {
                                                const rawB = subBefore ? subBefore[row.key] : undefined
                                                const rawA = subAfter ? subAfter[row.key] : undefined
                                                const cellB = row.format ? row.format(rawB) : formatAuditFieldValue(row.key, rawB)
                                                const cellA = row.format ? row.format(rawA) : formatAuditFieldValue(row.key, rawA)
                                                return { row, cellB, cellA }
                                            })
                                            .filter(({ cellB, cellA }) => !(cellB === '-' && cellA === '-'))
                                        if (rows.length === 0) {
                                            return (
                                                <tr>
                                                    <td className="border-t px-3 py-4 text-center text-slate-500" colSpan={3}>
                                                        비교할 구독 필드가 없습니다. 아래 JSON을 참고하세요.
                                                    </td>
                                                </tr>
                                            )
                                        }
                                        return rows.map(({ row, cellB, cellA }) => (
                                            <tr key={row.key} className="border-t">
                                                <td className="px-3 py-2 text-slate-600">{row.label}</td>
                                                <td className="px-3 py-2 break-all text-slate-800">{cellB}</td>
                                                <td className="px-3 py-2 break-all text-slate-900">{cellA}</td>
                                            </tr>
                                        ))
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div className="mt-6">
                    <div className="text-sm font-semibold text-slate-800">저장된 변경 데이터</div>
                    <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded border bg-slate-50/80 p-3">
                            <div className="text-xs font-medium text-slate-600">이전 (before)</div>
                            <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-all text-xs text-slate-800">
                                {before == null ? '—' : JSON.stringify(before, null, 2)}
                            </pre>
                        </div>
                        <div className="rounded border bg-slate-50/80 p-3">
                            <div className="text-xs font-medium text-slate-600">이후 (after)</div>
                            <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-all text-xs text-slate-800">
                                {after == null ? '—' : JSON.stringify(after, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default function SubscriptionsPage() {
    const { show } = useToast()
    const location = useLocation()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [subscriptionListLoading, setSubscriptionListLoading] = useState(false)
    const [subscriptionLoadError, setSubscriptionLoadError] = useState('')
    const [orderListLoading, setOrderListLoading] = useState(false)
    const [orderLoadError, setOrderLoadError] = useState('')
    const [refundListLoading, setRefundListLoading] = useState(false)
    const [refundLoadError, setRefundLoadError] = useState('')
    const [auditListLoading, setAuditListLoading] = useState(false)
    const [auditLoadError, setAuditLoadError] = useState('')
    const [iapSyncLoading, setIapSyncLoading] = useState(false)
    const [iapNotificationModalOpen, setIapNotificationModalOpen] = useState(false)
    const [iapNotificationEvents, setIapNotificationEvents] = useState([])
    const [iapNotificationTotalCount, setIapNotificationTotalCount] = useState(0)
    const [iapNotificationLoading, setIapNotificationLoading] = useState(false)
    const [iapNotificationRetryingId, setIapNotificationRetryingId] = useState(null)
    const [storeConflictModalOpen, setStoreConflictModalOpen] = useState(false)
    const [storeConflictGroups, setStoreConflictGroups] = useState([])
    const [storeConflictLoading, setStoreConflictLoading] = useState(false)
    const [storeConflictSummary, setStoreConflictSummary] = useState(null)
    const [serviceUsageListLoading, setServiceUsageListLoading] = useState(false)
    const [serviceUsageLoadError, setServiceUsageLoadError] = useState('')
    const [error, setError] = useState('')
    const [dashboard, setDashboard] = useState(null)
    const [operationQueue, setOperationQueue] = useState([])
    const [dashboardPeriodPreset, setDashboardPeriodPreset] = useState('THIS_MONTH')
    const [subscriptions, setSubscriptions] = useState([])
    const [subscriptionTotalCount, setSubscriptionTotalCount] = useState(0)
    const [subscriptionTotalPages, setSubscriptionTotalPages] = useState(1)
    const [subscriptionOrgOptions, setSubscriptionOrgOptions] = useState([])
    const [plans, setPlans] = useState([])
    const [orders, setOrders] = useState([])
    const [orderTotalCount, setOrderTotalCount] = useState(0)
    const [orderTotalPages, setOrderTotalPages] = useState(1)
    const [refunds, setRefunds] = useState([])
    const [refundTotalCount, setRefundTotalCount] = useState(0)
    const [refundTotalPages, setRefundTotalPages] = useState(1)
    const [auditLogs, setAuditLogs] = useState([])
    const [auditTotalCount, setAuditTotalCount] = useState(0)
    const [auditTotalPages, setAuditTotalPages] = useState(1)
    const [serviceUsageEvents, setServiceUsageEvents] = useState([])
    const [serviceUsageTotalCount, setServiceUsageTotalCount] = useState(0)
    const [serviceUsageTotalPages, setServiceUsageTotalPages] = useState(1)
    const [auditActionOptions, setAuditActionOptions] = useState([])
    const [auditTargetTypeOptions, setAuditTargetTypeOptions] = useState([])
    const [auditProviderOptions, setAuditProviderOptions] = useState([])
    const [auditStatusOptions, setAuditStatusOptions] = useState([])
    const [selectedAuditLog, setSelectedAuditLog] = useState(null)
    const [promotions, setPromotions] = useState([])
    const [pointOverview, setPointOverview] = useState(null)
    /** 구독일 조정 모달 대상(단일·복수) */
    const [grantTargets, setGrantTargets] = useState(null)
    const [grantMode, setGrantMode] = useState('GRANT')
    const [grantDayPresetSelection, setGrantDayPresetSelection] = useState(() => new Set([7]))
    const [grantDaysExtra, setGrantDaysExtra] = useState('')
    const [grantRestoreStoreEntitlement, setGrantRestoreStoreEntitlement] = useState(false)
    const [selectedQuotaSubscription, setSelectedQuotaSubscription] = useState(null)
    const [quotaDetailLoading, setQuotaDetailLoading] = useState(false)
    const [quotaLimitDraft, setQuotaLimitDraft] = useState({})
    const [quotaLimitAdjustLoading, setQuotaLimitAdjustLoading] = useState(false)
    const [customerMemoStorageQuotaDraft, setCustomerMemoStorageQuotaDraft] = useState('')
    const [customerMemoStorageQuotaLoading, setCustomerMemoStorageQuotaLoading] = useState(false)
    const [selectedTrialHistory, setSelectedTrialHistory] = useState(null)
    const [trialHistoryLoading, setTrialHistoryLoading] = useState(false)
    const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState([])
    const [subscriptionSearch, setSubscriptionSearch] = useState('')
    const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState('ALL')
    const [subscriptionOrgFilter, setSubscriptionOrgFilter] = useState('ALL')
    const [subscriptionKindFilter, setSubscriptionKindFilter] = useState('ALL')
    const [subscriptionPlanFilter, setSubscriptionPlanFilter] = useState('ALL')
    const [subscriptionBillingModeFilter, setSubscriptionBillingModeFilter] = useState('ALL')
    const [expiryFilter, setExpiryFilter] = useState('ALL')
    const [paidStartAtFrom, setPaidStartAtFrom] = useState('')
    const [paidStartAtTo, setPaidStartAtTo] = useState('')
    const [individualEndsAtFrom, setIndividualEndsAtFrom] = useState('')
    const [individualEndsAtTo, setIndividualEndsAtTo] = useState('')
    const [appliedSubscriptionSearch, setAppliedSubscriptionSearch] = useState('')
    const [appliedSubscriptionStatusFilter, setAppliedSubscriptionStatusFilter] = useState('ALL')
    const [appliedSubscriptionOrgFilter, setAppliedSubscriptionOrgFilter] = useState('ALL')
    const [appliedSubscriptionKindFilter, setAppliedSubscriptionKindFilter] = useState('ALL')
    const [appliedSubscriptionPlanFilter, setAppliedSubscriptionPlanFilter] = useState('ALL')
    const [appliedSubscriptionBillingModeFilter, setAppliedSubscriptionBillingModeFilter] = useState('ALL')
    const [appliedExpiryFilter, setAppliedExpiryFilter] = useState('ALL')
    const [appliedPaidStartAtFrom, setAppliedPaidStartAtFrom] = useState('')
    const [appliedPaidStartAtTo, setAppliedPaidStartAtTo] = useState('')
    const [appliedIndividualEndsAtFrom, setAppliedIndividualEndsAtFrom] = useState('')
    const [appliedIndividualEndsAtTo, setAppliedIndividualEndsAtTo] = useState('')
    const [subscriptionSortKey, setSubscriptionSortKey] = useState('individualEndsAt')
    const [subscriptionSortDirection, setSubscriptionSortDirection] = useState('asc')
    const [subscriptionPageSize, setSubscriptionPageSize] = useState(10)
    const [subscriptionPage, setSubscriptionPage] = useState(1)
    const [orderSearch, setOrderSearch] = useState('')
    const [orderProductFilter, setOrderProductFilter] = useState('ALL')
    const [orderStatusFilter, setOrderStatusFilter] = useState('ALL')
    const [orderPgProviderFilter, setOrderPgProviderFilter] = useState('ALL')
    const [orderDateFrom, setOrderDateFrom] = useState('')
    const [orderDateTo, setOrderDateTo] = useState('')
    const [appliedOrderSearch, setAppliedOrderSearch] = useState('')
    const [appliedOrderProductFilter, setAppliedOrderProductFilter] = useState('ALL')
    const [appliedOrderStatusFilter, setAppliedOrderStatusFilter] = useState('ALL')
    const [appliedOrderPgProviderFilter, setAppliedOrderPgProviderFilter] = useState('ALL')
    const [appliedOrderDateFrom, setAppliedOrderDateFrom] = useState('')
    const [appliedOrderDateTo, setAppliedOrderDateTo] = useState('')
    const [orderSortKey, setOrderSortKey] = useState('paidAt')
    const [orderSortDirection, setOrderSortDirection] = useState('desc')
    const [orderPageSize, setOrderPageSize] = useState(10)
    const [orderPage, setOrderPage] = useState(1)
    const [orderProductOptions, setOrderProductOptions] = useState([])
    const [orderPgProviderOptions, setOrderPgProviderOptions] = useState([])
    const [refundSearch, setRefundSearch] = useState('')
    const [refundStatusFilter, setRefundStatusFilter] = useState('ALL')
    const [refundDateFrom, setRefundDateFrom] = useState('')
    const [refundDateTo, setRefundDateTo] = useState('')
    const [appliedRefundSearch, setAppliedRefundSearch] = useState('')
    const [appliedRefundStatusFilter, setAppliedRefundStatusFilter] = useState('ALL')
    const [appliedRefundDateFrom, setAppliedRefundDateFrom] = useState('')
    const [appliedRefundDateTo, setAppliedRefundDateTo] = useState('')
    const [refundSortKey, setRefundSortKey] = useState('createdAt')
    const [refundSortDirection, setRefundSortDirection] = useState('desc')
    const [refundPageSize, setRefundPageSize] = useState(10)
    const [refundPage, setRefundPage] = useState(1)
    const [auditSearch, setAuditSearch] = useState('')
    const [auditActionFilter, setAuditActionFilter] = useState('ALL')
    const [auditTargetTypeFilter, setAuditTargetTypeFilter] = useState('ALL')
    const [auditProviderFilter, setAuditProviderFilter] = useState('ALL')
    const [auditStatusFilter, setAuditStatusFilter] = useState('ALL')
    const [auditOrderNoFilter, setAuditOrderNoFilter] = useState('')
    const [auditPaymentIdFilter, setAuditPaymentIdFilter] = useState('')
    const [auditWebhookEventIdFilter, setAuditWebhookEventIdFilter] = useState('')
    const [auditDateFrom, setAuditDateFrom] = useState('')
    const [auditDateTo, setAuditDateTo] = useState('')
    const [appliedAuditSearch, setAppliedAuditSearch] = useState('')
    const [appliedAuditActionFilter, setAppliedAuditActionFilter] = useState('ALL')
    const [appliedAuditTargetTypeFilter, setAppliedAuditTargetTypeFilter] = useState('ALL')
    const [appliedAuditProviderFilter, setAppliedAuditProviderFilter] = useState('ALL')
    const [appliedAuditStatusFilter, setAppliedAuditStatusFilter] = useState('ALL')
    const [appliedAuditOrderNoFilter, setAppliedAuditOrderNoFilter] = useState('')
    const [appliedAuditPaymentIdFilter, setAppliedAuditPaymentIdFilter] = useState('')
    const [appliedAuditWebhookEventIdFilter, setAppliedAuditWebhookEventIdFilter] = useState('')
    const [appliedAuditDateFrom, setAppliedAuditDateFrom] = useState('')
    const [appliedAuditDateTo, setAppliedAuditDateTo] = useState('')
    const [auditSortKey, setAuditSortKey] = useState('createdAt')
    const [auditSortDirection, setAuditSortDirection] = useState('desc')
    const [auditPageSize, setAuditPageSize] = useState(10)
    const [auditPage, setAuditPage] = useState(1)
    const [serviceUsagePageSize, setServiceUsagePageSize] = useState(10)
    const [serviceUsagePage, setServiceUsagePage] = useState(1)
    const [serviceUsageSearch, setServiceUsageSearch] = useState('')
    const [serviceUsageRefundBlockingFilter, setServiceUsageRefundBlockingFilter] = useState('ALL')
    const [serviceUsageCategoryFilter, setServiceUsageCategoryFilter] = useState('ALL')
    const [serviceUsagePaidAfterFilter, setServiceUsagePaidAfterFilter] = useState('ALL')
    const [serviceUsageDateFrom, setServiceUsageDateFrom] = useState('')
    const [serviceUsageDateTo, setServiceUsageDateTo] = useState('')
    const [appliedServiceUsageSearch, setAppliedServiceUsageSearch] = useState('')
    const [appliedServiceUsageRefundBlockingFilter, setAppliedServiceUsageRefundBlockingFilter] = useState('ALL')
    const [appliedServiceUsageCategoryFilter, setAppliedServiceUsageCategoryFilter] = useState('ALL')
    const [appliedServiceUsagePaidAfterFilter, setAppliedServiceUsagePaidAfterFilter] = useState('ALL')
    const [appliedServiceUsageDateFrom, setAppliedServiceUsageDateFrom] = useState('')
    const [appliedServiceUsageDateTo, setAppliedServiceUsageDateTo] = useState('')
    const [bulkTrialModal, setBulkTrialModal] = useState(null)
    const [bulkTrialDays, setBulkTrialDays] = useState(7)
    const [bulkTrialReason, setBulkTrialReason] = useState('')
    const [bulkTrialLoading, setBulkTrialLoading] = useState(false)
    const [bulkStatus, setBulkStatus] = useState('')
    const [bulkPlanCode, setBulkPlanCode] = useState('')
    const [bulkActionLoading, setBulkActionLoading] = useState(false)
    const [bulkUpdateModal, setBulkUpdateModal] = useState(null)
    const [bulkUpdateReason, setBulkUpdateReason] = useState('')
    const [storeLinkModal, setStoreLinkModal] = useState(null)
    const [storeLinkTarget, setStoreLinkTarget] = useState('')
    const [storeLinkReason, setStoreLinkReason] = useState('')
    const [storeLinkConfirmText, setStoreLinkConfirmText] = useState('')
    const [storeLinkLoading, setStoreLinkLoading] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState(null)
    const [orderDetailLoading, setOrderDetailLoading] = useState(false)
    const [grantReason, setGrantReason] = useState('')
    const [bulkComposeOpen, setBulkComposeOpen] = useState(false)
    const [bulkComposeStatus, setBulkComposeStatus] = useState('')
    const [bulkComposePlan, setBulkComposePlan] = useState('')
    const [bulkComposeBillingMode, setBulkComposeBillingMode] = useState('')
    const [bulkComposeGrantDays, setBulkComposeGrantDays] = useState('')
    const [bulkComposeQuotaFeature, setBulkComposeQuotaFeature] = useState('')
    const [bulkComposeQuotaDelta, setBulkComposeQuotaDelta] = useState('')
    const [bulkComposeReason, setBulkComposeReason] = useState('')
    const [bulkComposeLoading, setBulkComposeLoading] = useState(false)
    const [quotaConsumeLoading, setQuotaConsumeLoading] = useState(false)
    const [promotionSaving, setPromotionSaving] = useState(false)
    const [promotionForm, setPromotionForm] = useState(createInitialPromotionForm())
    const [editingPromotionId, setEditingPromotionId] = useState(null)
    const [billingBootstrapLoading, setBillingBootstrapLoading] = useState(false)
    const [billingBootstrapModal, setBillingBootstrapModal] = useState(null)
    const [billingBootstrapConfirmText, setBillingBootstrapConfirmText] = useState('')
    const [billingBootstrapResult, setBillingBootstrapResult] = useState(null)
    const [withdrawnStoreSubscriptionsOpen, setWithdrawnStoreSubscriptionsOpen] = useState(false)
    const [withdrawnStoreSubscriptions, setWithdrawnStoreSubscriptions] = useState([])
    const [withdrawnStoreSubscriptionLoading, setWithdrawnStoreSubscriptionLoading] = useState(false)
    const [withdrawnStoreSubscriptionError, setWithdrawnStoreSubscriptionError] = useState('')
    const [pointExpiryLoading, setPointExpiryLoading] = useState(false)
    const [pointReconcileLoading, setPointReconcileLoading] = useState(false)
    const [pointReconcileResult, setPointReconcileResult] = useState(null)
    const [pointAdjustmentTarget, setPointAdjustmentTarget] = useState(null)
    const [pointAdjustmentMode, setPointAdjustmentMode] = useState('GRANT')
    const [pointAdjustmentAmount, setPointAdjustmentAmount] = useState('')
    const [pointAdjustmentExpireAt, setPointAdjustmentExpireAt] = useState('')
    const [pointAdjustmentReason, setPointAdjustmentReason] = useState('')
    const [pointAdjustmentLoading, setPointAdjustmentLoading] = useState(false)
    const [couponReservationExpiryLoading, setCouponReservationExpiryLoading] = useState(false)

    const hubTabMenus = useMemo(() => {
        const tabs = [
            { id: 'status', name: '구독 상태', path: billingAdminHubPath('status') },
            { id: 'operationQueue', name: '운영 큐', path: billingAdminHubPath('operation-queue') },
            { id: 'orders', name: '주문/결제', path: billingAdminHubPath('orders') },
            { id: 'refunds', name: '환불/취소', path: billingAdminHubPath('refunds') },
            { id: 'serviceUsage', name: '서비스 사용원장', path: billingAdminHubPath('service-usage') },
        ]
        if (ENABLE_BILLING_COUPONS) {
            tabs.push({ id: 'coupons', name: '쿠폰/할인', path: billingAdminHubPath('coupons') })
        }
        if (ENABLE_BILLING_POINTS) {
            tabs.push({ id: 'points', name: '포인트', path: billingAdminHubPath('points') })
        }
        tabs.push(
            { id: 'billingLogs', name: '과금로그', path: billingAdminHubPath('billing-logs') },
            { id: 'audit', name: '이력관리', path: billingAdminHubPath('audit-logs') },
        )
        return tabs
    }, [])
    const activeMenu = useMemo(() => resolveBillingAdminMenuId(location.pathname), [location.pathname])
    const isDashboardView = activeMenu === 'dashboard'
    const subscriptionsManualAnchor = useMemo(() => {
        if (activeMenu === 'dashboard' || activeMenu === 'status' || activeMenu === 'orders') return 'step-01'
        if (activeMenu === 'operationQueue' || activeMenu === 'refunds' || activeMenu === 'serviceUsage' || activeMenu === 'billingLogs' || activeMenu === 'audit' || activeMenu === 'coupons' || activeMenu === 'points') return 'step-03'
        return ''
    }, [activeMenu])
    const orgOptions = subscriptionOrgOptions
    const dashboardSummary = dashboard?.summary || {}
    const dashboardPeriodInfo = dashboard?.period || {}
    const dashboardPeriodComparison = dashboard?.periodComparison || {}
    const dashboardCurrentPeriod = dashboardPeriodComparison.current || {}
    const dashboardPreviousPeriod = dashboardPeriodComparison.previous || {}
    const dashboardTrend = Array.isArray(dashboard?.trend) ? dashboard.trend : []
    const dashboardSubscriptionComparison = [
        {
            label: dashboardPeriodInfo.label || '선택 기간',
            newPaidSubscriptions: Number(dashboardCurrentPeriod.newPaidSubscriptions || 0),
            churnedSubscriptions: Number(dashboardCurrentPeriod.churnedSubscriptions || 0),
        },
        {
            label: dashboardPeriodInfo.comparisonLabel || '직전 기간',
            newPaidSubscriptions: Number(dashboardPreviousPeriod.newPaidSubscriptions || 0),
            churnedSubscriptions: Number(dashboardPreviousPeriod.churnedSubscriptions || 0),
        },
    ]
    const dashboardPlanMix = Array.isArray(dashboard?.planMix) ? dashboard.planMix : []
    const dashboardChannelMix = Array.isArray(dashboard?.channelMix) ? dashboard.channelMix : []
    const dashboardQueue = Array.isArray(dashboard?.operationQueue) ? dashboard.operationQueue : []
    const dashboardPendingQueue = dashboardQueue.filter((item) => Number(item?.count || 0) > 0)
    const dashboardQueueToShow = dashboardPendingQueue.length > 0 ? dashboardPendingQueue : dashboardQueue.slice(0, 4)
    const selectedSubscriptions = useMemo(() => (
        subscriptions.filter((item) => selectedSubscriptionIds.includes(item.id))
    ), [subscriptions, selectedSubscriptionIds])

    const singleSelectedSubscription = selectedSubscriptions.length === 1 ? selectedSubscriptions[0] : null
    const canGrantSelectedSubscriptionDays = selectedSubscriptions.some((s) => (
        s.billingReady !== false || isStoreEntitlementRestoreCandidate(s)
    ))
    const grantStoreEntitlementTargets = useMemo(() => (
        grantMode === 'REVOKE' ? [] : (grantTargets?.items || []).filter(isStoreEntitlementRestoreCandidate)
    ), [grantMode, grantTargets])
    const selectedPlanLabel = useMemo(() => (
        plans.find((plan) => plan.planCode === bulkPlanCode)?.name || bulkPlanCode || '-'
    ), [plans, bulkPlanCode])

    const pagedSubscriptions = subscriptions
    const pagedOrders = orders
    const pagedRefunds = refunds
    const pagedAuditLogs = auditLogs
    const pagedServiceUsageEvents = serviceUsageEvents

    const pageSelectedCount = useMemo(() => (
        subscriptions.filter((item) => selectedSubscriptionIds.includes(item.id)).length
    ), [subscriptions, selectedSubscriptionIds])

    useEffect(() => {
        setSubscriptionPage(1)
    }, [subscriptionSortKey, subscriptionSortDirection, subscriptionPageSize])

    useEffect(() => {
        if (!SUBSCRIPTION_PAGE_SIZES.includes(subscriptionPageSize)) {
            setSubscriptionPageSize(10)
        }
    }, [subscriptionPageSize])

    useEffect(() => {
        setOrderPage(1)
    }, [orderSortKey, orderSortDirection, orderPageSize])

    useEffect(() => {
        if (orderPage > orderTotalPages) {
            setOrderPage(orderTotalPages)
        }
    }, [orderPage, orderTotalPages])

    useEffect(() => {
        setRefundPage(1)
    }, [refundSortKey, refundSortDirection, refundPageSize])

    useEffect(() => {
        if (refundPage > refundTotalPages) {
            setRefundPage(refundTotalPages)
        }
    }, [refundPage, refundTotalPages])

    useEffect(() => {
        setAuditPage(1)
    }, [auditSortKey, auditSortDirection, auditPageSize])

    useEffect(() => {
        setServiceUsagePage(1)
    }, [serviceUsagePageSize])

    useEffect(() => {
        if (auditPage > auditTotalPages) {
            setAuditPage(auditTotalPages)
        }
    }, [auditPage, auditTotalPages])

    useEffect(() => {
        if (serviceUsagePage > serviceUsageTotalPages) {
            setServiceUsagePage(serviceUsageTotalPages)
        }
    }, [serviceUsagePage, serviceUsageTotalPages])

    const loadData = async () => {
        const menu = activeMenu
        const statusMenu = menu === 'status'
        const ordersMenu = menu === 'orders'
        const refundsMenu = menu === 'refunds'
        const serviceUsageMenu = menu === 'serviceUsage'
        const auditMenu = menu === 'audit' || menu === 'billingLogs'
        const clearAllListTabErrors = () => {
            setSubscriptionLoadError('')
            setOrderLoadError('')
            setRefundLoadError('')
            setAuditLoadError('')
            setServiceUsageLoadError('')
        }
        if (statusMenu) {
            clearAllListTabErrors()
            setSubscriptionListLoading(true)
            setError('')
        } else if (ordersMenu) {
            clearAllListTabErrors()
            setOrderListLoading(true)
            setError('')
        } else if (refundsMenu) {
            clearAllListTabErrors()
            setRefundListLoading(true)
            setError('')
        } else if (serviceUsageMenu) {
            clearAllListTabErrors()
            setServiceUsageListLoading(true)
            setError('')
        } else if (auditMenu) {
            clearAllListTabErrors()
            setAuditListLoading(true)
            setError('')
        } else {
            setLoading(true)
            setError('')
            clearAllListTabErrors()
        }
        try {
            if (menu === 'dashboard') {
                const dashboardRes = await api.get('/billing/admin/dashboard', { params: { period: dashboardPeriodPreset } })
                setDashboard(dashboardRes?.data?.data ?? null)
            } else if (menu === 'operationQueue') {
                const operationQueueRes = await api.get('/billing/admin/operation-queue')
                setOperationQueue(Array.isArray(operationQueueRes?.data?.data) ? operationQueueRes.data.data : [])
            } else if (menu === 'status') {
                const plansPromise = plans.length > 0
                    ? Promise.resolve({ data: { data: plans } })
                    : api.get('/billing/admin/config/plans')
                const [subscriptionsRes, plansRes] = await Promise.all([
                    api.get('/billing/admin/subscriptions', {
                        params: {
                            page: subscriptionPage,
                            size: subscriptionPageSize,
                            search: appliedSubscriptionSearch,
                            status: appliedSubscriptionStatusFilter,
                            orgName: appliedSubscriptionOrgFilter,
                            planCode: appliedSubscriptionPlanFilter,
                            billingMode: appliedSubscriptionBillingModeFilter,
                            subscriptionKind: appliedSubscriptionKindFilter,
                            paidStartAtFrom: appliedPaidStartAtFrom,
                            paidStartAtTo: appliedPaidStartAtTo,
                            individualEndsAtFrom: appliedIndividualEndsAtFrom,
                            individualEndsAtTo: appliedIndividualEndsAtTo,
                            expiry: appliedExpiryFilter,
                            sortKey: subscriptionSortKey,
                            sortDirection: subscriptionSortDirection,
                        },
                    }),
                    plansPromise,
                ])
                const subscriptionPayload = subscriptionsRes?.data?.data ?? {}
                const nextSubscriptions = Array.isArray(subscriptionPayload?.items) ? subscriptionPayload.items : []
                setSubscriptions(nextSubscriptions)
                setSubscriptionTotalCount(Number(subscriptionPayload?.totalCount) || 0)
                setSubscriptionTotalPages(Number(subscriptionPayload?.totalPages) || 1)
                setSubscriptionOrgOptions(Array.isArray(subscriptionPayload?.orgOptions) ? subscriptionPayload.orgOptions : [])
                if ((Number(subscriptionPayload?.page) || 1) !== subscriptionPage) {
                    setSubscriptionPage(Number(subscriptionPayload?.page) || 1)
                }
                setSelectedSubscriptionIds((prev) => prev.filter((id) => nextSubscriptions.some((item) => item.id === id && item.billingReady !== false)))
                const nextPlans = Array.isArray(plansRes?.data?.data) ? plansRes.data.data : []
                if (nextPlans.length > 0) {
                    setPlans(nextPlans)
                }
            } else if (menu === 'orders') {
                const ordersRes = await api.get('/billing/admin/orders', {
                    params: {
                        page: orderPage,
                        size: orderPageSize,
                        search: appliedOrderSearch,
                        productName: appliedOrderProductFilter,
                        status: appliedOrderStatusFilter,
                        pgProvider: appliedOrderPgProviderFilter,
                        dateFrom: appliedOrderDateFrom,
                        dateTo: appliedOrderDateTo,
                        sortKey: orderSortKey,
                        sortDirection: orderSortDirection,
                    },
                })
                const orderPayload = ordersRes?.data?.data ?? {}
                setOrders(Array.isArray(orderPayload?.items) ? orderPayload.items : [])
                setOrderProductOptions(Array.isArray(orderPayload?.productOptions) ? orderPayload.productOptions : [])
                setOrderPgProviderOptions(Array.isArray(orderPayload?.pgProviderOptions) ? orderPayload.pgProviderOptions : [])
                setOrderTotalCount(Number(orderPayload?.totalCount) || 0)
                setOrderTotalPages(Number(orderPayload?.totalPages) || 1)
                if ((Number(orderPayload?.page) || 1) !== orderPage) {
                    setOrderPage(Number(orderPayload?.page) || 1)
                }
            } else if (menu === 'refunds') {
                const refundsRes = await api.get('/billing/admin/refunds', {
                    params: {
                        page: refundPage,
                        size: refundPageSize,
                        search: appliedRefundSearch,
                        status: appliedRefundStatusFilter,
                        dateFrom: appliedRefundDateFrom,
                        dateTo: appliedRefundDateTo,
                        sortKey: refundSortKey,
                        sortDirection: refundSortDirection,
                    },
                })
                const refundPayload = refundsRes?.data?.data ?? {}
                setRefunds(Array.isArray(refundPayload?.items) ? refundPayload.items : [])
                setRefundTotalCount(Number(refundPayload?.totalCount) || 0)
                setRefundTotalPages(Number(refundPayload?.totalPages) || 1)
                if ((Number(refundPayload?.page) || 1) !== refundPage) {
                    setRefundPage(Number(refundPayload?.page) || 1)
                }
            } else if (menu === 'serviceUsage') {
                const usageRes = await api.get('/billing/admin/service-usage-events', {
                    params: {
                        page: serviceUsagePage,
                        size: serviceUsagePageSize,
                        search: appliedServiceUsageSearch,
                        refundBlocking: appliedServiceUsageRefundBlockingFilter,
                        usageCategory: appliedServiceUsageCategoryFilter,
                        paidAfter: appliedServiceUsagePaidAfterFilter,
                        dateFrom: appliedServiceUsageDateFrom,
                        dateTo: appliedServiceUsageDateTo,
                    },
                })
                const usagePayload = usageRes?.data?.data ?? {}
                setServiceUsageEvents(Array.isArray(usagePayload?.items) ? usagePayload.items : [])
                setServiceUsageTotalCount(Number(usagePayload?.totalCount) || 0)
                setServiceUsageTotalPages(Number(usagePayload?.totalPages) || 1)
                if ((Number(usagePayload?.page) || 1) !== serviceUsagePage) {
                    setServiceUsagePage(Number(usagePayload?.page) || 1)
                }
            } else if (menu === 'audit' || menu === 'billingLogs') {
                const auditsRes = await api.get('/billing/admin/audit-logs', {
                    params: {
                        page: auditPage,
                        size: auditPageSize,
                        search: appliedAuditSearch,
                        action: appliedAuditActionFilter,
                        targetType: appliedAuditTargetTypeFilter,
                        provider: appliedAuditProviderFilter,
                        status: appliedAuditStatusFilter,
                        orderNo: appliedAuditOrderNoFilter,
                        paymentId: appliedAuditPaymentIdFilter,
                        webhookEventId: appliedAuditWebhookEventIdFilter,
                        dateFrom: appliedAuditDateFrom,
                        dateTo: appliedAuditDateTo,
                        sortKey: auditSortKey,
                        sortDirection: auditSortDirection,
                    },
                })
                const auditPayload = auditsRes?.data?.data ?? {}
                setAuditLogs(Array.isArray(auditPayload?.items) ? auditPayload.items : [])
                setAuditTotalCount(Number(auditPayload?.totalCount) || 0)
                setAuditTotalPages(Number(auditPayload?.totalPages) || 1)
                setAuditActionOptions(Array.isArray(auditPayload?.actionOptions) ? auditPayload.actionOptions : [])
                setAuditTargetTypeOptions(Array.isArray(auditPayload?.targetTypeOptions) ? auditPayload.targetTypeOptions : [])
                setAuditProviderOptions(Array.isArray(auditPayload?.providerOptions) ? auditPayload.providerOptions : [])
                setAuditStatusOptions(Array.isArray(auditPayload?.statusOptions) ? auditPayload.statusOptions : [])
                if ((Number(auditPayload?.page) || 1) !== auditPage) {
                    setAuditPage(Number(auditPayload?.page) || 1)
                }
            } else if (menu === 'coupons') {
                const promotionsRes = await api.get('/billing/admin/promotions').catch(() => ({ data: { data: [] } }))
                setPromotions(Array.isArray(promotionsRes?.data?.data) ? promotionsRes.data.data : [])
            } else if (menu === 'points') {
                const pointsRes = await api.get('/billing/admin/points/overview').catch(() => ({ data: { data: null } }))
                setPointOverview(pointsRes?.data?.data ?? null)
            }
        } catch (e) {
            if (statusMenu) {
                setSubscriptionLoadError(`구독 목록을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else if (ordersMenu) {
                setOrderLoadError(`주문 목록을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else if (refundsMenu) {
                setRefundLoadError(`환불 목록을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else if (serviceUsageMenu) {
                setServiceUsageLoadError(`서비스 사용원장을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else if (auditMenu) {
                setAuditLoadError(`감사 이력을 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            } else {
                setError(`과금/구독 데이터를 불러오지 못했습니다.${pickBillingLoadDetailSuffix(e)}`)
            }
        } finally {
            if (statusMenu) {
                setSubscriptionListLoading(false)
            } else if (ordersMenu) {
                setOrderListLoading(false)
            } else if (refundsMenu) {
                setRefundListLoading(false)
            } else if (serviceUsageMenu) {
                setServiceUsageListLoading(false)
            } else if (auditMenu) {
                setAuditListLoading(false)
            } else {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        loadData()
    }, [
        subscriptionPage, subscriptionPageSize, appliedSubscriptionSearch, appliedSubscriptionStatusFilter, appliedSubscriptionOrgFilter, appliedSubscriptionKindFilter, appliedSubscriptionPlanFilter, appliedSubscriptionBillingModeFilter, appliedExpiryFilter, appliedPaidStartAtFrom, appliedPaidStartAtTo, appliedIndividualEndsAtFrom, appliedIndividualEndsAtTo, subscriptionSortKey, subscriptionSortDirection,
        orderPage, orderPageSize, appliedOrderSearch, appliedOrderProductFilter, appliedOrderStatusFilter, appliedOrderPgProviderFilter, appliedOrderDateFrom, appliedOrderDateTo, orderSortKey, orderSortDirection,
        refundPage, refundPageSize, appliedRefundSearch, appliedRefundStatusFilter, appliedRefundDateFrom, appliedRefundDateTo, refundSortKey, refundSortDirection,
        serviceUsagePage, serviceUsagePageSize, appliedServiceUsageSearch, appliedServiceUsageRefundBlockingFilter, appliedServiceUsageCategoryFilter, appliedServiceUsagePaidAfterFilter, appliedServiceUsageDateFrom, appliedServiceUsageDateTo,
        auditPage, auditPageSize, appliedAuditSearch, appliedAuditActionFilter, appliedAuditTargetTypeFilter, appliedAuditProviderFilter, appliedAuditStatusFilter,
        appliedAuditOrderNoFilter, appliedAuditPaymentIdFilter, appliedAuditWebhookEventIdFilter, appliedAuditDateFrom, appliedAuditDateTo, auditSortKey, auditSortDirection,
        dashboardPeriodPreset, activeMenu,
    ])

    const toggleGrantDayPreset = (d) => {
        setGrantDayPresetSelection((prev) => {
            const next = new Set(prev)
            if (next.has(d)) next.delete(d)
            else next.add(d)
            return next
        })
    }

    const resolveGrantTotalDays = () => {
        let t = 0
        grantDayPresetSelection.forEach((n) => {
            t += Number(n) || 0
        })
        const ex = parseInt(String(grantDaysExtra || '').trim(), 10)
        if (!Number.isNaN(ex) && ex > 0) t += ex
        return t
    }

    const grantSubscriptionDays = async () => {
        if (!grantTargets?.items?.length) return
        const days = resolveGrantTotalDays()
        const isRevoke = grantMode === 'REVOKE'
        if (days < 1) {
            show(`${isRevoke ? '회수' : '부여'}할 총 일수는 1일 이상이어야 합니다. 프리셋을 선택하거나 추가 일수를 입력해 주세요.`, 'error')
            return
        }
        try {
            const restoreCount = !isRevoke && grantRestoreStoreEntitlement
                ? grantTargets.items.filter(isStoreEntitlementRestoreCandidate).length
                : 0
            await Promise.all(grantTargets.items.map((it) => (
                api.post(
                    `/billing/admin/subscriptions/${it.id}/grant-days`,
                    isRevoke
                        ? { deltaDays: -days, reason: grantReason || '운영자 기간 회수' }
                        : {
                            days,
                            reason: grantReason || '운영자 수동 부여',
                            restoreStoreEntitlement: grantRestoreStoreEntitlement && isStoreEntitlementRestoreCandidate(it),
                        },
                )
            )))
            const baseMessage = grantTargets.items.length > 1
                ? `${grantTargets.items.length}명에게 구독일 ${days}일을 ${isRevoke ? '회수' : '부여'}했습니다.`
                : `구독일 ${isRevoke ? '회수' : '부여'}가 완료되었습니다.`
            show(
                `${baseMessage}${restoreCount > 0 ? ` 인앱결제 서비스 권한 ${restoreCount}건도 복구했습니다.` : ''}`,
                'success',
            )
            setGrantTargets(null)
            setGrantMode('GRANT')
            setGrantReason('')
            setGrantDayPresetSelection(new Set([7]))
            setGrantDaysExtra('')
            setGrantRestoreStoreEntitlement(false)
            await loadData()
        } catch (e) {
            show(`구독일 ${isRevoke ? '회수' : '부여'}에 실패했습니다.`, 'error')
        }
    }

    const toggleTrial = async (subscription, revoke = false) => {
        try {
            await api.post(`/billing/admin/subscriptions/${subscription.id}/trial`, {
                days: 7,
                revoke,
                reason: revoke ? 'Trial 회수' : '운영자 Trial 지급',
            })
            show(revoke ? 'Trial 회수가 완료되었습니다.' : 'Trial 지급이 완료되었습니다.', 'success')
            loadData()
        } catch (e) {
            show('Trial 처리에 실패했습니다.', 'error')
        }
    }

    const openSelectedGrantDays = (mode = 'GRANT') => {
        const ready = selectedSubscriptions.filter((s) => (
            s.billingReady !== false || (mode === 'GRANT' && isStoreEntitlementRestoreCandidate(s))
        ))
        if (ready.length === 0) {
            show(mode === 'REVOKE'
                ? '구독이 준비된(billing) 회원을 1명 이상 선택해주세요.'
                : '구독이 준비된 회원 또는 서비스 권한 제외 인앱결제 구독을 1건 이상 선택해주세요.', 'error')
            return
        }
        const items = ready.map((s) => ({
            id: s.id,
            targetName: s.targetName,
            storeEntitlementManaged: s.storeEntitlementManaged,
            storeEntitlementEnabled: s.storeEntitlementEnabled,
            storeEntitlementStatusLabel: s.storeEntitlementStatusLabel,
        }))
        setGrantTargets({ items })
        setGrantMode(mode)
        setGrantDayPresetSelection(new Set([7]))
        setGrantDaysExtra('')
        setGrantReason('')
        setGrantRestoreStoreEntitlement(mode !== 'REVOKE' && items.some(isStoreEntitlementRestoreCandidate))
    }

    const openSubscriptionDetail = async (subscription) => {
        if (!subscription) {
            show('구독 상세 대상을 확인해주세요.', 'error')
            return
        }
        if (subscription.billingReady === false) {
            show('\uAD6C\uB3C5 \uC815\uBCF4\uB97C \uC0DD\uC131\uD55C \uB4A4 \uC0C1\uC138\uB97C \uC870\uD68C\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4.', 'error')
            return
        }
        try {
            setQuotaDetailLoading(true)
            const response = await api.get(`/billing/admin/subscriptions/${subscription.id}`)
            const payload = response?.data?.data ?? subscription
            const detail = {
                ...payload,
                subscriptionKind: subscription.subscriptionKind,
                firstPaidStartedAt: subscription.firstPaidStartedAt,
                individualSubscriptionId: subscription.individualSubscriptionId,
                individualEndsAt: subscription.individualEndsAt,
            }
            setSelectedQuotaSubscription(detail)
            setQuotaLimitDraft({})
            setCustomerMemoStorageQuotaDraft(storageQuotaGbDraft(detail?.customerMemoStorageQuotaBytes ?? detail?.customerMemoStorageQuota?.maxUserAttachmentBytes))
        } catch (e) {
            show('구독 상세를 불러오지 못했습니다.', 'error')
        } finally {
            setQuotaDetailLoading(false)
        }
    }
    const openSelectedTrialHistory = () => {
        if (!singleSelectedSubscription) {
            show('Trial 이력은 1명의 회원을 선택한 뒤 확인해주세요.', 'error')
            return
        }
        openTrialHistory(singleSelectedSubscription)
    }

    const openSelectedStoreLinkModal = (mode, subscriptionOverride = null) => {
        const targetSubscription = subscriptionOverride || singleSelectedSubscription
        if (!targetSubscription) {
            show('인앱결제 연결 관리는 인앱결제 구독 1건을 선택한 뒤 처리해주세요.', 'error')
            return
        }
        if (!isStoreIapSubscription(targetSubscription)) {
            show('개인 인앱결제 구독만 연결을 관리할 수 있습니다.', 'error')
            return
        }
        setStoreLinkModal({ mode, subscription: targetSubscription })
        setStoreLinkTarget('')
        setStoreLinkReason(storeLinkDefaultReason(mode))
        setStoreLinkConfirmText('')
    }

    const closeStoreLinkModal = () => {
        if (storeLinkLoading) return
        setStoreLinkModal(null)
        setStoreLinkTarget('')
        setStoreLinkReason('')
        setStoreLinkConfirmText('')
    }

    const submitStoreLinkAction = async () => {
        if (!storeLinkModal?.subscription) return
        const mode = storeLinkModal.mode
        const isTransfer = mode === 'TRANSFER'
        const expectedConfirmText = storeLinkConfirmTextForMode(mode)
        const reason = storeLinkReason.trim()
        const confirmText = storeLinkConfirmText.trim()
        if (!reason) {
            show('처리 사유를 입력해주세요.', 'error')
            return
        }
        if (confirmText !== expectedConfirmText) {
            show(`확인 문구 ${expectedConfirmText} 를 입력해주세요.`, 'error')
            return
        }
        const payload = { reason, confirmText }
        if (isTransfer) {
            const target = storeLinkTarget.trim()
            if (!target) {
                show('이관 대상 회원 ID 또는 이메일을 입력해주세요.', 'error')
                return
            }
            if (/^\d+$/.test(target)) {
                payload.targetUserId = Number(target)
            } else {
                payload.targetEmail = target
            }
        }
        try {
            setStoreLinkLoading(true)
            await api.post(storeLinkEndpoint(mode, storeLinkModal.subscription.id), payload)
            show(storeLinkSuccessMessage(mode), 'success')
            setStoreLinkModal(null)
            setStoreLinkTarget('')
            setStoreLinkReason('')
            setStoreLinkConfirmText('')
            setSelectedQuotaSubscription(null)
            setSelectedSubscriptionIds([])
            await loadData()
        } catch (e) {
            show(`${storeLinkModeTitle(mode)}에 실패했습니다.${pickBillingLoadDetailSuffix(e)}`, 'error')
        } finally {
            setStoreLinkLoading(false)
        }
    }

    const updateSelectedSubscriptions = async (payload, successMessage, errorMessage, onSuccess) => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        try {
            setBulkActionLoading(true)
            await Promise.all(selectedSubscriptionIds.map((subscriptionId) => (
                api.patch(`/billing/admin/subscriptions/${subscriptionId}`, payload)
            )))
            show(successMessage, 'success')
            setSelectedSubscriptionIds([])
            if (typeof onSuccess === 'function') {
                onSuccess()
            }
            await loadData()
        } catch (e) {
            show(errorMessage, 'error')
        } finally {
            setBulkActionLoading(false)
        }
    }

    const openBulkStatusModal = () => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        if (!bulkStatus) {
            show('변경할 상태를 선택해주세요.', 'error')
            return
        }
        setBulkUpdateReason('운영자 일괄 상태 변경')
        setBulkUpdateModal({
            type: 'status',
            title: '선택 회원 상태 변경',
            nextValueLabel: formatBillingStatusLabel(bulkStatus, 'subscription'),
            payload: {
                status: bulkStatus,
                reason: '운영자 일괄 상태 변경',
            },
            successMessage: '선택한 회원의 구독 상태를 변경했습니다.',
            errorMessage: '구독 상태 변경에 실패했습니다.',
        })
    }

    const openBulkPlanModal = () => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        if (!bulkPlanCode) {
            show('변경할 플랜을 선택해주세요.', 'error')
            return
        }
        setBulkUpdateReason('운영자 일괄 플랜 변경')
        setBulkUpdateModal({
            type: 'plan',
            title: '선택 회원 플랜 변경',
            nextValueLabel: selectedPlanLabel,
            payload: {
                planCode: bulkPlanCode,
                reason: '운영자 일괄 플랜 변경',
            },
            successMessage: '선택한 회원의 플랜을 변경했습니다.',
            errorMessage: '플랜 변경에 실패했습니다.',
        })
    }

    const changeSubscriptionStatus = async (subscription, status) => {
        try {
            await api.patch(`/billing/admin/subscriptions/${subscription.id}`, {
                status,
                reason: '운영자 상태 변경',
            })
            show('구독 상태가 변경되었습니다.', 'success')
            loadData()
        } catch (e) {
            show('구독 상태 변경에 실패했습니다.', 'error')
        }
    }

    const reconcileOrder = async (orderId) => {
        try {
            await api.post(`/billing/admin/orders/${orderId}/reconcile`)
            show('주문 재검증 요청이 완료되었습니다.', 'success')
            loadData()
        } catch (e) {
            show('주문 재검증에 실패했습니다.', 'error')
        }
    }

    const updatePromotionForm = (field, value) => {
        setPromotionForm((prev) => ({ ...prev, [field]: value }))
    }

    const updatePromotionReward = (index, field, value) => {
        setPromotionForm((prev) => ({
            ...prev,
            rewards: prev.rewards.map((reward, rewardIndex) => (
                rewardIndex === index ? { ...reward, [field]: value } : reward
            )),
        }))
    }

    const addPromotionReward = () => {
        setPromotionForm((prev) => ({
            ...prev,
            rewards: [...prev.rewards, createInitialRewardForm()],
        }))
    }

    const removePromotionReward = (index) => {
        setPromotionForm((prev) => ({
            ...prev,
            rewards: prev.rewards.filter((_, rewardIndex) => rewardIndex !== index),
        }))
    }

    const loadPromotionToForm = (promotion) => {
        setEditingPromotionId(promotion.id)
        setPromotionForm({
            promotionType: promotion.promotionType || 'COUPON',
            triggerType: promotion.triggerType || 'CODE_REDEEM',
            code: promotion.code || '',
            name: promotion.name || '',
            description: promotion.description || '',
            status: promotion.status || 'ACTIVE',
            perUserLimit: promotion.perUserLimit || '',
            totalLimit: promotion.totalLimit || '',
            rewards: Array.isArray(promotion.rewards) && promotion.rewards.length > 0
                ? promotion.rewards.map((reward) => ({
                    rewardType: reward.rewardType || 'POINT_GRANT',
                    targetRole: reward.targetRole || 'REDEEMER',
                    value: reward.valueInt ?? reward.value ?? '',
                    expireDays: reward.valueJson?.expireDays ?? '',
                }))
                : [createInitialRewardForm()],
        })
    }

    const resetPromotionForm = () => {
        setEditingPromotionId(null)
        setPromotionForm(createInitialPromotionForm())
    }

    const submitPromotion = async () => {
        try {
            setPromotionSaving(true)
            const rewards = promotionForm.rewards
                .filter((reward) => reward.rewardType && String(reward.value || '').trim())
                .map((reward) => {
                    const payload = {
                        rewardType: reward.rewardType,
                        targetRole: reward.targetRole,
                        value: Number(reward.value) || 0,
                    }
                    if (reward.rewardType === 'POINT_GRANT' && reward.expireDays) {
                        payload.valueJson = { expireDays: Number(reward.expireDays) || 0 }
                    }
                    return payload
                })

            if (!promotionForm.name.trim()) {
                show('프로모션 이름을 입력해주세요.', 'error')
                return
            }
            if (rewards.length === 0) {
                show('최소 1개의 reward를 입력해주세요.', 'error')
                return
            }

            const payload = {
                promotionType: promotionForm.promotionType,
                triggerType: promotionForm.triggerType,
                code: promotionForm.code.trim() || undefined,
                name: promotionForm.name.trim(),
                description: promotionForm.description.trim() || undefined,
                status: promotionForm.status,
                perUserLimit: promotionForm.perUserLimit ? Number(promotionForm.perUserLimit) : undefined,
                totalLimit: promotionForm.totalLimit ? Number(promotionForm.totalLimit) : undefined,
                rewards,
            }

            if (editingPromotionId) {
                await api.patch(`/billing/admin/promotions/${editingPromotionId}`, payload)
                show('프로모션이 수정되었습니다.', 'success')
            } else {
                await api.post('/billing/admin/promotions', payload)
                show('프로모션이 생성되었습니다.', 'success')
            }
            resetPromotionForm()
            await loadData()
        } catch (e) {
            show(editingPromotionId ? '프로모션 수정에 실패했습니다.' : '프로모션 생성에 실패했습니다.', 'error')
        } finally {
            setPromotionSaving(false)
        }
    }

    const togglePromotionStatus = async (promotion, nextStatus) => {
        try {
            await api.patch(`/billing/admin/promotions/${promotion.id}`, {
                status: nextStatus,
                reason: `운영자 ${nextStatus === 'ACTIVE' ? '재활성화' : '비활성화'}`,
            })
            show(`프로모션을 ${nextStatus === 'ACTIVE' ? '재활성화' : '비활성화'}했습니다.`, 'success')
            await loadData()
        } catch (e) {
            show('프로모션 상태 변경에 실패했습니다.', 'error')
        }
    }

    const runPointExpiry = async () => {
        try {
            setPointExpiryLoading(true)
            const res = await api.post('/billing/admin/points/expire')
            const payload = res?.data?.data ?? null
            show(`포인트 만료 처리 완료: ${payload?.expiredAmount || 0}P / ${payload?.expiredLotCount || 0}건`, 'success')
            await loadData()
        } catch (e) {
            show('포인트 만료 처리에 실패했습니다.', 'error')
        } finally {
            setPointExpiryLoading(false)
        }
    }
    const runPointReconciliation = async () => {
        try {
            setPointReconcileLoading(true)
            const res = await api.post('/billing/admin/points/reconcile', { walletLimit: 5000 })
            const payload = res?.data?.data ?? null
            setPointReconcileResult(payload)
            const mismatchCount = Number(payload?.mismatchCount || 0)
            show(
                mismatchCount > 0
                    ? `포인트 원장 불일치 ${mismatchCount}건을 확인했습니다.`
                    : '포인트 원장과 잔액이 일치합니다.',
                mismatchCount > 0 ? 'error' : 'success',
            )
        } catch (e) {
            show('포인트 원장 정합성 점검에 실패했습니다.', 'error')
        } finally {
            setPointReconcileLoading(false)
        }
    }

    const openPointAdjustment = (wallet) => {
        setPointAdjustmentTarget(wallet)
        setPointAdjustmentMode('GRANT')
        setPointAdjustmentAmount('')
        setPointAdjustmentExpireAt('')
        setPointAdjustmentReason('')
    }

    const closePointAdjustment = (force = false) => {
        if (pointAdjustmentLoading && !force) return
        setPointAdjustmentTarget(null)
        setPointAdjustmentAmount('')
        setPointAdjustmentExpireAt('')
        setPointAdjustmentReason('')
    }

    const submitPointAdjustment = async () => {
        const ownerId = Number(pointAdjustmentTarget?.ownerId || 0)
        const amount = Math.floor(Number(pointAdjustmentAmount || 0))
        const reason = String(pointAdjustmentReason || '').trim()
        if (ownerId < 1 || amount < 1 || !reason) {
            show('조정 포인트와 사유를 확인해 주세요.', 'error')
            return
        }
        let expireAt
        if (pointAdjustmentMode === 'GRANT' && pointAdjustmentExpireAt) {
            const parsed = new Date(pointAdjustmentExpireAt)
            if (Number.isNaN(parsed.getTime())) {
                show('만료일을 확인해 주세요.', 'error')
                return
            }
            expireAt = parsed.toISOString()
        }
        try {
            setPointAdjustmentLoading(true)
            await api.post(`/billing/admin/users/${ownerId}/points/adjust`, {
                delta: pointAdjustmentMode === 'GRANT' ? amount : -amount,
                reason,
                memo: pointAdjustmentMode === 'GRANT' ? '운영자 보너스 포인트 지급' : '운영자 포인트 회수',
                ...(expireAt ? { expireAt } : {}),
            })
            show(`포인트 ${pointAdjustmentMode === 'GRANT' ? '지급' : '회수'}가 완료되었습니다.`, 'success')
            closePointAdjustment(true)
            await loadData()
        } catch (e) {
            show(`포인트 ${pointAdjustmentMode === 'GRANT' ? '지급' : '회수'}에 실패했습니다.`, 'error')
        } finally {
            setPointAdjustmentLoading(false)
        }
    }


    const runCouponReservationExpiry = async () => {
        try {
            setCouponReservationExpiryLoading(true)
            const res = await api.post('/billing/admin/coupons/reservations/expire')
            const payload = res?.data?.data ?? null
            show(`쿠폰 예약 만료 처리 완료: ${payload?.expiredCount || 0}건`, 'success')
            await loadData()
        } catch (e) {
            show('쿠폰 예약 만료 처리에 실패했습니다.', 'error')
        } finally {
            setCouponReservationExpiryLoading(false)
        }
    }

    const openOrderDetail = async (orderId) => {
        setOrderDetailLoading(true)
        try {
            const response = await api.get(`/billing/admin/orders/${orderId}`)
            setSelectedOrder(response?.data?.data ?? null)
        } catch (e) {
            show('주문 상세를 불러오지 못했습니다.', 'error')
        } finally {
            setOrderDetailLoading(false)
        }
    }

    const syncIapSubscriptions = async () => {
        try {
            setIapSyncLoading(true)
            const response = await api.post('/billing/admin/iap-subscriptions/sync')
            const payload = response?.data?.data ?? {}
            show('IAP sync complete: processed ' + (payload.processed || 0) + ', failed ' + (payload.failed || 0) + ', skipped ' + (payload.skipped || 0), 'success')
            await loadData()
        } catch (e) {
            show('IAP sync failed.', 'error')
        } finally {
            setIapSyncLoading(false)
        }
    }

    const loadIapNotificationEvents = async () => {
        setIapNotificationModalOpen(true)
        setIapNotificationLoading(true)
        try {
            const response = await api.get('/billing/admin/iap-notifications', { params: { page: 1, size: 30 } })
            const payload = response?.data?.data ?? {}
            setIapNotificationEvents(Array.isArray(payload?.items) ? payload.items : [])
            setIapNotificationTotalCount(Number(payload?.totalCount) || 0)
        } catch (e) {
            setIapNotificationEvents([])
            setIapNotificationTotalCount(0)
            show('스토어 알림 목록을 불러오지 못했습니다.', 'error')
        } finally {
            setIapNotificationLoading(false)
        }
    }

    const retryIapNotificationEvent = async (eventId) => {
        try {
            setIapNotificationRetryingId(eventId)
            const response = await api.post(`/billing/admin/iap-notifications/${eventId}/retry`)
            const payload = response?.data?.data ?? {}
            show(`스토어 알림 재처리 완료: 처리 ${payload.processed || 0}건 / 실패 ${payload.failed || 0}건`, 'success')
            await loadIapNotificationEvents()
            await loadData()
        } catch (e) {
            show('스토어 알림 재처리에 실패했습니다.', 'error')
        } finally {
            setIapNotificationRetryingId(null)
        }
    }

    const openStoreAccountConflictsModal = async () => {
        setStoreConflictModalOpen(true)
        setStoreConflictLoading(true)
        try {
            const response = await api.get('/billing/admin/iap-subscriptions/conflicts')
            const payload = response?.data?.data ?? {}
            const groups = Array.isArray(payload?.groups) ? payload.groups : (Array.isArray(payload?.items) ? payload.items : [])
            setStoreConflictGroups(groups)
            setStoreConflictSummary(payload)
        } catch (e) {
            setStoreConflictGroups([])
            setStoreConflictSummary(null)
            show('스토어 계정 충돌 목록을 불러오지 못했습니다.', 'error')
        } finally {
            setStoreConflictLoading(false)
        }
    }
    const updateRefund = async (refundId, action) => {
        try {
            await unwrapAsyncResult(await api.post(`/billing/admin/refunds/${refundId}/${action}`))
            show(`환불 ${formatRefundActionLabel(action)} 처리가 완료되었습니다.`, 'success')
            loadData()
        } catch (e) {
            show('환불 처리에 실패했습니다.', 'error')
        }
    }

    const consumeSubscriptionQuota = async (subscriptionId, featureCode) => {
        try {
            setQuotaConsumeLoading(true)
            const response = await api.post(`/billing/admin/subscriptions/${subscriptionId}/usage/consume`, {
                featureCode,
                units: 1,
                refType: 'ADMIN_UI',
                reason: '운영자 수동 차감 테스트',
            })
            const updatedSubscription = response?.data?.data?.subscription
            if (updatedSubscription) {
                setSelectedQuotaSubscription(updatedSubscription)
            }
            show('사용량이 1건 차감되었습니다.', 'success')
            await loadData()
        } catch (e) {
            show('사용량 차감에 실패했습니다.', 'error')
        } finally {
            setQuotaConsumeLoading(false)
        }
    }

    const adjustSubscriptionQuotaLimitRow = async (subscriptionId, featureCode) => {
        const raw = String(quotaLimitDraft[featureCode] ?? '').trim()
        const delta = parseInt(raw, 10)
        if (Number.isNaN(delta) || delta === 0) {
            show('한도 증감은 0이 아닌 정수로 입력해주세요.', 'error')
            return
        }
        try {
            setQuotaLimitAdjustLoading(true)
            const response = await api.post(`/billing/admin/subscriptions/${subscriptionId}/quota-limit-adjust`, {
                featureCode,
                delta,
                reason: '운영자 한도 조정',
            })
            const updatedSubscription = response?.data?.data?.subscription
            if (updatedSubscription) {
                setSelectedQuotaSubscription(updatedSubscription)
            }
            setQuotaLimitDraft((prev) => {
                const next = { ...prev }
                delete next[featureCode]
                return next
            })
            show('할당량 한도가 반영되었습니다.', 'success')
            await loadData()
        } catch (e) {
            show(`한도 조정에 실패했습니다.${pickBillingLoadDetailSuffix(e)}`, 'error')
        } finally {
            setQuotaLimitAdjustLoading(false)
        }
    }

    const openTrialHistory = async (subscription) => {
        setTrialHistoryLoading(true)
        try {
            const response = await api.get(`/billing/admin/subscriptions/${subscription.id}/trial-grants`)
            setSelectedTrialHistory({
                subscription,
                rows: Array.isArray(response?.data?.data) ? response.data.data : [],
            })
        } catch (e) {
            show('Trial 이력을 불러오지 못했습니다.', 'error')
        } finally {
            setTrialHistoryLoading(false)
        }
    }

    const toggleSubscriptionSelection = (subscriptionId) => {
        const subscription = subscriptions.find((item) => item.id === subscriptionId)
        if (subscription?.billingReady === false) {
            return
        }
        setSelectedSubscriptionIds((prev) => (
            prev.includes(subscriptionId)
                ? prev.filter((id) => id !== subscriptionId)
                : [...prev, subscriptionId]
        ))
    }

    const toggleAllSubscriptionSelection = () => {
        const pageIds = pagedSubscriptions.filter((item) => item.billingReady !== false).map((item) => item.id)
        if (pageIds.length === 0) {
            return
        }
        const isAllSelected = pageIds.every((id) => selectedSubscriptionIds.includes(id))
        if (isAllSelected) {
            setSelectedSubscriptionIds((prev) => prev.filter((id) => !pageIds.includes(id)))
            return
        }
        setSelectedSubscriptionIds((prev) => Array.from(new Set([...prev, ...pageIds])))
    }

    const bootstrapMissingBilling = async ({ dryRun = false, confirmText = '', limit = 5000 } = {}) => {
        try {
            setBillingBootstrapLoading(true)
            const response = await api.post('/billing/admin/subscriptions/bootstrap-missing', { dryRun, confirmText, limit }, { __silent: true })
            const payload = response?.data?.data ?? null
            const summary = dryRun
                ? `누락 계정 ${payload?.missingAccounts || 0}건 / 누락 구독 ${payload?.missingSubscriptions || 0}건`
                : `계정 ${payload?.createdAccounts || 0}건 / 구독 ${payload?.createdSubscriptions || 0}건 생성`
            setBillingBootstrapResult({
                mode: dryRun ? 'dryRun' : 'execute',
                payload,
            })
            show(dryRun ? `dry-run 완료: ${summary}` : `bootstrap 완료: ${summary}`, 'success')
            await loadData()
        } catch (e) {
            show(e?.response?.data?.message || '누락 billing bootstrap 실행에 실패했습니다.', 'error')
        } finally {
            setBillingBootstrapLoading(false)
        }
    }

    const openBillingBootstrapModal = () => {
        setBillingBootstrapConfirmText('')
        setBillingBootstrapModal({ limit: 5000 })
    }

    const submitBillingBootstrap = async () => {
        if (!billingBootstrapModal) return
        const trimmed = billingBootstrapConfirmText.trim().toUpperCase()
        if (trimmed !== 'BOOTSTRAP') {
            show('확인 문구 BOOTSTRAP 을 입력해주세요.', 'error')
            return
        }
        await bootstrapMissingBilling({
            dryRun: false,
            confirmText: trimmed,
            limit: billingBootstrapModal.limit,
        })
        setBillingBootstrapModal(null)
        setBillingBootstrapConfirmText('')
    }

    const openWithdrawnStoreSubscriptionsModal = async () => {
        setWithdrawnStoreSubscriptionsOpen(true)
        setWithdrawnStoreSubscriptionError('')
        setWithdrawnStoreSubscriptionLoading(true)
        try {
            const response = await api.get('/billing/admin/subscriptions/withdrawn-store-active')
            const payload = response?.data?.data ?? {}
            setWithdrawnStoreSubscriptions(Array.isArray(payload?.items) ? payload.items : [])
        } catch (e) {
            setWithdrawnStoreSubscriptions([])
            setWithdrawnStoreSubscriptionError('구독중인 탈퇴회원 목록을 불러오지 못했습니다.')
            show('구독중인 탈퇴회원 목록을 불러오지 못했습니다.', 'error')
        } finally {
            setWithdrawnStoreSubscriptionLoading(false)
        }
    }
    const applySubscriptionFilters = () => {
        setSubscriptionPage(1)
        setAppliedSubscriptionSearch(subscriptionSearch)
        setAppliedSubscriptionStatusFilter(subscriptionStatusFilter)
        setAppliedSubscriptionOrgFilter(subscriptionOrgFilter)
        setAppliedSubscriptionKindFilter(subscriptionKindFilter)
        setAppliedSubscriptionPlanFilter(subscriptionPlanFilter)
        setAppliedSubscriptionBillingModeFilter(subscriptionBillingModeFilter)
        setAppliedExpiryFilter(expiryFilter)
        setAppliedPaidStartAtFrom(paidStartAtFrom)
        setAppliedPaidStartAtTo(paidStartAtTo)
        setAppliedIndividualEndsAtFrom(individualEndsAtFrom)
        setAppliedIndividualEndsAtTo(individualEndsAtTo)
    }

    const resetSubscriptionFilters = () => {
        setSubscriptionSearch('')
        setSubscriptionStatusFilter('ALL')
        setSubscriptionOrgFilter('ALL')
        setSubscriptionKindFilter('ALL')
        setSubscriptionPlanFilter('ALL')
        setSubscriptionBillingModeFilter('ALL')
        setExpiryFilter('ALL')
        setPaidStartAtFrom('')
        setPaidStartAtTo('')
        setIndividualEndsAtFrom('')
        setIndividualEndsAtTo('')
        setSubscriptionPage(1)
        setAppliedSubscriptionSearch('')
        setAppliedSubscriptionStatusFilter('ALL')
        setAppliedSubscriptionOrgFilter('ALL')
        setAppliedSubscriptionKindFilter('ALL')
        setAppliedSubscriptionPlanFilter('ALL')
        setAppliedSubscriptionBillingModeFilter('ALL')
        setAppliedExpiryFilter('ALL')
        setAppliedPaidStartAtFrom('')
        setAppliedPaidStartAtTo('')
        setAppliedIndividualEndsAtFrom('')
        setAppliedIndividualEndsAtTo('')
    }

    const applyServiceUsageFilters = () => {
        setServiceUsagePage(1)
        setAppliedServiceUsageSearch(serviceUsageSearch)
        setAppliedServiceUsageRefundBlockingFilter(serviceUsageRefundBlockingFilter)
        setAppliedServiceUsageCategoryFilter(serviceUsageCategoryFilter)
        setAppliedServiceUsagePaidAfterFilter(serviceUsagePaidAfterFilter)
        setAppliedServiceUsageDateFrom(serviceUsageDateFrom)
        setAppliedServiceUsageDateTo(serviceUsageDateTo)
    }

    const resetServiceUsageFilters = () => {
        setServiceUsageSearch('')
        setServiceUsageRefundBlockingFilter('ALL')
        setServiceUsageCategoryFilter('ALL')
        setServiceUsagePaidAfterFilter('ALL')
        setServiceUsageDateFrom('')
        setServiceUsageDateTo('')
        setServiceUsagePage(1)
        setAppliedServiceUsageSearch('')
        setAppliedServiceUsageRefundBlockingFilter('ALL')
        setAppliedServiceUsageCategoryFilter('ALL')
        setAppliedServiceUsagePaidAfterFilter('ALL')
        setAppliedServiceUsageDateFrom('')
        setAppliedServiceUsageDateTo('')
    }
    const applyOrderFilters = () => {
        setOrderPage(1)
        setAppliedOrderSearch(orderSearch)
        setAppliedOrderProductFilter(orderProductFilter)
        setAppliedOrderStatusFilter(orderStatusFilter)
        setAppliedOrderPgProviderFilter(orderPgProviderFilter)
        setAppliedOrderDateFrom(orderDateFrom)
        setAppliedOrderDateTo(orderDateTo)
    }

    const resetOrderFilters = () => {
        setOrderSearch('')
        setOrderProductFilter('ALL')
        setOrderStatusFilter('ALL')
        setOrderPgProviderFilter('ALL')
        setOrderDateFrom('')
        setOrderDateTo('')
        setOrderPage(1)
        setAppliedOrderSearch('')
        setAppliedOrderProductFilter('ALL')
        setAppliedOrderStatusFilter('ALL')
        setAppliedOrderPgProviderFilter('ALL')
        setAppliedOrderDateFrom('')
        setAppliedOrderDateTo('')
    }

    const applyRefundFilters = () => {
        setRefundPage(1)
        setAppliedRefundSearch(refundSearch)
        setAppliedRefundStatusFilter(refundStatusFilter)
        setAppliedRefundDateFrom(refundDateFrom)
        setAppliedRefundDateTo(refundDateTo)
    }

    const resetRefundFilters = () => {
        setRefundSearch('')
        setRefundStatusFilter('ALL')
        setRefundDateFrom('')
        setRefundDateTo('')
        setRefundPage(1)
        setAppliedRefundSearch('')
        setAppliedRefundStatusFilter('ALL')
        setAppliedRefundDateFrom('')
        setAppliedRefundDateTo('')
    }

    const applyAuditFilters = () => {
        setAuditPage(1)
        setAppliedAuditSearch(auditSearch)
        setAppliedAuditActionFilter(auditActionFilter)
        setAppliedAuditTargetTypeFilter(auditTargetTypeFilter)
        setAppliedAuditProviderFilter(auditProviderFilter)
        setAppliedAuditStatusFilter(auditStatusFilter)
        setAppliedAuditOrderNoFilter(auditOrderNoFilter)
        setAppliedAuditPaymentIdFilter(auditPaymentIdFilter)
        setAppliedAuditWebhookEventIdFilter(auditWebhookEventIdFilter)
        setAppliedAuditDateFrom(auditDateFrom)
        setAppliedAuditDateTo(auditDateTo)
    }

    const resetAuditFilters = () => {
        setAuditSearch('')
        setAuditActionFilter('ALL')
        setAuditTargetTypeFilter('ALL')
        setAuditProviderFilter('ALL')
        setAuditStatusFilter('ALL')
        setAuditOrderNoFilter('')
        setAuditPaymentIdFilter('')
        setAuditWebhookEventIdFilter('')
        setAuditDateFrom('')
        setAuditDateTo('')
        setAuditPage(1)
        setAppliedAuditSearch('')
        setAppliedAuditActionFilter('ALL')
        setAppliedAuditTargetTypeFilter('ALL')
        setAppliedAuditProviderFilter('ALL')
        setAppliedAuditStatusFilter('ALL')
        setAppliedAuditOrderNoFilter('')
        setAppliedAuditPaymentIdFilter('')
        setAppliedAuditWebhookEventIdFilter('')
        setAppliedAuditDateFrom('')
        setAppliedAuditDateTo('')
    }

    const handleEnterToApply = (event, applyFn) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyFn()
        }
    }

    const changeSubscriptionSort = (nextKey) => {
        if (subscriptionSortKey === nextKey) {
            setSubscriptionSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSubscriptionSortKey(nextKey)
        setSubscriptionSortDirection('asc')
    }

    const changeOrderSort = (nextKey) => {
        if (orderSortKey === nextKey) {
            setOrderSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setOrderSortKey(nextKey)
        setOrderSortDirection(nextKey === 'amount' ? 'desc' : 'desc')
    }

    const changeRefundSort = (nextKey) => {
        if (refundSortKey === nextKey) {
            setRefundSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setRefundSortKey(nextKey)
        setRefundSortDirection(nextKey === 'amount' ? 'desc' : 'desc')
    }

    const changeAuditSort = (nextKey) => {
        if (auditSortKey === nextKey) {
            setAuditSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setAuditSortKey(nextKey)
        setAuditSortDirection('desc')
    }

    const downloadSubscriptionsCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/subscriptions', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedSubscriptionSearch,
                        status: appliedSubscriptionStatusFilter,
                        orgName: appliedSubscriptionOrgFilter,
                        planCode: appliedSubscriptionPlanFilter,
                        billingMode: appliedSubscriptionBillingModeFilter,
                        subscriptionKind: appliedSubscriptionKindFilter,
                        paidStartAtFrom: appliedPaidStartAtFrom,
                        paidStartAtTo: appliedPaidStartAtTo,
                        individualEndsAtFrom: appliedIndividualEndsAtFrom,
                        individualEndsAtTo: appliedIndividualEndsAtTo,
                        expiry: appliedExpiryFilter,
                        sortKey: subscriptionSortKey,
                        sortDirection: subscriptionSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 구독 데이터가 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['조직', '이름', '구분', '플랜', '과금모델', '최초 유료 구독 시작일', '개인 구독 만료일', '상태', '팩스 잔여', '팩스 예약', '팩스 사용', '문자 잔여', '분석 잔여'],
                    ...items.map((item) => [
                        item.orgName || '',
                        item.targetName || '',
                        formatSubscriptionKindLabel(item.subscriptionKind),
                        formatSubscriptionHubPlanCode(item),
                        formatSubscriptionBillingModeLabel(item),
                        formatDate(item.firstPaidStartedAt) || '',
                        formatSubscriptionExpiry(item.individualEndsAt),
                        formatBillingStatusLabel(item.status, 'subscription'),
                        String(formatLimit(item.claimFaxRemaining)),
                        String(formatLimit(item.claimFaxReserved)),
                        String(formatLimit(item.claimFaxUsed)),
                        String(formatLimit(item.groupSmsRemaining)),
                        String(formatLimit(item.healthAnalysisRemaining)),
                    ]),
                ]
                const csv = '\uFEFF' + rows.map((row) => row.map(csvEscape).join(',')).join('\n')
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `billing_subscriptions_${new Date().toISOString().slice(0, 10)}.csv`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                show('구독 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const downloadOrdersCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/orders', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedOrderSearch,
                        productName: appliedOrderProductFilter,
                        status: appliedOrderStatusFilter,
                        pgProvider: appliedOrderPgProviderFilter,
                        dateFrom: appliedOrderDateFrom,
                        dateTo: appliedOrderDateTo,
                        sortKey: orderSortKey,
                        sortDirection: orderSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 주문 데이터가 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['주문번호', '구매자', '조직', '상품', '금액', '상태', 'PG', '결제수단', 'PG거래번호', '결제일시'],
                    ...items.map((order) => [
                        order.orderNo || order.id || '',
                        order.buyerName || '',
                        order.orgName || order.targetType || '',
                        orderProductLabel(order),
                        String(order.amount ?? ''),
                        order.status || '',
                        order.pgProvider || '',
                        order.paymentMethod || '',
                        order.pgTransactionId || order.pgOrderId || '',
                        formatDateTimeMinute(order.paidAt || order.createdAt) || '',
                    ]),
                ]
                downloadCsvFile(rows, `billing_orders_${new Date().toISOString().slice(0, 10)}.csv`)
            } catch (e) {
                show('주문 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const downloadRefundsCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/refunds', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedRefundSearch,
                        status: appliedRefundStatusFilter,
                        dateFrom: appliedRefundDateFrom,
                        dateTo: appliedRefundDateTo,
                        sortKey: refundSortKey,
                        sortDirection: refundSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 환불 데이터가 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['환불번호', '주문번호', '결제ID', '요청자', '금액', '상태', '사유', '요청시각'],
                    ...items.map((refund) => [
                        refund.refundNo || refund.id || '',
                        refund.orderNo || refund.orderId || '',
                        refund.paymentId || '',
                        refund.requestedBy || '',
                        String(refund.amount ?? ''),
                        refund.status || '',
                        refund.reason || '',
                        formatDateTimeMinute(refund.requestedAt || refund.updatedAt) || '',
                    ]),
                ]
                downloadCsvFile(rows, `billing_refunds_${new Date().toISOString().slice(0, 10)}.csv`)
            } catch (e) {
                show('환불 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const downloadAuditLogsCsv = () => {
        ;(async () => {
            try {
                const response = await api.get('/billing/admin/audit-logs', {
                    params: {
                        page: 1,
                        size: 20000,
                        search: appliedAuditSearch,
                        action: appliedAuditActionFilter,
                        targetType: appliedAuditTargetTypeFilter,
                        provider: appliedAuditProviderFilter,
                        status: appliedAuditStatusFilter,
                        orderNo: appliedAuditOrderNoFilter,
                        paymentId: appliedAuditPaymentIdFilter,
                        webhookEventId: appliedAuditWebhookEventIdFilter,
                        dateFrom: appliedAuditDateFrom,
                        dateTo: appliedAuditDateTo,
                        sortKey: auditSortKey,
                        sortDirection: auditSortDirection,
                    },
                })
                const items = Array.isArray(response?.data?.data?.items) ? response.data.data.items : []
                if (items.length === 0) {
                    show('다운로드할 감사 이력이 없습니다.', 'error')
                    return
                }
                const rows = [
                    ['행위자 이메일', '액션', '대상유형', '대상ID', '상태', 'PG', '주문번호', '결제ID', '웹훅ID', '사유', '시각'],
                    ...items.map((log) => [
                        log.actorEmail || '',
                        log.action || '',
                        log.targetType || '',
                        String(log.targetId ?? ''),
                        pickAuditStatus(log) || '',
                        log.provider || '',
                        log.orderNo || '',
                        String(log.paymentId ?? ''),
                        log.webhookEventId || '',
                        log.reason || '',
                        formatDateTimeMinute(log.createdAt) || '',
                    ]),
                ]
                downloadCsvFile(rows, `billing_audit_logs_${new Date().toISOString().slice(0, 10)}.csv`)
            } catch (e) {
                show('감사 이력 CSV 다운로드에 실패했습니다.', 'error')
            }
        })()
    }

    const openBulkTrial = (revoke = false) => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 구독을 선택해주세요.', 'error')
            return
        }
        setBulkTrialDays(7)
        setBulkTrialReason(revoke ? '운영자 일괄 Trial 회수' : '운영자 일괄 Trial 지급')
        setBulkTrialModal({ revoke })
    }


    const applyCustomerMemoStorageQuota = async (reset = false) => {
        if (!selectedQuotaSubscription) return
        const raw = String(customerMemoStorageQuotaDraft ?? '').trim()
        const quotaGb = Number.parseInt(raw, 10)
        if (!reset && (!Number.isFinite(quotaGb) || quotaGb < 1)) {
            show('저장한도는 1GB 이상 정수로 입력해 주세요.', 'error')
            return
        }
        try {
            setCustomerMemoStorageQuotaLoading(true)
            const response = await api.post(`/billing/admin/subscriptions/${selectedQuotaSubscription.id}/customer-memo-storage-quota`, reset
                ? { reset: true, note: '운영자 기본값 복원' }
                : { quotaGb, note: '운영자 저장한도 설정' })
            const quota = response?.data?.data ?? null
            if (quota) {
                setSelectedQuotaSubscription((prev) => prev ? ({
                    ...prev,
                    customerMemoStorageQuota: quota,
                    customerMemoStorageQuotaBytes: quota.maxUserAttachmentBytes,
                    customerMemoStorageUsedBytes: quota.userAttachmentBytes,
                    customerMemoStorageRemainingBytes: quota.userAttachmentRemainingBytes,
                    customerMemoStorageQuotaSource: quota.quotaSource,
                    customerMemoStorageQuotaSourceLabel: quota.quotaSourceLabel,
                }) : prev)
                setCustomerMemoStorageQuotaDraft(storageQuotaGbDraft(quota.maxUserAttachmentBytes))
            }
            show(reset ? '고객메모 저장한도를 플랜 기본값으로 복원했습니다.' : '고객메모 저장한도를 저장했습니다.', 'success')
            await loadData()
        } catch (e) {
            show(`고객메모 저장한도 저장에 실패했습니다.${pickBillingLoadDetailSuffix(e)}`, 'error')
        } finally {
            setCustomerMemoStorageQuotaLoading(false)
        }
    }

    const submitBulkTrial = async () => {
        if (!bulkTrialModal) return
        try {
            setBulkTrialLoading(true)
            await api.post('/billing/admin/subscriptions/trial-bulk', {
                subscriptionIds: selectedSubscriptionIds,
                revoke: bulkTrialModal.revoke,
                days: Number(bulkTrialDays) || 7,
                reason: bulkTrialReason || (bulkTrialModal.revoke ? '운영자 일괄 Trial 회수' : '운영자 일괄 Trial 지급'),
            })
            show(bulkTrialModal.revoke ? '일괄 Trial 회수가 완료되었습니다.' : '일괄 Trial 지급이 완료되었습니다.', 'success')
            setBulkTrialModal(null)
            setSelectedSubscriptionIds([])
            await loadData()
        } catch (e) {
            show('일괄 Trial 처리에 실패했습니다.', 'error')
        } finally {
            setBulkTrialLoading(false)
        }
    }

    const submitBulkUpdate = async () => {
        if (!bulkUpdateModal) return
        const reason = bulkUpdateReason.trim()
        if (!reason) {
            show('변경 사유를 입력해주세요.', 'error')
            return
        }
        await updateSelectedSubscriptions({
            ...bulkUpdateModal.payload,
            reason,
        }, bulkUpdateModal.successMessage, bulkUpdateModal.errorMessage, () => {
            if (bulkUpdateModal.type === 'status') {
                setBulkStatus('')
            }
            if (bulkUpdateModal.type === 'plan') {
                setBulkPlanCode('')
            }
            setBulkUpdateReason('')
            setBulkUpdateModal(null)
        })
    }

    const openBulkCompose = () => {
        if (selectedSubscriptionIds.length === 0) {
            show('먼저 대상 회원을 선택해주세요.', 'error')
            return
        }
        const eligible = selectedSubscriptions.filter((s) => s.billingReady !== false)
        if (eligible.length === 0) {
            show('billing이 준비된 회원을 선택해주세요.', 'error')
            return
        }
        setBulkComposeStatus('')
        setBulkComposePlan('')
        setBulkComposeBillingMode('')
        setBulkComposeGrantDays('')
        setBulkComposeQuotaFeature('')
        setBulkComposeQuotaDelta('')
        setBulkComposeReason('운영자 일괄 적용')
        setBulkComposeOpen(true)
    }

    const submitBulkCompose = async () => {
        const reason = bulkComposeReason.trim()
        if (!reason) {
            show('변경 사유를 입력해주세요.', 'error')
            return
        }
        const statusVal = bulkComposeStatus.trim()
        const planVal = bulkComposePlan.trim()
        const billingModeVal = bulkComposeBillingMode.trim()
        const grantDaysVal = parseInt(String(bulkComposeGrantDays).trim(), 10)
        const quotaDeltaRaw = String(bulkComposeQuotaDelta).trim()
        const quotaDeltaVal = quotaDeltaRaw === '' ? NaN : parseInt(quotaDeltaRaw, 10)
        const quotaFeatureVal = bulkComposeQuotaFeature.trim()
        const hasPatch = Boolean(statusVal || billingModeVal)
        const hasPlanChange = Boolean(planVal)
        const hasGrant = !Number.isNaN(grantDaysVal) && grantDaysVal !== 0
        const hasQuotaAdjust = quotaFeatureVal && !Number.isNaN(quotaDeltaVal) && quotaDeltaVal !== 0
        if (!hasPatch && !hasPlanChange && !hasGrant && !hasQuotaAdjust) {
            show('구독 상태·플랜·과금모델·구독일·할당량 조정 중 최소 한 항목을 지정해주세요.', 'error')
            return
        }
        if (hasPlanChange && hasPatch) {
            show('\uD50C\uB79C \uBCC0\uACBD\uC740 \uC0C1\uD0DC\u00B7\uACFC\uAE08\uBAA8\uB378 \uBCC0\uACBD\uACFC \uBD84\uB9AC\uD574 \uC801\uC6A9\uD574\uC8FC\uC138\uC694.', 'error')
            return
        }
        const ids = selectedSubscriptions.filter((s) => s.billingReady !== false).map((s) => s.id)
        if (ids.length === 0) {
            show('적용할 대상이 없습니다.', 'error')
            return
        }
        try {
            setBulkComposeLoading(true)
            if (hasPatch) {
                const payload = { reason }
                if (statusVal) payload.status = statusVal
                if (billingModeVal) payload.billingMode = billingModeVal
                await Promise.all(ids.map((id) => api.patch(`/billing/admin/subscriptions/${id}`, payload)))
            }
            if (hasPlanChange) {
                await Promise.all(ids.map((id) => (
                    api.post(`/billing/admin/subscriptions/${id}/plan-change`, {
                        targetPlanCode: planVal,
                        reason,
                    })
                )))
            }
            if (hasGrant) {
                await Promise.all(ids.map((id) => (
                    api.post(`/billing/admin/subscriptions/${id}/grant-days`, {
                        deltaDays: grantDaysVal,
                        reason,
                    })
                )))
            }
            if (hasQuotaAdjust) {
                await Promise.all(ids.map((id) => (
                    api.post(`/billing/admin/subscriptions/${id}/quota-limit-adjust`, {
                        featureCode: quotaFeatureVal,
                        delta: quotaDeltaVal,
                        reason,
                    })
                )))
            }
            show('선택한 회원에게 일괄 적용했습니다.', 'success')
            setBulkComposeOpen(false)
            setBulkComposeStatus('')
            setBulkComposePlan('')
            setBulkComposeBillingMode('')
            setBulkComposeGrantDays('')
            setBulkComposeQuotaFeature('')
            setBulkComposeQuotaDelta('')
            setBulkComposeReason('')
            setSelectedSubscriptionIds([])
            await loadData()
        } catch (e) {
            show('일괄 적용에 실패했습니다.', 'error')
        } finally {
            setBulkComposeLoading(false)
        }
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl font-bold">{isDashboardView ? '대시보드' : '과금/구독 허브'}</h1>
                        <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor={subscriptionsManualAnchor} />
                    </div>
                    {isDashboardView ? (
                        <div className="mt-2">
                            <button
                                type="button"
                                className="text-sm font-medium text-brand-navy hover:underline"
                                onClick={() => navigate(billingAdminHubPath('status'))}
                            >
                                과금/구독 허브로 이동
                            </button>
                        </div>
                    ) : (
                        <div className="mt-2">
                            <button
                                type="button"
                                className="text-sm font-medium text-slate-600 hover:text-brand-navy hover:underline"
                                onClick={() => navigate('/subscriptions/dashboard')}
                            >
                                대시보드로 이동
                            </button>
                        </div>
                    )}
                </div>
                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={loadData}>새로고침</button>
            </div>

            {!isDashboardView && (
                <div className="mt-6 border-b">
                    <nav className="flex flex-wrap gap-x-6">
                        {hubTabMenus.map((tab) => (
                            <button
                                type="button"
                                key={tab.id}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeMenu === tab.id ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                                onClick={() => navigate(tab.path)}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            {loading && !billingAdminListTabActive(activeMenu) && <div className="mt-8 text-slate-500">로딩 중...</div>}
            {!loading && error && !billingAdminListTabActive(activeMenu) && (
                <div className="mt-8 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {!loading && !error && activeMenu === 'dashboard' && (
                <div className="mt-4 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
                        <div className="inline-flex flex-wrap rounded border border-slate-200 bg-slate-50 p-1" role="group" aria-label="대시보드 집계 기간">
                            {BILLING_DASHBOARD_PERIOD_OPTIONS.map((option) => (
                                <button key={option.value} type="button" className={`rounded px-3 py-1.5 text-xs font-medium ${dashboardPeriodPreset === option.value ? 'bg-brand-navy text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:text-slate-950'}`} aria-pressed={dashboardPeriodPreset === option.value} onClick={() => setDashboardPeriodPreset(option.value)}>
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <div className="text-xs text-slate-500">{formatDashboardPeriodRange(dashboardPeriodInfo)} · 업데이트 {formatDateTimeMinute(dashboard?.generatedAt) || '-'}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-6">
                        <DashboardMetricCard label="예상 월 반복매출" value={currency(Number(dashboardSummary.estimatedMrr || 0))} change={dashboardSummary.mrrChangeRate} meta={`${dashboardPeriodInfo.comparisonLabel || '직전 기간'} 말 대비 · VAT 제외`} />
                        <DashboardMetricCard label={`${dashboardPeriodInfo.label || '선택 기간'} 순수납`} value={currency(Number(dashboardSummary.collectedAmount || 0))} change={dashboardSummary.collectedAmountChangeRate} meta={`환불 ${currency(Number(dashboardSummary.refundedAmount || 0))}`} />
                        <DashboardMetricCard label="활성 개인 구독" value={`${Number(dashboardSummary.activePaidSubscriptions || 0).toLocaleString()}명`} meta={`조직 계약 ${Number(dashboardSummary.activeOrgContracts || 0).toLocaleString()}건`} />
                        <DashboardMetricCard label="구독 이탈률" value={formatDashboardPercent(dashboardSummary.churnRate)} meta={`${dashboardPeriodInfo.label || '선택 기간'} 종료 ${Number(dashboardSummary.churnedSubscriptions || 0).toLocaleString()}명`} alert={Number(dashboardSummary.churnRate || 0) > 5} />
                        <DashboardMetricCard label="결제 성공률" value={formatDashboardPercent(dashboardSummary.paymentSuccessRate)} meta={`${Number(dashboardSummary.successfulPayments || 0).toLocaleString()}건 성공 · ${Number(dashboardSummary.failedPayments || 0).toLocaleString()}건 실패`} alert={Number(dashboardSummary.failedPayments || 0) > 0} />
                        <DashboardMetricCard label="체험 전환율" value={formatDashboardPercent(dashboardSummary.trialConversionRate)} meta={`${Number(dashboardSummary.trialConversions || 0).toLocaleString()} / ${Number(dashboardSummary.trialStarts || 0).toLocaleString()}명`} />
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                        <DashboardPanel
                            title="기간별 순수납 추세"
                            meta={formatDashboardPeriodRange(dashboardPeriodInfo)}
                            className="xl:col-span-2"
                        >
                            <div className="h-64 min-w-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={dashboardTrend} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="date" tickFormatter={formatDashboardTrendDate} tick={{ fontSize: 11 }} minTickGap={24} />
                                        <YAxis tickFormatter={compactDashboardCurrency} tick={{ fontSize: 11 }} width={58} />
                                        <Tooltip
                                            formatter={(value, name) => [currency(Number(value || 0)), dashboardTrendLabel(name)]}
                                            labelFormatter={(value) => formatDate(value)}
                                        />
                                        <Legend formatter={dashboardTrendLabel} wrapperStyle={{ fontSize: 12 }} />
                                        <Line type="monotone" dataKey="collectedAmount" stroke="#0f766e" strokeWidth={3} dot={false} activeDot={{ r: 4 }} />
                                        <Line type="monotone" dataKey="iapAmount" stroke="#2563eb" strokeWidth={2} dot={false} />
                                        <Line type="monotone" dataKey="orgPaidAmount" stroke="#d97706" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </DashboardPanel>

                        <DashboardPanel title={`${dashboardPeriodInfo.label || '선택 기간'} 흐름`} meta={dashboardPeriodInfo.comparisonLabel || '직전 기간 비교'}>
                            <div className="border-b border-slate-100 pb-3">
                                <div className="text-xs font-medium text-slate-500">순수납</div>
                                <div className="mt-1 text-xl font-semibold text-slate-950">
                                    {currency(Number(dashboardPeriodComparison.currentCollectedAmount || 0))}
                                </div>
                            </div>
                            <div className="divide-y divide-slate-100">
                                <DashboardComparisonRow
                                    label={`${dashboardPeriodInfo.comparisonLabel || '직전 기간'} 대비`}
                                    baseline={dashboardPeriodComparison.previousCollectedAmount}
                                    change={dashboardPeriodComparison.collectedAmountChangeRate}
                                />
                            </div>
                            <div className="space-y-3 border-t border-slate-100 pt-3">
                                <DashboardProgressBar
                                    label="비교 기간 달성률"
                                    value={dashboardPeriodComparison.comparisonAttainmentRate}
                                    detail={`${dashboardPeriodInfo.comparisonLabel || '직전 기간'} ${currency(Number(dashboardPeriodComparison.previousCollectedAmount || 0))}`}
                                />
                                <DashboardProgressBar label="기간 진행률" value={dashboardPeriodComparison.progressRate} />
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
                                <DashboardInlineValue
                                    label={dashboardPeriodInfo.preset === 'THIS_MONTH' ? '월말 단순 예상' : '선택 기간 순수납'}
                                    value={currency(Number(dashboardPeriodComparison.projectedCollectedAmount || 0))}
                                    subvalue={formatDashboardChange(dashboardPeriodComparison.projectedChangeRate, dashboardPeriodInfo.comparisonLabel || '직전 기간')}
                                />
                                <DashboardInlineValue
                                    label="수납 페이스"
                                    value={formatDashboardPercent(dashboardPeriodComparison.paceIndex)}
                                    subvalue={dashboardPaceLabel(dashboardPeriodComparison.paceIndex)}
                                />
                            </div>
                        </DashboardPanel>

                        <DashboardPanel title="운영 처리 큐" meta={dashboardPendingQueue.length > 0 ? `${dashboardPendingQueue.length}개 항목 확인 필요` : '정상'}>
                            <div className="divide-y divide-slate-100">
                                {dashboardQueueToShow.map((item) => {
                                    const pending = Number(item.count || 0) > 0
                                    return (
                                        <button
                                            key={item.code}
                                            type="button"
                                            className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                            onClick={() => navigate(billingDashboardQueuePath(item.targetMenu))}
                                        >
                                            <span className="min-w-0 truncate text-sm text-slate-700">{item.label}</span>
                                            <span className={`shrink-0 text-sm font-semibold ${pending ? 'text-amber-700' : 'text-slate-400'}`}>{Number(item.count || 0).toLocaleString()}</span>
                                        </button>
                                    )
                                })}
                                {dashboardQueueToShow.length === 0 && <div className="py-8 text-center text-sm text-slate-500">처리할 항목이 없습니다.</div>}
                            </div>
                        </DashboardPanel>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <DashboardPanel title="플랜·채널 구성">
                            <DashboardCompactList
                                items={dashboardPlanMix.map((item) => ({
                                    key: item.segmentCode || `${item.planCode}-${item.subscriberType || 'INDIVIDUAL'}`,
                                    label: item.displayName || `${item.planCode} - ${item.subscriberTypeLabel || '개인'}`,
                                    value: `${Number(item.count || 0).toLocaleString()}${item.countUnit || '명'}`,
                                    subvalue: currency(Number(item.monthlyAmount || 0)),
                                }))}
                                empty="활성 플랜이 없습니다."
                            />
                            <div className="mt-3 border-t border-slate-100 pt-2">
                                <DashboardCompactList
                                    items={dashboardChannelMix.map((item) => ({
                                        key: item.channel,
                                        label: dashboardChannelLabel(item.channel),
                                        value: `${Number(item.count || 0).toLocaleString()}${item.countUnit || '명'}`,
                                    }))}
                                    empty="채널 정보가 없습니다."
                                />
                            </div>
                        </DashboardPanel>

                        <DashboardPanel title="구독 증감 비교" meta={dashboardPeriodInfo.comparisonLabel || '직전 기간'}>
                            <div className="h-48 min-w-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dashboardSubscriptionComparison} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                        <Tooltip />
                                        <Bar dataKey="newPaidSubscriptions" name="신규" fill="#0284c7" radius={[2, 2, 0, 0]} />
                                        <Bar dataKey="churnedSubscriptions" name="이탈" fill="#e11d48" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </DashboardPanel>

                        <DashboardPanel title="체험 전환">
                            <div className="grid grid-cols-3 divide-x divide-slate-100 py-3 text-center">
                                <DashboardInlineMetric label="체험 시작" value={dashboard?.trialFunnel?.started} />
                                <DashboardInlineMetric label="유료 전환" value={dashboard?.trialFunnel?.converted} />
                                <DashboardInlineMetric label="현재 체험" value={dashboard?.trialFunnel?.active} />
                            </div>
                            <div className="mt-4 border-t border-slate-100 pt-3">
                                <div className="flex items-center justify-between text-xs text-slate-500">
                                    <span>전환율</span>
                                    <strong className="text-sm text-slate-900">{formatDashboardPercent(dashboard?.trialFunnel?.conversionRate)}</strong>
                                </div>
                            </div>
                        </DashboardPanel>

                        <DashboardPanel title="조직 청구·정산">
                            <DashboardCompactList
                                items={[
                                    { key: 'contracts', label: '활성 계약', value: `${Number(dashboard?.organizationBilling?.activeContracts || 0).toLocaleString()}건` },
                                    { key: 'seats', label: '실제 적용 인원', value: `${Number(dashboard?.organizationBilling?.seats || 0).toLocaleString()}명` },
                                    { key: 'monthly', label: '월 계약 예상액', value: currency(Number(dashboard?.organizationBilling?.monthlyContractAmount || 0)) },
                                    { key: 'generated', label: `${dashboardPeriodInfo.label || '선택 기간'} 생성 청구`, value: currency(Number(dashboard?.organizationBilling?.generatedAmount || 0)), subvalue: `초안 ${Number(dashboard?.organizationBilling?.draftCount || 0).toLocaleString()}건` },
                                    { key: 'issued', label: '발행 청구', value: currency(Number(dashboard?.organizationBilling?.issuedAmount || 0)) },
                                    { key: 'paid', label: '수납', value: currency(Number(dashboard?.organizationBilling?.paidAmount || 0)), subvalue: `회수율 ${formatDashboardPercent(dashboard?.organizationBilling?.collectionRate)}` },
                                    { key: 'outstanding', label: '현재 미수금', value: currency(Number(dashboard?.organizationBilling?.outstandingAmount || 0)) },
                                    { key: 'overdue', label: '현재 연체', value: `${Number(dashboard?.organizationBilling?.overdueCount || 0).toLocaleString()}건`, subvalue: currency(Number(dashboard?.organizationBilling?.overdueAmount || 0)), alert: Number(dashboard?.organizationBilling?.overdueCount || 0) > 0 },
                                ]}
                            />
                        </DashboardPanel>
                    </div>

                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                        <DashboardPanel title="최근 과금 활동" className="xl:col-span-2">
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="border-b border-slate-200 text-xs text-slate-500">
                                        <tr>
                                            <th className="px-2 py-2 text-left font-medium">일시</th>
                                            <th className="px-2 py-2 text-left font-medium">구분</th>
                                            <th className="px-2 py-2 text-left font-medium">활동</th>
                                            <th className="px-2 py-2 text-left font-medium">대상</th>
                                            <th className="px-2 py-2 text-left font-medium">처리 주체</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {(dashboard?.recentAudits || []).map((item) => (
                                            <tr key={item.id} className="align-top">
                                                <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-500">{formatDateTimeMinute(item.createdAt)}</td>
                                                <td className="whitespace-nowrap px-2 py-2 text-xs font-medium text-slate-600">{item.categoryLabel || '과금'}</td>
                                                <td className="px-2 py-2">
                                                    <div className="font-medium text-slate-800">{item.activityLabel || '과금 데이터 변경'}</div>
                                                    {item.reason && <div className="mt-0.5 max-w-[20rem] truncate text-xs text-slate-500">{item.reason}</div>}
                                                </td>
                                                <td className="px-2 py-2">
                                                    <div className="max-w-[18rem] truncate text-slate-700">{item.targetDisplayName || '-'}</div>
                                                    <div className="mt-0.5 text-xs text-slate-400">{item.targetTypeLabel || '과금 대상'}</div>
                                                </td>
                                                <td className="px-2 py-2">
                                                    <div className="whitespace-nowrap text-slate-700">{item.actorName || '시스템 자동 처리'}</div>
                                                    {item.providerLabel && <div className="mt-0.5 text-xs text-slate-400">{item.providerLabel}</div>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {(dashboard?.recentAudits || []).length === 0 && <div className="py-8 text-center text-sm text-slate-500">최근 과금 활동이 없습니다.</div>}
                            </div>
                        </DashboardPanel>

                        <DashboardPanel title="회원·구독 리스크">
                            <DashboardCompactList
                                items={[
                                    { key: 'members', label: '전체 회원', value: `${Number(dashboard?.totalMembers || 0).toLocaleString()}명` },
                                    { key: 'today', label: '오늘 가입', value: `${Number(dashboard?.todayJoinedMembers || 0).toLocaleString()}명` },
                                    { key: 'risk', label: '이용 제한 상태', value: `${Number(dashboard?.suspendedSubscriptions || 0).toLocaleString()}명`, alert: Number(dashboard?.suspendedSubscriptions || 0) > 0 },
                                    { key: 'expiring', label: '7일 내 만료', value: `${Number(dashboard?.expiringIn7Days || 0).toLocaleString()}명`, alert: Number(dashboard?.expiringIn7Days || 0) > 0 },
                                    { key: 'refunds', label: '환불 처리 대기', value: `${Number(dashboard?.pendingRefunds || 0).toLocaleString()}건`, alert: Number(dashboard?.pendingRefunds || 0) > 0 },
                                ]}
                            />
                        </DashboardPanel>
                    </div>
                </div>
            )}

            {activeMenu === 'operationQueue' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold">{'\uACFC\uAE08 \uC6B4\uC601 \uD050'}</h2>
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${operationQueue.some((item) => Number(item?.count || 0) > 0) ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}>
                            {operationQueue.reduce((total, item) => total + Number(item?.count || 0), 0) > 0
                                ? `\uD655\uC778 \uD544\uC694 ${operationQueue.reduce((total, item) => total + Number(item?.count || 0), 0).toLocaleString()}\uAC74`
                                : '\uC815\uC0C1'}
                        </span>
                    </div>
                    <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="border-y border-slate-200 bg-slate-50 text-xs text-slate-500">
                                <tr>
                                    <th className="px-3 py-2.5 text-left font-medium">{'\uD655\uC778 \uD56D\uBAA9'}</th>
                                    <th className="px-3 py-2.5 text-right font-medium">{'\uAC74\uC218'}</th>
                                    <th className="px-3 py-2.5 text-center font-medium">{'\uC0C1\uD0DC'}</th>
                                    <th className="px-3 py-2.5 text-right font-medium">{'\uBC14\uB85C\uAC00\uAE30'}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {operationQueue.map((item) => {
                                    const count = Number(item?.count || 0)
                                    const pending = count > 0
                                    return (
                                        <tr key={item.code || item.label} className={pending ? 'bg-amber-50/40' : ''}>
                                            <td className="px-3 py-3 font-medium text-slate-800">{item.label || '-'}</td>
                                            <td className={`px-3 py-3 text-right font-semibold ${pending ? 'text-amber-800' : 'text-slate-500'}`}>{count.toLocaleString()}{'\uAC74'}</td>
                                            <td className="px-3 py-3 text-center">
                                                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${pending ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}>
                                                    {pending ? '\uD655\uC778 \uD544\uC694' : '\uC815\uC0C1'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-right">
                                                <button type="button" className="text-sm font-medium text-brand-navy hover:underline" onClick={() => navigate(billingDashboardQueuePath(item.targetMenu))}>
                                                    {'\uBC14\uB85C\uAC00\uAE30'}
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                        {operationQueue.length === 0 && <div className="py-8 text-center text-sm text-slate-500">{'\uD45C\uC2DC\uD560 \uC6B4\uC601 \uD56D\uBAA9\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.'}</div>}
                    </div>
                </div>
            )}

            {activeMenu === 'status' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-semibold">구독 상태</h2>
                            <p className="mt-1 text-sm text-slate-500">전체 회원 기준으로 구독 상태를 조회합니다. <code>GA_PROXY</code> 생성 회원은 제외됩니다.</p>
                            <p className="mt-1 text-xs text-amber-800/90">목록은 서버에서 전체 회원을 집계한 뒤 페이징합니다. 회원 수가 많으면 첫 로딩이 길어질 수 있습니다.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <select
                                className="rounded border px-3 py-2 text-xs hover:bg-white"
                                value={subscriptionPageSize}
                                onChange={(e) => {
                                    const n = Number(e.target.value)
                                    setSubscriptionPageSize(SUBSCRIPTION_PAGE_SIZES.includes(n) ? n : 10)
                                }}
                            >
                                {SUBSCRIPTION_PAGE_SIZES.map((sz) => (
                                    <option key={sz} value={sz}>{sz}개씩</option>
                                ))}
                            </select>
                            <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-white" onClick={resetSubscriptionFilters}>필터 초기화</button>
                            <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-white" onClick={downloadSubscriptionsCsv}>CSV 다운로드</button>
                            <button
                                type="button"
                                className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                                onClick={openWithdrawnStoreSubscriptionsModal}
                                disabled={withdrawnStoreSubscriptionLoading}
                            >
                                {withdrawnStoreSubscriptionLoading ? '조회 중...' : '구독중인 탈퇴회원 보기'}
                            </button>
                        </div>
                    </div>

                    <form
                        id="subscription-status-filter-form"
                        className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5"
                        onSubmit={(e) => {
                            e.preventDefault()
                            applySubscriptionFilters()
                        }}
                    >
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">검색어</span>
                            <input
                                className="w-full rounded border px-3 py-2 text-sm"
                                placeholder="조직/이름/이메일/플랜"
                                value={subscriptionSearch}
                                onChange={(e) => setSubscriptionSearch(e.target.value)}
                                onKeyDown={(e) => handleEnterToApply(e, applySubscriptionFilters)}
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">조직</span>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={subscriptionOrgFilter} onChange={(e) => setSubscriptionOrgFilter(e.target.value)}>
                                <option value="ALL">전체 조직</option>
                                <option value="미지정">미지정</option>
                                {orgOptions.map((orgName) => (
                                    <option key={orgName} value={orgName}>{orgName}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">구분</span>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={subscriptionKindFilter} onChange={(e) => setSubscriptionKindFilter(e.target.value)}>
                                <option value="ALL">전체 구분</option>
                                <option value="INDIVIDUAL">개인</option>
                                <option value="ORG">조직</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">플랜</span>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={subscriptionPlanFilter} onChange={(e) => setSubscriptionPlanFilter(e.target.value)}>
                                <option value="ALL">전체 플랜</option>
                                <option value="MISSING">미생성</option>
                                {plans
                                    .filter((plan) => SUBSCRIPTION_HUB_PLAN_CODES.has(String(plan.planCode || '').toUpperCase()))
                                    .map((plan) => (
                                        <option key={plan.planCode} value={plan.planCode}>{plan.planCode}</option>
                                    ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">과금모델</span>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={subscriptionBillingModeFilter} onChange={(e) => setSubscriptionBillingModeFilter(e.target.value)}>
                                <option value="ALL">전체 과금모델</option>
                                <option value="STORE_IAP">인앱결제</option>
                                <option value="INDIVIDUAL">개인 결제</option>
                                <option value="ORG_CONTRACT">조직 결제</option>
                                <option value="WEB_PG">웹 결제</option>
                                <option value="TRIAL">체험</option>
                                <option value="MISSING">미생성</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">상태</span>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={subscriptionStatusFilter} onChange={(e) => setSubscriptionStatusFilter(e.target.value)}>
                                <option value="ALL">전체 상태</option>
                                <option value="SETUP_REQUIRED">구독 미생성</option>
                                <option value="TRIAL">{formatBillingStatusLabel('TRIAL', 'subscription')}</option>
                                <option value="ACTIVE">{formatBillingStatusLabel('ACTIVE', 'subscription')}</option>
                                <option value="SUSPENDED">{formatBillingStatusLabel('SUSPENDED', 'subscription')}</option>
                                <option value="EXPIRED">{formatBillingStatusLabel('EXPIRED', 'subscription')}</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">만료조건</span>
                            <select className="w-full rounded border px-3 py-2 text-sm" value={expiryFilter} onChange={(e) => setExpiryFilter(e.target.value)}>
                                <option value="ALL">전체 만료조건</option>
                                <option value="WITHIN_7_DAYS">7일 이내 만료</option>
                                <option value="WITHIN_30_DAYS">30일 이내 만료</option>
                                <option value="EXPIRED">만료됨</option>
                                <option value="NO_END_AT">만료일 없음</option>
                            </select>
                        </label>
                        <div className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">시작일</span>
                            <div className="grid grid-cols-2 gap-2">
                                <input aria-label="시작일 시작" type="date" className="w-full rounded border px-3 py-2 text-sm" value={paidStartAtFrom} onChange={(e) => setPaidStartAtFrom(e.target.value)} />
                                <input aria-label="시작일 종료" type="date" className="w-full rounded border px-3 py-2 text-sm" value={paidStartAtTo} onChange={(e) => setPaidStartAtTo(e.target.value)} />
                            </div>
                        </div>
                        <div className="block">
                            <span className="mb-1 block text-xs font-medium text-slate-600">만료일</span>
                            <div className="flex flex-col gap-2 sm:flex-row">
                                <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
                                    <input aria-label="만료일 시작" type="date" className="w-full rounded border px-3 py-2 text-sm" value={individualEndsAtFrom} onChange={(e) => setIndividualEndsAtFrom(e.target.value)} />
                                    <input aria-label="만료일 종료" type="date" className="w-full rounded border px-3 py-2 text-sm" value={individualEndsAtTo} onChange={(e) => setIndividualEndsAtTo(e.target.value)} />
                                </div>
                                <button type="submit" className="shrink-0 rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy/90">{'\uAC80\uC0C9'}</button>
                            </div>
                        </div>
                    </form>

                    {subscriptionLoadError && (
                        <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{subscriptionLoadError}</div>
                    )}

                    <div className="mt-4 rounded border bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                            <div className="text-slate-600">
                                검색 결과 <span className="font-semibold text-slate-900">{subscriptionTotalCount}</span>건 / 선택된 회원 <span className="font-semibold text-slate-900">{selectedSubscriptionIds.length}</span>건 / 현재 페이지에서 선택 <span className="font-semibold text-slate-900">{pageSelectedCount}</span>건
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50"
                                    disabled={billingBootstrapLoading}
                                    onClick={() => bootstrapMissingBilling({ dryRun: true })}
                                >
                                    누락 billing dry-run
                                </button>
                                <button
                                    type="button"
                                    className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50"
                                    disabled={billingBootstrapLoading}
                                    onClick={openBillingBootstrapModal}
                                >
                                    누락 billing 생성
                                </button>
                                <button
                                    type="button"
                                    className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50"
                                    disabled={selectedSubscriptionIds.length === 0}
                                    onClick={() => setSelectedSubscriptionIds([])}
                                >
                                    선택 해제
                                </button>
                            </div>
                        </div>

                        {selectedSubscriptionIds.length > 0 && (
                            <div className="mt-3 rounded border bg-white px-4 py-3 text-xs text-slate-600">
                                선택 회원:
                                {' '}
                                <span className="font-medium text-slate-900">
                                    {selectedSubscriptions.slice(0, 3).map((item) => item.targetName).join(', ')}
                                    {selectedSubscriptions.length > 3 ? ` 외 ${selectedSubscriptions.length - 3}명` : ''}
                                </span>
                            </div>
                        )}

                        <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            <button type="button" className="h-7 whitespace-nowrap rounded border bg-white px-2 py-1 text-[11px] leading-4 hover:bg-slate-100 disabled:opacity-50" disabled={!canGrantSelectedSubscriptionDays} onClick={() => openSelectedGrantDays('GRANT')}>기간 부여</button>
                            <button type="button" className="h-7 whitespace-nowrap rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] leading-4 text-rose-700 hover:bg-rose-100 disabled:opacity-50" disabled={!selectedSubscriptions.some((s) => s.billingReady !== false)} onClick={() => openSelectedGrantDays('REVOKE')}>기간 회수</button>
                            <button type="button" className="h-7 whitespace-nowrap rounded border bg-white px-2 py-1 text-[11px] leading-4 hover:bg-slate-100 disabled:opacity-50" disabled={!singleSelectedSubscription} onClick={openSelectedTrialHistory}>Trial 이력</button>
                            <button type="button" className="h-7 whitespace-nowrap rounded border bg-white px-2 py-1 text-[11px] leading-4 hover:bg-slate-100 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || bulkActionLoading} onClick={() => openBulkTrial(false)}>선택 Trial 지급</button>
                            <button type="button" className="h-7 whitespace-nowrap rounded border bg-white px-2 py-1 text-[11px] leading-4 hover:bg-slate-100 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || bulkActionLoading} onClick={() => openBulkTrial(true)}>선택 Trial 회수</button>
                            <button type="button" className="h-7 whitespace-nowrap rounded border border-teal-200 bg-teal-50 px-2 py-1 text-[11px] font-medium leading-4 text-teal-900 hover:bg-teal-100 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || bulkComposeLoading} onClick={openBulkCompose}>일괄 적용</button>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
                            <div className="flex flex-wrap items-center gap-2 rounded border bg-white p-3">
                                <span className="text-xs font-medium text-slate-600">선택 상태 변경</span>
                                <select className="rounded border px-3 py-2 text-xs" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
                                    <option value="">상태 선택</option>
                                    <option value="TRIAL">{formatBillingStatusLabel('TRIAL', 'subscription')}</option>
                                    <option value="ACTIVE">{formatBillingStatusLabel('ACTIVE', 'subscription')}</option>
                                    <option value="SUSPENDED">{formatBillingStatusLabel('SUSPENDED', 'subscription')}</option>
                                    <option value="EXPIRED">{formatBillingStatusLabel('EXPIRED', 'subscription')}</option>
                                </select>
                                <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || !bulkStatus || bulkActionLoading} onClick={openBulkStatusModal}>
                                    변경 확인
                                </button>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 rounded border bg-white p-3">
                                <span className="text-xs font-medium text-slate-600">선택 플랜 변경</span>
                                <select className="rounded border px-3 py-2 text-xs" value={bulkPlanCode} onChange={(e) => setBulkPlanCode(e.target.value)}>
                                    <option value="">플랜 선택</option>
                                    {plans.map((plan) => (
                                        <option key={plan.planCode} value={plan.planCode}>{plan.name}</option>
                                    ))}
                                </select>
                                <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50" disabled={selectedSubscriptionIds.length === 0 || !bulkPlanCode || bulkActionLoading} onClick={openBulkPlanModal}>
                                    변경 확인
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {subscriptionListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={pagedSubscriptions.filter((item) => item.billingReady !== false).length > 0 && pagedSubscriptions.filter((item) => item.billingReady !== false).every((item) => selectedSubscriptionIds.includes(item.id))}
                                            onChange={toggleAllSubscriptionSelection}
                                        />
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'orgName')} onClick={() => changeSubscriptionSort('orgName')}>
                                            조직 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'orgName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'targetName')} onClick={() => changeSubscriptionSort('targetName')}>
                                            이름 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'targetName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">구분</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'planName')} onClick={() => changeSubscriptionSort('planName')}>
                                            플랜 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'planName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'billingMode')} onClick={() => changeSubscriptionSort('billingMode')}>
                                            과금모델 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'billingMode')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'firstPaidStartedAt')} onClick={() => changeSubscriptionSort('firstPaidStartedAt')}>
                                            시작일 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'firstPaidStartedAt')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'individualEndsAt')} onClick={() => changeSubscriptionSort('individualEndsAt')}>
                                            만료일 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'individualEndsAt')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className={sortButtonClass(subscriptionSortKey, 'status')} onClick={() => changeSubscriptionSort('status')}>
                                            상태 {sortMark(subscriptionSortKey, subscriptionSortDirection, 'status')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">상세</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedSubscriptions.map((item) => (
                                    <tr key={item.id} className="border-t">
                                        <td className="px-4 py-3">
                                            <input type="checkbox" checked={selectedSubscriptionIds.includes(item.id)} disabled={item.billingReady === false} onChange={() => toggleSubscriptionSelection(item.id)} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{item.orgName || '미지정'}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{item.targetName}</div>
                                            <div className="text-xs text-slate-500">{item.userEmail || '-'}</div>
                                            {item.billingReady === false && (
                                                <div className="text-xs text-amber-600">구독 미생성</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">{formatSubscriptionKindLabel(item.subscriptionKind)}</td>
                                        <td className="px-4 py-3">{formatSubscriptionHubPlanCode(item)}</td>
                                        <td className="px-4 py-3">{formatSubscriptionBillingModeLabel(item)}</td>
                                        <td className="px-4 py-3">{formatDate(item.firstPaidStartedAt) || '-'}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">
                                            {item.individualEndsAt ? (
                                                <>
                                                    <div className="text-sm text-slate-900">{formatDate(item.individualEndsAt)}</div>
                                                    <div className="mt-0.5 text-slate-500">{formatExpiryRemaining(item.individualEndsAt)}</div>
                                                </>
                                            ) : (
                                                <span className="text-slate-400">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge value={item.status} type="subscription" /></td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                disabled={quotaDetailLoading || item.billingReady === false}
                                                onClick={() => openSubscriptionDetail(item)}
                                            >
                                                상세
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {subscriptions.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={10}>조건에 맞는 회원 구독이 없습니다.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <TablePager
                        totalCount={subscriptionTotalCount}
                        page={subscriptionPage}
                        pageSize={subscriptionPageSize}
                        totalPages={subscriptionTotalPages}
                        onPrev={() => setSubscriptionPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setSubscriptionPage((prev) => Math.min(subscriptionTotalPages, prev + 1))}
                    />
                </div>
            )}

            {activeMenu === 'orders' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <h2 className="text-lg font-semibold">주문/결제</h2>
                    {orderLoadError && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{orderLoadError}</div>
                    )}
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7">
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="주문번호/구매자/조직/상품/PG 검색"
                            value={orderSearch}
                            onChange={(e) => setOrderSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyOrderFilters)}
                        />
                        <select aria-label="상품" className="rounded border px-3 py-2 text-sm" value={orderProductFilter} onChange={(e) => setOrderProductFilter(e.target.value)}>
                            <option value="ALL">전체 상품</option>
                            {orderProductOptions.map((productName) => (
                                <option key={productName} value={productName}>{productName}</option>
                            ))}
                        </select>
                        <select aria-label="상태" className="rounded border px-3 py-2 text-sm" value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
                            <option value="ALL">전체 상태</option>
                            <option value="PENDING">{formatBillingStatusLabel('PENDING', 'order')}</option>
                            <option value="PAYMENT_PENDING">{formatBillingStatusLabel('PAYMENT_PENDING', 'order')}</option>
                            <option value="PAID">{formatBillingStatusLabel('PAID', 'order')}</option>
                            <option value="FAILED">{formatBillingStatusLabel('FAILED', 'order')}</option>
                            <option value="CANCELED">{formatBillingStatusLabel('CANCELED', 'order')}</option>
                            <option value="REFUNDED">{formatBillingStatusLabel('REFUNDED', 'order')}</option>
                        </select>
                        <select aria-label="PG" className="rounded border px-3 py-2 text-sm" value={orderPgProviderFilter} onChange={(e) => setOrderPgProviderFilter(e.target.value)}>
                            <option value="ALL">전체 PG</option>
                            {orderPgProviderOptions.map((provider) => (
                                <option key={provider} value={provider}>{orderPgLabel({ pgProvider: provider })}</option>
                            ))}
                        </select>
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={orderDateFrom} onChange={(e) => setOrderDateFrom(e.target.value)} />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={orderDateTo} onChange={(e) => setOrderDateTo(e.target.value)} />
                        <div className="flex flex-wrap gap-2">
                            <select className="rounded border px-3 py-2 text-sm" value={orderPageSize} onChange={(e) => setOrderPageSize(Number(e.target.value) || 10)}>
                                <option value={10}>10개씩</option>
                                <option value={20}>20개씩</option>
                                <option value={50}>50개씩</option>
                                <option value={100}>100개씩</option>
                            </select>
                            <button className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={applyOrderFilters}>검색</button>
                            <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={resetOrderFilters}>필터 초기화</button>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                        <div>검색 결과 <span className="font-semibold text-slate-900">{orderTotalCount}</span>건</div>
                        <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={downloadOrdersCsv}>CSV 다운로드</button>
                    </div>
                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {orderListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                                    <th className="px-4 py-3 text-left whitespace-nowrap">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('paidAt')}>
                                            결제일시 {sortMark(orderSortKey, orderSortDirection, 'paidAt')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">주문번호</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('buyerName')}>
                                            구매자 {sortMark(orderSortKey, orderSortDirection, 'buyerName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('productName')}>
                                            상품 {sortMark(orderSortKey, orderSortDirection, 'productName')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('amount')}>
                                            금액 {sortMark(orderSortKey, orderSortDirection, 'amount')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeOrderSort('status')}>
                                            상태 {sortMark(orderSortKey, orderSortDirection, 'status')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">PG</th>
                                    <th className="px-4 py-3 text-left">상세</th>
                </tr>
            </thead>
            <tbody>
                                {pagedOrders.map((order) => (
                                    <tr key={order.id} className="border-t">
                                        <td className="px-4 py-3 whitespace-nowrap">{formatDateTimeMinute(order.paidAt || order.createdAt)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs">{order.orderNo || order.id}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{order.buyerName || '-'}</div>
                                            <div className="text-xs text-slate-500">{order.orgName || order.targetType || '-'}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="max-w-[13rem] truncate" title={orderProductLabel(order)}>{orderProductLabel(order)}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">{currency(order.amount)}</td>
                                        <td className="px-4 py-3"><StatusBadge value={order.status} type="order" /></td>
                                        <td className="px-4 py-3">
                                            <span className="block max-w-[9rem] truncate text-xs text-slate-700" title={orderPgLabel(order)}>{orderPgLabel(order)}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1 whitespace-nowrap">
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => openOrderDetail(order.id)}>상세</button>
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => reconcileOrder(order.id)}>재검증</button>
                                            </div>
                                        </td>
                    </tr>
                ))}
                                {orders.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={8}>주문 데이터가 없습니다.</td>
                                    </tr>
                                )}
            </tbody>
        </table>
                    </div>
                    <TablePager
                        totalCount={orderTotalCount}
                        page={orderPage}
                        pageSize={orderPageSize}
                        totalPages={orderTotalPages}
                        onPrev={() => setOrderPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setOrderPage((prev) => Math.min(orderTotalPages, prev + 1))}
                    />
                </div>
            )}

            {activeMenu === 'refunds' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <h2 className="text-lg font-semibold">환불/취소</h2>
                    {refundLoadError && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{refundLoadError}</div>
                    )}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="환불번호/주문번호/요청자 검색"
                            value={refundSearch}
                            onChange={(e) => setRefundSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyRefundFilters)}
                        />
                        <select className="rounded border px-3 py-2 text-sm" value={refundStatusFilter} onChange={(e) => setRefundStatusFilter(e.target.value)}>
                            <option value="ALL">전체 상태</option>
                            <option value="REQUESTED">{formatBillingStatusLabel('REQUESTED', 'refund')}</option>
                            <option value="APPROVED">{formatBillingStatusLabel('APPROVED', 'refund')}</option>
                            <option value="REJECTED">{formatBillingStatusLabel('REJECTED', 'refund')}</option>
                            <option value="COMPLETED">{formatBillingStatusLabel('COMPLETED', 'refund')}</option>
                        </select>
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={refundDateFrom} onChange={(e) => setRefundDateFrom(e.target.value)} />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={refundDateTo} onChange={(e) => setRefundDateTo(e.target.value)} />
                        <div className="flex gap-2">
                            <select className="rounded border px-3 py-2 text-sm" value={refundPageSize} onChange={(e) => setRefundPageSize(Number(e.target.value) || 10)}>
                                <option value={10}>10개씩</option>
                                <option value={20}>20개씩</option>
                                <option value={50}>50개씩</option>
                                <option value={100}>100개씩</option>
                            </select>
                            <button className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={applyRefundFilters}>검색</button>
                            <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={resetRefundFilters}>필터 초기화</button>
                        </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                        <div>검색 결과 <span className="font-semibold text-slate-900">{refundTotalCount}</span>건</div>
                        <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={downloadRefundsCsv}>CSV 다운로드</button>
                    </div>
                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {refundListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('refundNo')}>
                                            환불번호 {sortMark(refundSortKey, refundSortDirection, 'refundNo')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">주문번호</th>
                                    <th className="px-4 py-3 text-left">결제ID</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('requestedBy')}>
                                            요청자 {sortMark(refundSortKey, refundSortDirection, 'requestedBy')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('amount')}>
                                            금액 {sortMark(refundSortKey, refundSortDirection, 'amount')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('status')}>
                                            상태 {sortMark(refundSortKey, refundSortDirection, 'status')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">사유</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeRefundSort('createdAt')}>
                                            생성시각 {sortMark(refundSortKey, refundSortDirection, 'createdAt')}
                                        </button>
                                    </th>
                    <th className="px-4 py-3 text-left">액션</th>
                </tr>
            </thead>
            <tbody>
                                {pagedRefunds.map((refund) => (
                                    <tr key={refund.id} className="border-t">
                                        <td className="px-4 py-3">{refund.refundNo || refund.id}</td>
                                        <td className="px-4 py-3">{refund.orderNo || refund.orderId || '-'}</td>
                                        <td className="px-4 py-3">{refund.paymentId || '-'}</td>
                                        <td className="px-4 py-3">{refund.requestedBy || '-'}</td>
                                        <td className="px-4 py-3">{currency(refund.amount)}</td>
                                        <td className="px-4 py-3"><StatusBadge value={refund.status} type="refund" /></td>
                                        <td className="px-4 py-3">{refund.reason || '-'}</td>
                                        <td className="px-4 py-3">{formatDateTimeMinute(refund.requestedAt || refund.updatedAt)}</td>
                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-2">
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => updateRefund(refund.id, 'approve')}>승인</button>
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => updateRefund(refund.id, 'reject')}>반려</button>
                                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => updateRefund(refund.id, 'complete')}>완료</button>
                                            </div>
                        </td>
                    </tr>
                ))}
                                {refunds.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={9}>환불 데이터가 없습니다.</td>
                                    </tr>
                                )}
            </tbody>
        </table>
            </div>
                    <TablePager
                        totalCount={refundTotalCount}
                        page={refundPage}
                        pageSize={refundPageSize}
                        totalPages={refundTotalPages}
                        onPrev={() => setRefundPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setRefundPage((prev) => Math.min(refundTotalPages, prev + 1))}
                    />
        </div>
            )}

            {activeMenu === 'serviceUsage' && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-lg font-semibold">서비스 사용원장</h2>
                        <select className="rounded border px-3 py-2 text-sm" value={serviceUsagePageSize} onChange={(e) => setServiceUsagePageSize(Number(e.target.value) || 10)}>
                            {SUBSCRIPTION_PAGE_SIZES.map((n) => <option key={n} value={n}>{n}개씩</option>)}
                        </select>
                    </div>
                    {serviceUsageLoadError && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{serviceUsageLoadError}</div>
                    )}
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="사용자/기능/경로 검색"
                            value={serviceUsageSearch}
                            onChange={(e) => setServiceUsageSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyServiceUsageFilters)}
                        />
                        <select className="rounded border px-3 py-2 text-sm" value={serviceUsageRefundBlockingFilter} onChange={(e) => setServiceUsageRefundBlockingFilter(e.target.value)}>
                            <option value="ALL">전체 환불판단</option>
                            <option value="TRUE">환불 차단</option>
                            <option value="FALSE">차단 아님</option>
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={serviceUsageCategoryFilter} onChange={(e) => setServiceUsageCategoryFilter(e.target.value)}>
                            <option value="ALL">전체 분류</option>
                            <option value="API_MUTATION">일반 기능 사용</option>
                            <option value="PAID_QUOTA">쿼터 기능 사용</option>
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={serviceUsagePaidAfterFilter} onChange={(e) => setServiceUsagePaidAfterFilter(e.target.value)}>
                            <option value="ALL">전체 기간 기준</option>
                            <option value="TRUE">구독 시작 이후</option>
                        </select>
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={serviceUsageDateFrom} onChange={(e) => setServiceUsageDateFrom(e.target.value)} />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={serviceUsageDateTo} onChange={(e) => setServiceUsageDateTo(e.target.value)} />
                        <div className="flex gap-2">
                            <button className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={applyServiceUsageFilters}>검색</button>
                            <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={resetServiceUsageFilters}>초기화</button>
                        </div>
                    </div>
                    {serviceUsageListLoading ? (
                        <div className="mt-6 text-sm text-slate-500">로딩 중...</div>
                    ) : (
                        <div className="mt-4 overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-4 py-3 text-left">발생일시</th>
                                        <th className="px-4 py-3 text-left">사용자</th>
                                        <th className="px-4 py-3 text-left">구분</th>
                                        <th className="px-4 py-3 text-left">환불판단</th>
                                        <th className="px-4 py-3 text-left">분류</th>
                                        <th className="px-4 py-3 text-left">기능</th>
                                        <th className="px-4 py-3 text-left">수량</th>
                                        <th className="px-4 py-3 text-left">요청</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedServiceUsageEvents.map((event) => (
                                        <tr key={event.id} className="border-t">
                                            <td className="px-4 py-3">{formatDateTimeMinute(event.occurredAt)}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{event.ownerName || '-'}</div>
                                                <div className="text-xs text-slate-500">{event.ownerEmail || event.orgName || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3">{event.usageTypeLabel || event.sourceLabel || '서비스 사용'}</td>
                                            <td className="px-4 py-3">{event.refundBlockingLabel || (event.refundBlocking ? '환불 차단' : '차단 아님')}</td>
                                            <td className="px-4 py-3">{event.usageCategoryLabel || event.usageCategory || '-'}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{event.featureName || '-'}</div>
                                                <div className="text-xs text-slate-500">{event.refType || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3">{event.units ?? 1}</td>
                                            <td className="px-4 py-3">
                                                <div>{event.requestMethod || '-'}</div>
                                                <div className="max-w-xs truncate text-xs text-slate-500" title={event.requestPath || ''}>{event.requestPath || event.refId || '-'}</div>
                                            </td>
                                        </tr>
                                    ))}
                                    {pagedServiceUsageEvents.length === 0 && (
                                        <tr>
                                            <td className="px-4 py-8 text-center text-slate-500" colSpan={8}>서비스 사용원장이 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <TablePager
                        totalCount={serviceUsageTotalCount}
                        page={serviceUsagePage}
                        pageSize={serviceUsagePageSize}
                        totalPages={serviceUsageTotalPages}
                        onPrev={() => setServiceUsagePage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setServiceUsagePage((prev) => Math.min(serviceUsageTotalPages, prev + 1))}
                    />
                </div>
            )}
            {(activeMenu === 'audit' || activeMenu === 'billingLogs') && !loading && (
                <div className="mt-6 rounded border bg-white p-5">
                    <h2 className="text-lg font-semibold">{activeMenu === 'billingLogs' ? '과금로그' : '이력관리'}</h2>
                    <p className="mt-1 text-sm text-slate-500">결제, 환불, 웹훅, 구독 활성화 감사로그를 상태와 PG, 주문/결제 식별자로 세부 필터링합니다.</p>
                    {auditLoadError && (
                        <div className="mt-3 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{auditLoadError}</div>
                    )}
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="행위자/액션/대상/사유/IP 검색"
                            value={auditSearch}
                            onChange={(e) => setAuditSearch(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <select className="rounded border px-3 py-2 text-sm" value={auditActionFilter} onChange={(e) => setAuditActionFilter(e.target.value)}>
                            <option value="ALL">전체 액션</option>
                            {auditActionOptions.map((action) => (
                                <option key={action} value={action}>{formatBillingAuditActionLabel(action)}</option>
                            ))}
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={auditTargetTypeFilter} onChange={(e) => setAuditTargetTypeFilter(e.target.value)}>
                            <option value="ALL">전체 대상</option>
                            {auditTargetTypeOptions.map((targetType) => (
                                <option key={targetType} value={targetType}>{formatBillingAuditTargetLabel(targetType)}</option>
                            ))}
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={auditStatusFilter} onChange={(e) => setAuditStatusFilter(e.target.value)}>
                            <option value="ALL">전체 상태</option>
                            {auditStatusOptions.map((status) => (
                                <option key={status} value={status}>{formatBillingStatusLabel(status)}</option>
                            ))}
                        </select>
                        <select className="rounded border px-3 py-2 text-sm" value={auditProviderFilter} onChange={(e) => setAuditProviderFilter(e.target.value)}>
                            <option value="ALL">전체 PG</option>
                            {auditProviderOptions.map((provider) => (
                                <option key={provider} value={provider}>{provider}</option>
                            ))}
                        </select>
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="주문번호"
                            value={auditOrderNoFilter}
                            onChange={(e) => setAuditOrderNoFilter(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="결제ID"
                            value={auditPaymentIdFilter}
                            onChange={(e) => setAuditPaymentIdFilter(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <input
                            className="rounded border px-3 py-2 text-sm"
                            placeholder="웹훅 이벤트ID"
                            value={auditWebhookEventIdFilter}
                            onChange={(e) => setAuditWebhookEventIdFilter(e.target.value)}
                            onKeyDown={(e) => handleEnterToApply(e, applyAuditFilters)}
                        />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={auditDateFrom} onChange={(e) => setAuditDateFrom(e.target.value)} />
                        <input type="date" className="rounded border px-3 py-2 text-sm" value={auditDateTo} onChange={(e) => setAuditDateTo(e.target.value)} />
                        <div className="flex gap-2">
                            <select className="rounded border px-3 py-2 text-sm" value={auditPageSize} onChange={(e) => setAuditPageSize(Number(e.target.value) || 10)}>
                                <option value={10}>10개씩</option>
                                <option value={20}>20개씩</option>
                                <option value={50}>50개씩</option>
                                <option value={100}>100개씩</option>
                        </select>
                            <button className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50" onClick={applyAuditFilters}>검색</button>
                            <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={resetAuditFilters}>필터 초기화</button>
                    </div>
                </div>
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
                        <div>검색 결과 <span className="font-semibold text-slate-900">{auditTotalCount}</span>건</div>
                        <div className="flex flex-wrap gap-2">
                            {activeMenu === 'billingLogs' && (
                                <>
                                    <button type="button" className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={syncIapSubscriptions} disabled={iapSyncLoading}>
                                        {iapSyncLoading ? '동기화 중...' : 'IAP 구독 재조회'}
                                    </button>
                                    <button type="button" className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={loadIapNotificationEvents} disabled={iapNotificationLoading}>
                                        {iapNotificationLoading ? '조회 중...' : '스토어 알림 원문'}
                                    </button>
                                    <button type="button" className="rounded border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60" onClick={openStoreAccountConflictsModal} disabled={storeConflictLoading}>
                                        {storeConflictLoading ? '조회 중...' : '스토어 계정 충돌'}
                                    </button>
                                </>
                            )}
                            <button type="button" className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={downloadAuditLogsCsv}>CSV 다운로드</button>
                        </div>
                    </div>
                    <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                        {auditListLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                            </div>
                        )}
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('actorEmail')}>
                                            행위자 {sortMark(auditSortKey, auditSortDirection, 'actorEmail')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('action')}>
                                            액션 {sortMark(auditSortKey, auditSortDirection, 'action')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('target')}>
                                            대상 {sortMark(auditSortKey, auditSortDirection, 'target')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">상태</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('provider')}>
                                            PG {sortMark(auditSortKey, auditSortDirection, 'provider')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('orderNo')}>
                                            주문번호 {sortMark(auditSortKey, auditSortDirection, 'orderNo')}
                                        </button>
                                    </th>
                                    <th className="px-4 py-3 text-left">사유</th>
                                    <th className="px-4 py-3 text-left">
                                        <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => changeAuditSort('createdAt')}>
                                            시각 {sortMark(auditSortKey, auditSortDirection, 'createdAt')}
                                        </button>
                                    </th>
                        </tr>
                    </thead>
                    <tbody>
                                {pagedAuditLogs.map((log, index) => (
                                    <tr
                                        key={`${log.id || log.createdAt || 'audit'}-${index}`}
                                        className="cursor-pointer border-t hover:bg-slate-50/90"
                                        onClick={() => setSelectedAuditLog(log)}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900">{log.actorName || '-'}</div>
                                            <div className="text-xs text-slate-500 break-all">{log.actorEmail || '-'}</div>
                                        </td>
                                        <td className="px-4 py-3">{formatBillingAuditActionLabel(log.action)}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900">{log.targetDisplayName || '-'}</div>
                                            <div className="text-xs text-slate-500">
                                                {`${formatBillingAuditTargetLabel(log.targetType)}${log.targetId != null && log.targetId !== '' ? ` #${log.targetId}` : ''}`}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {pickAuditStatus(log) ? (
                                                <span className={`rounded-full px-2 py-1 text-xs ${billingStatusToneClass(pickAuditStatus(log))}`}>
                                                    {formatBillingStatusLabel(pickAuditStatus(log))}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3">{log.provider || '-'}</td>
                                        <td className="px-4 py-3">
                                            <div>{log.orderNo || '-'}</div>
                                            <div className="text-xs text-slate-500">{log.paymentId ? `payment#${log.paymentId}` : log.webhookEventId || ''}</div>
                                        </td>
                                        <td className="max-w-xs truncate px-4 py-3" title={log.reason || ''}>{log.reason || '-'}</td>
                                        <td className="px-4 py-3">{formatDateTimeMinute(log.createdAt)}</td>
                            </tr>
                        ))}
                                {auditLogs.length === 0 && (
                                    <tr>
                                        <td className="px-4 py-8 text-center text-slate-500" colSpan={8}>감사 이력이 없습니다.</td>
                                    </tr>
                                )}
                    </tbody>
                </table>
                    </div>
                    <TablePager
                        totalCount={auditTotalCount}
                        page={auditPage}
                        pageSize={auditPageSize}
                        totalPages={auditTotalPages}
                        onPrev={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                        onNext={() => setAuditPage((prev) => Math.min(auditTotalPages, prev + 1))}
                    />
                </div>
            )}

            {!loading && !error && activeMenu === 'coupons' && (
                <div className="mt-6 space-y-6">
                    <div className="rounded border bg-white p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h2 className="text-lg font-semibold">{editingPromotionId ? '프로모션 수정' : '프로모션 생성'}</h2>
                                <p className="mt-1 text-sm text-slate-500">쿠폰, 추천인, 이벤트성 reward를 공통 구조로 등록합니다.</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                    disabled={couponReservationExpiryLoading}
                                    onClick={runCouponReservationExpiry}
                                >
                                    쿠폰 예약 만료 실행
                                </button>
                                {editingPromotionId && (
                                    <button
                                        className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                                        onClick={resetPromotionForm}
                                    >
                                        새로 작성
                                    </button>
                                )}
                                <button
                                    className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50"
                                    disabled={promotionSaving}
                                    onClick={submitPromotion}
                                >
                                    프로모션 저장
                                </button>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 text-sm">
                            <FormField label="프로모션 유형">
                                <select className="w-full rounded border px-3 py-2" value={promotionForm.promotionType} onChange={(e) => updatePromotionForm('promotionType', e.target.value)}>
                                    <option value="COUPON">쿠폰</option>
                                    <option value="REFERRAL">추천인</option>
                                    <option value="CAMPAIGN">캠페인</option>
                                    <option value="OPERATOR_GRANT">운영자 지급</option>
                        </select>
                            </FormField>
                            <FormField label="트리거">
                                <select className="w-full rounded border px-3 py-2" value={promotionForm.triggerType} onChange={(e) => updatePromotionForm('triggerType', e.target.value)}>
                                    <option value="CODE_REDEEM">코드 사용</option>
                                    <option value="SIGNUP_APPROVED">가입 승인</option>
                                    <option value="FIRST_PAID_ORDER">첫 결제 완료</option>
                                    <option value="MANUAL">수동</option>
                                </select>
                            </FormField>
                            <FormField label="코드">
                                <input className="w-full rounded border px-3 py-2" value={promotionForm.code} onChange={(e) => updatePromotionForm('code', e.target.value.toUpperCase())} placeholder="WELCOME2026" />
                            </FormField>
                            <FormField label="상태">
                                <select className="w-full rounded border px-3 py-2" value={promotionForm.status} onChange={(e) => updatePromotionForm('status', e.target.value)}>
                                    <option value="ACTIVE">ACTIVE</option>
                                    <option value="INACTIVE">INACTIVE</option>
                                </select>
                            </FormField>
                            <FormField label="이름">
                                <input className="w-full rounded border px-3 py-2" value={promotionForm.name} onChange={(e) => updatePromotionForm('name', e.target.value)} placeholder="가입 축하 쿠폰" />
                            </FormField>
                            <FormField label="사용자당 한도">
                                <input className="w-full rounded border px-3 py-2" type="number" min="1" value={promotionForm.perUserLimit} onChange={(e) => updatePromotionForm('perUserLimit', e.target.value)} placeholder="1" />
                            </FormField>
                            <FormField label="전체 한도">
                                <input className="w-full rounded border px-3 py-2" type="number" min="1" value={promotionForm.totalLimit} onChange={(e) => updatePromotionForm('totalLimit', e.target.value)} placeholder="1000" />
                            </FormField>
                            <FormField label="설명">
                                <input className="w-full rounded border px-3 py-2" value={promotionForm.description} onChange={(e) => updatePromotionForm('description', e.target.value)} placeholder="대상/용도 메모" />
                            </FormField>
                    </div>
                        <div className="mt-5">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-medium">Rewards</div>
                                <button className="rounded border px-3 py-2 text-xs hover:bg-slate-50" onClick={addPromotionReward}>reward 추가</button>
                            </div>
                            <div className="mt-3 space-y-3">
                                {promotionForm.rewards.map((reward, index) => (
                                    <div key={`reward-${index}`} className="grid grid-cols-1 gap-3 rounded border bg-slate-50 p-4 md:grid-cols-5">
                                        <select className="rounded border px-3 py-2 text-sm" value={reward.rewardType} onChange={(e) => updatePromotionReward(index, 'rewardType', e.target.value)}>
                                            <option value="POINT_GRANT">{formatPromotionRewardTypeLabel('POINT_GRANT')}</option>
                                            <option value="TRIAL_START">{formatPromotionRewardTypeLabel('TRIAL_START')}</option>
                                            <option value="TRIAL_DAYS_EXTEND">{formatPromotionRewardTypeLabel('TRIAL_DAYS_EXTEND')}</option>
                                            <option value="SUBSCRIPTION_DAYS_EXTEND">{formatPromotionRewardTypeLabel('SUBSCRIPTION_DAYS_EXTEND')}</option>
                                            <option value="AMOUNT_DISCOUNT">{formatPromotionRewardTypeLabel('AMOUNT_DISCOUNT')}</option>
                                            <option value="PERCENT_DISCOUNT">{formatPromotionRewardTypeLabel('PERCENT_DISCOUNT')}</option>
                                        </select>
                                        <select className="rounded border px-3 py-2 text-sm" value={reward.targetRole} onChange={(e) => updatePromotionReward(index, 'targetRole', e.target.value)}>
                                            <option value="REDEEMER">{formatPromotionRewardTargetRoleLabel('REDEEMER')}</option>
                                            <option value="REFERRER">{formatPromotionRewardTargetRoleLabel('REFERRER')}</option>
                                            <option value="REFEREE">{formatPromotionRewardTargetRoleLabel('REFEREE')}</option>
                                        </select>
                                        <input className="rounded border px-3 py-2 text-sm" type="number" min="0" value={reward.value} onChange={(e) => updatePromotionReward(index, 'value', e.target.value)} placeholder="값" />
                                        <input className="rounded border px-3 py-2 text-sm" type="number" min="0" value={reward.expireDays} onChange={(e) => updatePromotionReward(index, 'expireDays', e.target.value)} placeholder="포인트 만료일" disabled={reward.rewardType !== 'POINT_GRANT'} />
                                        <button className="rounded border px-3 py-2 text-xs hover:bg-white disabled:opacity-50" disabled={promotionForm.rewards.length <= 1} onClick={() => removePromotionReward(index)}>삭제</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <AdminTable
                        title="등록된 프로모션"
                        columns={['코드', '이름', '유형/트리거', '상태', 'Reward', '한도', '사용현황', '생성일', '액션']}
                        rows={promotions.map((promotion) => [
                            promotion.code || '-',
                            promotion.name || '-',
                            <div key={`promotion-type-${promotion.id}`} className="text-xs text-slate-600">
                                <div>{promotion.promotionType}</div>
                                <div>{promotion.triggerType}</div>
                            </div>,
                            <StatusBadge key={`promotion-status-${promotion.id}`} value={promotion.status} type="default" />,
                            <div key={`promotion-reward-${promotion.id}`} className="space-y-1 text-xs text-slate-600">
                                {(promotion.rewards || []).map((reward) => (
                                    <div key={reward.id || `${promotion.id}-${reward.rewardType}`}>
                                        {formatRewardLabel(reward)}
                                    </div>
                                ))}
                            </div>,
                            `${promotion.perUserLimit || '-'} / ${promotion.totalLimit || '-'}`,
                            `${promotion.usedCount || 0}${promotion.remainingCount !== null && promotion.remainingCount !== undefined ? ` / 남음 ${promotion.remainingCount}` : ''}`,
                            formatDateTimeMinute(promotion.createdAt),
                            <div key={`promotion-actions-${promotion.id}`} className="flex flex-wrap gap-2">
                                <button className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={() => loadPromotionToForm(promotion)}>
                                    수정
                                </button>
                                <button
                                    className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                                    onClick={() => togglePromotionStatus(promotion, promotion.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                                >
                                    {promotion.status === 'ACTIVE' ? '비활성화' : '재활성화'}
                                </button>
                            </div>,
                        ])}
                        emptyText="등록된 프로모션이 없습니다."
                    />
                </div>
            )}
            {!loading && !error && activeMenu === 'points' && (
                <div className="mt-6 space-y-4">
                    <PointCommerceAdminPanel />
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="grid min-w-0 flex-1 grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                            <InfoBlock label="지갑 수" value={String(pointOverview?.walletCount ?? 0)} />
                            <InfoBlock label="사용자 지갑" value={String(pointOverview?.userWalletCount ?? 0)} />
                            <InfoBlock label="전체 잔액" value={`${Number(pointOverview?.totalBalance || 0).toLocaleString()}P`} />
                            <InfoBlock label="유료 포인트" value={`${Number(pointOverview?.paidBalance || 0).toLocaleString()}P`} />
                            <InfoBlock label="보너스 포인트" value={`${Number(pointOverview?.bonusBalance || 0).toLocaleString()}P`} />
                            <InfoBlock label="30일 내 만료" value={`${Number(pointOverview?.expiringSoon || 0).toLocaleString()}P`} />
                        </div>
                        <div className="flex shrink-0 gap-2">
                            <button
                                className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                disabled={pointReconcileLoading}
                                onClick={runPointReconciliation}
                            >
                                원장 정합성 점검
                            </button>
                            <button
                                className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                disabled={pointExpiryLoading}
                                onClick={runPointExpiry}
                            >
                                포인트 만료 실행
                            </button>
                        </div>
                    </div>
                    {pointReconcileResult && (
                        <div className={`rounded border px-3 py-2 text-sm ${Number(pointReconcileResult?.mismatchCount || 0) > 0 ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
                            점검 지갑 {Number(pointReconcileResult?.checkedWalletCount || 0).toLocaleString()}개 · 불일치 {Number(pointReconcileResult?.mismatchCount || 0).toLocaleString()}건
                        </div>
                    )}
                    <AdminTable
                        title="상위 포인트 보유자"
                        columns={['이름', '이메일', '추천코드', '전체', '유료', '보너스', '상태', '수정일', '관리']}
                        rows={(pointOverview?.wallets || []).map((wallet) => [
                            wallet.ownerName || '-',
                            wallet.ownerEmail || '-',
                            wallet.referralCode || '-',
                            `${Number(wallet.balance || 0).toLocaleString()}P`,
                            `${Number(wallet.paidBalance || 0).toLocaleString()}P`,
                            `${Number(wallet.bonusBalance || 0).toLocaleString()}P`,
                            wallet.status || '-',
                            formatDateTimeMinute(wallet.updatedAt),
                            wallet.ownerType === 'USER' && wallet.ownerId ? (
                                <button
                                    key={`point-adjust-${wallet.walletId}`}
                                    className="rounded border px-2 py-1 text-xs font-medium hover:bg-slate-50"
                                    onClick={() => openPointAdjustment(wallet)}
                                >
                                    지급·회수
                                </button>
                            ) : '-',
                        ])}
                        emptyText="포인트 지갑이 없습니다."
                    />
                    <AdminTable
                        title="최근 포인트 거래"
                        columns={['사용자', '유형', '참조', '증감', '유료', '보너스', '잔액', '메모', '일시']}
                        rows={(pointOverview?.recentTransactions || []).map((tx) => [
                            tx.ownerName || tx.ownerEmail || '-',
                            tx.txType || '-',
                            `${tx.refType || '-'} / ${tx.refId || '-'}`,
                            `${Number(tx.amount || 0).toLocaleString()}P`,
                            `${Number(tx.paidAmount || 0).toLocaleString()}P`,
                            `${Number(tx.bonusAmount || 0).toLocaleString()}P`,
                            `${Number(tx.balanceAfter || 0).toLocaleString()}P`,
                            tx.memo || '-',
                            formatDateTimeMinute(tx.createdAt),
                        ])}
                        emptyText="포인트 거래가 없습니다."
                    />
                </div>
            )}

            {pointAdjustmentTarget && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
                    onClick={() => closePointAdjustment()}
                >
                    <div
                        className="w-full max-w-md rounded-lg border bg-white p-5 shadow-xl"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="point-adjustment-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 id="point-adjustment-title" className="text-base font-semibold text-slate-900">포인트 지급·회수</h3>
                                <div className="mt-1 text-sm text-slate-600">
                                    {pointAdjustmentTarget.ownerName || pointAdjustmentTarget.ownerEmail || '사용자'} · {Number(pointAdjustmentTarget.balance || 0).toLocaleString()}P
                                </div>
                            </div>
                            <button
                                type="button"
                                className="rounded border px-2 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                                disabled={pointAdjustmentLoading}
                                onClick={() => closePointAdjustment()}
                            >
                                닫기
                            </button>
                        </div>
                        <div className="mt-5 grid grid-cols-2 rounded border p-1">
                            {[
                                { value: 'GRANT', label: '지급' },
                                { value: 'REVOKE', label: '회수' },
                            ].map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`rounded px-3 py-2 text-sm font-medium ${pointAdjustmentMode === option.value ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                    onClick={() => setPointAdjustmentMode(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">포인트</label>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    className="w-full rounded border px-3 py-2 text-sm"
                                    value={pointAdjustmentAmount}
                                    onChange={(event) => setPointAdjustmentAmount(event.target.value)}
                                />
                            </div>
                            {pointAdjustmentMode === 'GRANT' && (
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">만료일</label>
                                    <input
                                        type="datetime-local"
                                        className="w-full rounded border px-3 py-2 text-sm"
                                        value={pointAdjustmentExpireAt}
                                        onChange={(event) => setPointAdjustmentExpireAt(event.target.value)}
                                    />
                                </div>
                            )}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">사유</label>
                                <textarea
                                    rows="3"
                                    className="w-full rounded border px-3 py-2 text-sm"
                                    value={pointAdjustmentReason}
                                    onChange={(event) => setPointAdjustmentReason(event.target.value)}
                                />
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                disabled={pointAdjustmentLoading}
                                onClick={() => closePointAdjustment()}
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                className="rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                                disabled={pointAdjustmentLoading}
                                onClick={submitPointAdjustment}
                            >
                                {pointAdjustmentLoading ? '처리 중' : pointAdjustmentMode === 'GRANT' ? '지급' : '회수'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedAuditLog && (
                <BillingAuditLogDetailModal log={selectedAuditLog} onClose={() => setSelectedAuditLog(null)} />
            )}

            {grantTargets?.items?.length > 0 && (
                <div
                    className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
                    onClick={() => {
                        setGrantTargets(null)
                        setGrantMode('GRANT')
                        setGrantReason('')
                        setGrantRestoreStoreEntitlement(false)
                    }}
                >
                    <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">구독일 {grantMode === 'REVOKE' ? '회수' : '부여'}</h3>
                        <p className="mt-1 text-xs text-slate-500">선택한 회원 전원의 구독 종료일을 동일한 일수만큼 {grantMode === 'REVOKE' ? '앞당깁니다' : '연장합니다'}. 프리셋은 복수 선택 시 합산됩니다.</p>
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">대상 ({grantTargets.items.length}명)</label>
                                <div className="max-h-32 overflow-y-auto rounded border bg-slate-50 px-3 py-2 text-xs text-slate-800">
                                    {grantTargets.items.map((it) => (
                                        <div key={it.id} className="flex items-center justify-between gap-2">
                                            <span>{it.targetName || `ID ${it.id}`}</span>
                                            {isStoreEntitlementRestoreCandidate(it) && (
                                                <span className="shrink-0 rounded bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
                                                    서비스 권한 제외
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="mb-2 block font-medium text-slate-700">일수 프리셋 (복수 선택·합산)</label>
                                <div className="flex flex-wrap gap-3">
                                    {GRANT_DAY_PRESET_OPTIONS.map((d) => (
                                        <label key={d} className="inline-flex cursor-pointer items-center gap-2 text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={grantDayPresetSelection.has(d)}
                                                onChange={() => toggleGrantDayPreset(d)}
                                            />
                                            <span>{d}일</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">추가 일수 (선택)</label>
                                <input
                                    type="number"
                                    min={0}
                                    className="w-full rounded border px-3 py-2"
                                    placeholder="0"
                                    value={grantDaysExtra}
                                    onChange={(e) => setGrantDaysExtra(e.target.value)}
                                />
                            </div>
                            <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-slate-800">
                                합계 {grantMode === 'REVOKE' ? '회수' : '부여'} 일수: <span className="font-semibold text-brand-navy">{resolveGrantTotalDays()}</span>일
                            </div>
                            {grantStoreEntitlementTargets.length > 0 && (
                                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                    <div className="font-medium">서비스 권한 제외 상태의 인앱결제 구독이 포함되어 있습니다.</div>
                                    <div className="mt-1 text-xs">기간만 부여하면 사용자는 계속 미구독으로 처리될 수 있습니다.</div>
                                    <label className="mt-2 flex items-start gap-2 text-xs">
                                        <input
                                            type="checkbox"
                                            checked={grantRestoreStoreEntitlement}
                                            onChange={(e) => setGrantRestoreStoreEntitlement(e.target.checked)}
                                        />
                                        <span>기간 부여와 함께 인앱결제 서비스 권한을 복구합니다.</span>
                                    </label>
                                </div>
                            )}
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">사유</label>
                                <textarea className="w-full rounded border px-3 py-2" rows={3} value={grantReason} onChange={(e) => setGrantReason(e.target.value)} placeholder={grantMode === 'REVOKE' ? '운영자 기간 회수' : '운영자 수동 부여'} />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => { setGrantTargets(null); setGrantMode('GRANT'); setGrantReason(''); setGrantRestoreStoreEntitlement(false) }}>취소</button>
                            <button type="button" className={`rounded px-3 py-2 text-sm text-white ${grantMode === 'REVOKE' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-brand-navy'}`} onClick={grantSubscriptionDays}>{grantMode === 'REVOKE' ? '회수' : '부여'}</button>
                        </div>
                    </div>
                </div>
            )}

            {selectedOrder && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
                    <div className="w-full max-w-xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">주문 상세</h3>
                                <p className="mt-1 text-sm text-slate-500">{selectedOrder.orderNo || selectedOrder.id}</p>
                            </div>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setSelectedOrder(null)}>닫기</button>
                        </div>
                        {orderDetailLoading ? (
                            <div className="mt-6 text-sm text-slate-500">상세 로딩 중...</div>
                        ) : (
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <InfoBlock label="구매자" value={selectedOrder.buyerName || '-'} />
                                <InfoBlock label="조직" value={selectedOrder.orgName || '-'} />
                                <InfoBlock label="상품" value={orderProductLabel(selectedOrder)} />
                                <InfoBlock label="상태" valueNode={<StatusBadge value={selectedOrder.status} type="order" />} />
                                <InfoBlock label="쿠폰" value={formatCouponReservation(selectedOrder)} />
                                <InfoBlock label="결제수단" value={selectedOrder.paymentMethod || '-'} />
                                <InfoBlock label="PG" value={selectedOrder.pgProvider || '-'} />
                                {selectedOrder.gatewayProvider && (
                                    <InfoBlock label="결제 경로" value={gatewayProviderLabel(selectedOrder.gatewayProvider)} />
                                )}
                                {(selectedOrder.gatewayChannelCode || selectedOrder.gatewayPgProvider) && (
                                    <InfoBlock label="PG 채널" value={formatPortOneChannel({ channelCode: selectedOrder.gatewayChannelCode, pgProvider: selectedOrder.gatewayPgProvider })} />
                                )}
                                {selectedOrder.gatewayPaymentMode && (
                                    <InfoBlock label="결제 유형" value={gatewayPaymentModeLabel(selectedOrder.gatewayPaymentMode)} />
                                )}
                                <InfoBlock label="PG 거래번호" value={selectedOrder.pgTransactionId || '-'} />
                                <InfoBlock label="PG 주문번호" value={selectedOrder.pgOrderId || '-'} />
                                <InfoBlock label="금액" value={currency(selectedOrder.amount)} />
                                <InfoBlock label="결제일시" value={formatDateTimeMinute(selectedOrder.paidAt || selectedOrder.createdAt)} />
                                {selectedOrder.payment && (
                                    <div className="md:col-span-2 rounded border p-4">
                                        <div className="text-slate-500">결제 원장</div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                                            <InfoBlock compact label="Payment ID" value={selectedOrder.payment.id || '-'} />
                                            <InfoBlock compact label="상태" valueNode={<StatusBadge value={selectedOrder.payment.status} type="payment" />} />
                                            <InfoBlock compact label="금액" value={currency(selectedOrder.payment.amount)} />
                                            <InfoBlock compact label="PG" value={selectedOrder.payment.pgProvider || '-'} />
                                            <InfoBlock compact label="결제수단" value={selectedOrder.payment.pgMethod || '-'} />
                                            <InfoBlock compact label="결제시각" value={formatDateTimeMinute(selectedOrder.payment.paidAt)} />
                                        </div>
                                    </div>
                                )}
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">증빙 문서</div>
                                    {Array.isArray(selectedOrder.documents) && selectedOrder.documents.length > 0 ? (
                                        <div className="mt-3 overflow-x-auto">
                                            <table className="min-w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                                        <th className="px-3 py-2 text-left">문서번호</th>
                                                        <th className="px-3 py-2 text-left">종류</th>
                                                        <th className="px-3 py-2 text-left">상태</th>
                                                        <th className="px-3 py-2 text-left">링크</th>
                                                        <th className="px-3 py-2 text-left">발행일시</th>
                        </tr>
                    </thead>
                    <tbody>
                                                    {selectedOrder.documents.map((doc) => (
                                                        <tr key={doc.documentId || doc.id} className="border-t">
                                                            <td className="px-3 py-2">{doc.id || doc.documentId || '-'}</td>
                                                            <td className="px-3 py-2">{doc.documentType || '-'}</td>
                                                            <td className="px-3 py-2"><StatusBadge value={doc.status} type="document" /></td>
                                                            <td className="px-3 py-2">{renderExternalLink(doc.metadata?.receiptUrl, '보기')}</td>
                                                            <td className="px-3 py-2">{formatDateTimeMinute(doc.issuedAt || doc.requestedAt)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                                        </div>
                                    ) : (
                                        <div className="mt-2 text-sm text-slate-500">연결된 증빙이 없습니다.</div>
                                    )}
                    </div>
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">웹훅 이벤트</div>
                                    {Array.isArray(selectedOrder.webhookEvents) && selectedOrder.webhookEvents.length > 0 ? (
                                        <div className="mt-3 space-y-3">
                                            {selectedOrder.webhookEvents.map((event) => (
                                                <div key={event.id} className="rounded border bg-slate-50 p-3 text-xs">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="font-medium">{event.provider}</span>
                                                        <span className="text-slate-700">{event.eventType || '-'}</span>
                                                        <StatusBadge value={event.status} type="event" />
                                                        <span className="text-slate-500">{formatDateTimeMinute(event.receivedAt)}</span>
                </div>
                                                    <div className="mt-2 text-slate-600">{event.eventId}</div>
                                                    {event.errorMessage && (
                                                        <div className="mt-2 text-rose-700">처리 오류: {event.errorMessage}</div>
                                                    )}
                                                    <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-slate-700">
                                                        {prettyJson(event.parsedPayload)}
                                                    </pre>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="mt-2 text-sm text-slate-500">수신된 웹훅 이벤트가 없습니다.</div>
                                    )}
                                </div>
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">영수증</div>
                                    <div className="mt-2">{renderExternalLink(selectedOrder.receiptUrl, selectedOrder.receiptUrl || '미발행')}</div>
                                </div>
                                <div className="md:col-span-2 rounded border p-4">
                                    <div className="text-slate-500">메타데이터</div>
                                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700 whitespace-pre-wrap">
                                        {prettyJson(selectedOrder.metadata)}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {selectedQuotaSubscription && (
                <div
                    className="fixed inset-0 bg-black/30 flex items-center justify-center p-4"
                    onClick={() => {
                        setSelectedQuotaSubscription(null)
                        setQuotaLimitDraft({})
                    }}
                >
                    <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">구독 상세</h3>
                                <p className="mt-1 text-sm text-slate-500">{selectedQuotaSubscription.targetName} / {selectedQuotaSubscription.planName}</p>
                            </div>
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm"
                                onClick={() => {
                                    setSelectedQuotaSubscription(null)
                                    setQuotaLimitDraft({})
                                }}
                            >
                                닫기
                            </button>
                        </div>
                        <div className="mt-6 grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                            <InfoBlock label="구분" value={formatSubscriptionKindLabel(selectedQuotaSubscription.subscriptionKind)} />
                            <InfoBlock label="플랜" value={formatSubscriptionHubPlanCode(selectedQuotaSubscription)} />
                            <InfoBlock label="최초 유료 구독 시작일" value={formatDate(selectedQuotaSubscription.firstPaidStartedAt)} />
                            <InfoBlock label="개인 구독 만료일" value={formatDate(selectedQuotaSubscription.individualEndsAt)} />
                            <InfoBlock label="구독 상태" valueNode={<StatusBadge value={selectedQuotaSubscription.status} type="subscription" />} />
                            <InfoBlock label="현재 기간" value={`${formatDate(selectedQuotaSubscription.currentQuotaPeriod?.periodStartAt)} ~ ${formatDate(selectedQuotaSubscription.currentQuotaPeriod?.periodEndAt)}`} />
                        </div>

                        {isStoreIapSubscription(selectedQuotaSubscription) && (
                            <div className="mt-4 rounded border border-sky-200 bg-sky-50/40 p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-medium text-slate-900">인앱결제 관리</div>
                                        <div className={`mt-1 text-xs font-medium ${storeEntitlementToneClass(selectedQuotaSubscription)}`}>
                                            서비스 권한: {selectedQuotaSubscription.storeEntitlementStatusLabel || (isStoreEntitlementEnabled(selectedQuotaSubscription) ? '포함' : '제외')}
                                            {selectedQuotaSubscription.storeDuplicateActive && (
                                                <span className="ml-1 font-normal text-amber-700">활성 {selectedQuotaSubscription.storeActiveSubscriptionCount || 0}건</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                                            onClick={() => openSelectedStoreLinkModal('UNLINK', selectedQuotaSubscription)}
                                        >
                                            연결 해제
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded border border-sky-200 bg-white px-2 py-1 text-xs font-medium text-sky-900 hover:bg-sky-100"
                                            onClick={() => openSelectedStoreLinkModal('TRANSFER', selectedQuotaSubscription)}
                                        >
                                            이관
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded border border-violet-200 bg-white px-2 py-1 text-xs font-medium text-violet-900 hover:bg-violet-100"
                                            onClick={() => openSelectedStoreLinkModal(storeEntitlementMode(selectedQuotaSubscription), selectedQuotaSubscription)}
                                        >
                                            {isStoreEntitlementEnabled(selectedQuotaSubscription) ? '권한 제외' : '권한 복구'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {selectedQuotaSubscription.portOneRecurring && (
                            <section className="mt-4 rounded border border-emerald-200 bg-emerald-50/30 p-4">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <h4 className="text-sm font-medium text-slate-900">웹 자동결제</h4>
                                    <span className="text-xs font-medium text-emerald-800">
                                        {selectedQuotaSubscription.portOneRecurring.statusLabel || selectedQuotaSubscription.portOneRecurring.status || '-'}
                                    </span>
                                </div>
                                <dl className="mt-3 grid grid-cols-1 gap-x-5 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                                    <CompactDetail label="PG 채널" value={formatPortOneChannel(selectedQuotaSubscription.portOneRecurring)} />
                                    <CompactDetail label="요금" value={formatPortOneRecurringAmount(selectedQuotaSubscription.portOneRecurring)} />
                                    <CompactDetail label="자동 갱신" value={formatPortOneAutoRenew(selectedQuotaSubscription.portOneRecurring)} />
                                    <CompactDetail label="다음 결제" value={formatDateTimeMinute(selectedQuotaSubscription.portOneRecurring.nextPaymentAt)} />
                                    <CompactDetail label="최근 결제 성공" value={formatDateTimeMinute(selectedQuotaSubscription.portOneRecurring.lastSuccessAt)} />
                                    <CompactDetail
                                        label="다음 재시도"
                                        value={selectedQuotaSubscription.portOneRecurring.nextAttemptAt
                                            ? `${formatDateTimeMinute(selectedQuotaSubscription.portOneRecurring.nextAttemptAt)} (${selectedQuotaSubscription.portOneRecurring.retryCount || 0}/${selectedQuotaSubscription.portOneRecurring.maxRetryCount || 0})`
                                            : '-'}
                                    />
                                    <CompactDetail label="예약 플랜" value={formatPortOnePendingPlan(selectedQuotaSubscription.portOneRecurring)} />
                                    <CompactDetail label="종료 사유" value={portOneTerminationReasonLabel(selectedQuotaSubscription.portOneRecurring.terminationReason)} />
                                    <CompactDetail label="최근 주문" value={selectedQuotaSubscription.portOneRecurring.latestOrderNo || '-'} />
                                </dl>
                                {selectedQuotaSubscription.portOneRecurring.lastFailureMessage && (
                                    <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                                        결제 확인 필요: {selectedQuotaSubscription.portOneRecurring.lastFailureMessage}
                                        {selectedQuotaSubscription.portOneRecurring.lastFailureCode ? ` (${selectedQuotaSubscription.portOneRecurring.lastFailureCode})` : ''}
                                    </div>
                                )}
                            </section>
                        )}
                        <div className="mt-6 rounded border p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="text-sm font-medium">고객메모 저장한도</div>
                                    <div className="mt-1 text-xs text-slate-500">
                                        사용 {formatStorageSize(selectedQuotaSubscription.customerMemoStorageUsedBytes)} / 한도 {formatStorageSize(selectedQuotaSubscription.customerMemoStorageQuotaBytes)} / 잔여 {formatStorageSize(selectedQuotaSubscription.customerMemoStorageRemainingBytes)}
                                    </div>
                                    <div className="mt-1 text-xs text-slate-500">기준: {selectedQuotaSubscription.customerMemoStorageQuotaSourceLabel || '-'}</div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" disabled={customerMemoStorageQuotaLoading} onClick={() => setCustomerMemoStorageQuotaDraft('1')}>1GB</button>
                                    <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" disabled={customerMemoStorageQuotaLoading} onClick={() => setCustomerMemoStorageQuotaDraft('3')}>3GB</button>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
                                <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        className="w-full outline-none"
                                        value={customerMemoStorageQuotaDraft}
                                        onChange={(e) => setCustomerMemoStorageQuotaDraft(e.target.value)}
                                        placeholder="GB"
                                    />
                                    <span className="text-xs font-medium text-slate-500">GB</span>
                                </label>
                                <button type="button" className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={customerMemoStorageQuotaLoading} onClick={() => applyCustomerMemoStorageQuota(false)}>
                                    {customerMemoStorageQuotaLoading ? '저장 중...' : '저장'}
                                </button>
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50" disabled={customerMemoStorageQuotaLoading} onClick={() => applyCustomerMemoStorageQuota(true)}>
                                    기본값
                                </button>
                            </div>
                        </div>                        <div className="mt-6 rounded border p-4">
                            <div className="text-sm font-medium">기능별 Counter</div>
                            <div className="mt-3 max-h-[32vh] overflow-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                            <th className="px-3 py-2 text-left">기능</th>
                                            <th className="px-3 py-2 text-left">모드</th>
                                            <th className="px-3 py-2 text-left">한도</th>
                                            <th className="px-3 py-2 text-left">사용</th>
                                            <th className="px-3 py-2 text-left">잔여</th>
                                            <th className="px-3 py-2 text-left">액션</th>
                        </tr>
                    </thead>
                    <tbody>
                                        {(selectedQuotaSubscription.currentQuotaPeriod?.counters || []).map((counter) => (
                                            <tr key={counter.featureCode} className="border-t">
                                                <td className="px-3 py-2">{counter.featureName}</td>
                                                <td className="px-3 py-2"><StatusBadge value={counter.quotaMode} type="quota" /></td>
                                                <td className="px-3 py-2">{counter.quotaMode === 'UNLIMITED' ? '무제한' : counter.limitValue ?? 0}</td>
                                                <td className="px-3 py-2">{counter.usedValue ?? 0}</td>
                                                <td className="px-3 py-2">{counter.remainingValue === -1 ? '무제한' : counter.remainingValue ?? 0}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex flex-col gap-2">
                                                        <button
                                                            type="button"
                                                            className="rounded border px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                            disabled={quotaConsumeLoading || quotaLimitAdjustLoading || counter.quotaMode === 'NONE'}
                                                            onClick={() => consumeSubscriptionQuota(selectedQuotaSubscription.id, counter.featureCode)}
                                                        >
                                                            1회 차감
                                                        </button>
                                                        {counter.quotaMode === 'LIMITED' && (
                                                            <div className="flex flex-wrap items-center gap-1">
                                                                <input
                                                                    type="number"
                                                                    className="w-[4.5rem] rounded border px-1 py-0.5 text-xs"
                                                                    placeholder="±한도"
                                                                    value={quotaLimitDraft[counter.featureCode] ?? ''}
                                                                    onChange={(e) => setQuotaLimitDraft((prev) => ({
                                                                        ...prev,
                                                                        [counter.featureCode]: e.target.value,
                                                                    }))}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                                                                    disabled={quotaConsumeLoading || quotaLimitAdjustLoading}
                                                                    onClick={() => adjustSubscriptionQuotaLimitRow(selectedQuotaSubscription.id, counter.featureCode)}
                                                                >
                                                                    한도 반영
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                            </div>
                        </div>
                        <div className="mt-4 rounded border p-4">
                            <div className="text-sm font-medium">최근 사용 이력</div>
                            <div className="mt-3 space-y-2 text-xs text-slate-600">
                                {(selectedQuotaSubscription.currentQuotaPeriod?.recentEvents || []).length === 0 && (
                                    <div className="text-slate-500">사용 이력이 없습니다.</div>
                                )}
                                {(selectedQuotaSubscription.currentQuotaPeriod?.recentEvents || []).slice(0, 10).map((event) => (
                                    <div key={event.id} className="rounded bg-slate-50 px-3 py-2">
                                        {event.featureName} / {event.units} / {event.refType} / {formatDateTimeMinute(event.createdAt)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedTrialHistory && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setSelectedTrialHistory(null)}>
                    <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold">Trial 이력</h3>
                                <p className="mt-1 text-sm text-slate-500">{selectedTrialHistory.subscription.targetName} / {selectedTrialHistory.subscription.planName}</p>
                            </div>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setSelectedTrialHistory(null)}>닫기</button>
                        </div>
                        {trialHistoryLoading ? (
                            <div className="mt-6 text-sm text-slate-500">Trial 이력 로딩 중...</div>
                        ) : (
                            <div className="mt-6 max-h-[55vh] overflow-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                                            <th className="px-3 py-2 text-left">상태</th>
                                            <th className="px-3 py-2 text-left">유형</th>
                                            <th className="px-3 py-2 text-left">기간</th>
                                            <th className="px-3 py-2 text-left">소스</th>
                                            <th className="px-3 py-2 text-left">사유</th>
                                            <th className="px-3 py-2 text-left">생성시각</th>
                        </tr>
                    </thead>
                    <tbody>
                                        {selectedTrialHistory.rows.map((row) => (
                                            <tr key={row.id} className="border-t">
                                                <td className="px-3 py-2"><StatusBadge value={row.status} type="trial" /></td>
                                                <td className="px-3 py-2">{row.trialType || '-'}</td>
                                                <td className="px-3 py-2">{`${formatDate(row.startAt)} ~ ${formatDate(row.endAt)}`}</td>
                                                <td className="px-3 py-2">{row.grantSourceType || '-'}</td>
                                                <td className="px-3 py-2">{row.reason || '-'}</td>
                                                <td className="px-3 py-2">{formatDateTimeMinute(row.createdAt)}</td>
                            </tr>
                        ))}
                                        {selectedTrialHistory.rows.length === 0 && (
                                            <tr>
                                                <td className="px-3 py-8 text-center text-slate-500" colSpan={6}>Trial 이력이 없습니다.</td>
                                            </tr>
                                        )}
                    </tbody>
                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {bulkTrialModal && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setBulkTrialModal(null)}>
                    <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">{bulkTrialModal.revoke ? '일괄 Trial 회수' : '일괄 Trial 지급'}</h3>
                        <div className="mt-4 space-y-4 text-sm">
                <div>
                                <label className="mb-1 block text-slate-600">대상 건수</label>
                                <div className="rounded border bg-slate-50 px-3 py-2">{selectedSubscriptionIds.length}건</div>
                </div>
                            {!bulkTrialModal.revoke && (
                                <div>
                                    <label className="mb-1 block text-slate-600">부여 일수</label>
                                    <input type="number" className="w-full rounded border px-3 py-2" value={bulkTrialDays} onChange={(e) => setBulkTrialDays(e.target.value)} />
            </div>
                            )}
                            <div>
                                <label className="mb-1 block text-slate-600">사유</label>
                                <textarea className="w-full rounded border px-3 py-2" rows={3} value={bulkTrialReason} onChange={(e) => setBulkTrialReason(e.target.value)} />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setBulkTrialModal(null)}>취소</button>
                            <button className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={bulkTrialLoading} onClick={submitBulkTrial}>
                                {bulkTrialLoading ? '처리 중...' : '실행'}
                        </button>
            </div>
                    </div>
                </div>
            )}

            {storeLinkModal && (
                <div
                    className="fixed inset-0 bg-black/30 flex items-center justify-center p-4"
                    onClick={closeStoreLinkModal}
                >
                    <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">{storeLinkModeTitle(storeLinkModal.mode)}</h3>
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <label className="mb-1 block text-slate-600">대상 구독</label>
                                <div className="rounded border bg-slate-50 px-3 py-2">
                                    <div className="font-medium text-slate-900">{storeLinkModal.subscription.targetName}</div>
                                    <div className="mt-1 text-xs text-slate-500">
                                        {storeLinkModal.subscription.targetEmail || '-'} / {storeLinkModal.subscription.planName || storeLinkModal.subscription.planCode || '-'}
                                    </div>
                                    {storeLinkModal.subscription.storeEntitlementManaged && (
                                        <div className={`mt-1 text-xs font-medium ${storeEntitlementToneClass(storeLinkModal.subscription)}`}>
                                            서비스 권한: {storeLinkModal.subscription.storeEntitlementStatusLabel || (isStoreEntitlementEnabled(storeLinkModal.subscription) ? '포함' : '제외')}
                                            {storeLinkModal.subscription.storeDuplicateActive && (
                                                <span className="ml-1 text-[11px] font-normal text-amber-700">활성 {storeLinkModal.subscription.storeActiveSubscriptionCount || 0}건</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div>
                                    <label className="mb-1 block text-slate-600">상품 ID</label>
                                    <div className="rounded border bg-slate-50 px-3 py-2 text-xs">{storeLinkModal.subscription.storeProductId || '-'}</div>
                                </div>
                                <div>
                                    <label className="mb-1 block text-slate-600">스토어 식별 힌트</label>
                                    <div className="rounded border bg-slate-50 px-3 py-2 text-xs">
                                        {storeLinkModal.subscription.storeTransactionHint || storeLinkModal.subscription.storePurchaseTokenHint || storeLinkModal.subscription.storeAccountIdentifierHint || '-'}
                                    </div>
                                </div>
                            </div>
                            {storeLinkModal.mode === 'TRANSFER' && (
                                <div>
                                    <label className="mb-1 block text-slate-600">이관 대상 회원 ID 또는 이메일</label>
                                    <input
                                        className="w-full rounded border px-3 py-2"
                                        value={storeLinkTarget}
                                        onChange={(e) => setStoreLinkTarget(e.target.value)}
                                        placeholder="회원 ID 또는 이메일"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="mb-1 block text-slate-600">처리 사유</label>
                                <textarea className="w-full rounded border px-3 py-2" rows={3} value={storeLinkReason} onChange={(e) => setStoreLinkReason(e.target.value)} />
                            </div>
                            <div>
                                <label className="mb-1 block text-slate-600">확인 문구</label>
                                <input
                                    className="w-full rounded border px-3 py-2"
                                    value={storeLinkConfirmText}
                                    onChange={(e) => setStoreLinkConfirmText(e.target.value)}
                                    placeholder={storeLinkConfirmTextForMode(storeLinkModal.mode)}
                                />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="rounded border px-3 py-2 text-sm disabled:opacity-50" disabled={storeLinkLoading} onClick={closeStoreLinkModal}>취소</button>
                            <button className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={storeLinkLoading} onClick={submitStoreLinkAction}>
                                {storeLinkLoading ? '처리 중...' : '처리 실행'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {bulkUpdateModal && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => {
                    setBulkUpdateModal(null)
                    setBulkUpdateReason('')
                }}>
                    <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">{bulkUpdateModal.title}</h3>
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <label className="mb-1 block text-slate-600">변경 대상</label>
                                <div className="rounded border bg-slate-50 px-3 py-2">
                                    {selectedSubscriptions.slice(0, 5).map((item) => item.targetName).join(', ')}
                                    {selectedSubscriptions.length > 5 ? ` 외 ${selectedSubscriptions.length - 5}명` : ''}
                        </div>
                        </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="mb-1 block text-slate-600">대상 건수</label>
                                    <div className="rounded border bg-slate-50 px-3 py-2">{selectedSubscriptionIds.length}건</div>
                        </div>
                                <div>
                                    <label className="mb-1 block text-slate-600">변경 값</label>
                                    <div className="rounded border bg-slate-50 px-3 py-2">{bulkUpdateModal.nextValueLabel}</div>
                    </div>
                    </div>
                            <div>
                                <label className="mb-1 block text-slate-600">변경 사유</label>
                                <textarea
                                    className="w-full rounded border px-3 py-2"
                                    rows={4}
                                    value={bulkUpdateReason}
                                    onChange={(e) => setBulkUpdateReason(e.target.value)}
                                    placeholder="운영 변경 사유를 입력해주세요."
                                />
                </div>
                    </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => {
                                setBulkUpdateModal(null)
                                setBulkUpdateReason('')
                            }}>
                                취소
                            </button>
                            <button className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={bulkActionLoading} onClick={submitBulkUpdate}>
                                {bulkActionLoading ? '변경 중...' : '변경 실행'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {bulkComposeOpen && (
                <div
                    className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4"
                    onClick={() => {
                        if (!bulkComposeLoading) setBulkComposeOpen(false)
                    }}
                >
                    <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">선택 회원 일괄 적용</h3>
                        <p className="mt-1 text-xs text-slate-500">구독 상태·플랜·과금모델·구독일 조정·할당량 한도 조정을 한 번에 지정합니다. 비워 둔 항목은 변경하지 않습니다. billing이 준비된 선택 항목만 적용됩니다.</p>
                        <div className="mt-4 space-y-4 text-sm">
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">구독 상태 (선택)</label>
                                <select className="w-full rounded border px-3 py-2" value={bulkComposeStatus} onChange={(e) => setBulkComposeStatus(e.target.value)}>
                                    <option value="">변경 안 함</option>
                                    <option value="TRIAL">{formatBillingStatusLabel('TRIAL', 'subscription')}</option>
                                    <option value="ACTIVE">{formatBillingStatusLabel('ACTIVE', 'subscription')}</option>
                                    <option value="SUSPENDED">{formatBillingStatusLabel('SUSPENDED', 'subscription')}</option>
                                    <option value="EXPIRED">{formatBillingStatusLabel('EXPIRED', 'subscription')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">플랜 (선택)</label>
                                <select className="w-full rounded border px-3 py-2" value={bulkComposePlan} onChange={(e) => setBulkComposePlan(e.target.value)}>
                                    <option value="">변경 안 함</option>
                                    {plans.map((plan) => (
                                        <option key={plan.planCode} value={plan.planCode}>{plan.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">과금모델 (선택)</label>
                                <select className="w-full rounded border px-3 py-2" value={bulkComposeBillingMode} onChange={(e) => setBulkComposeBillingMode(e.target.value)}>
                                    <option value="">변경 안 함</option>
                                    <option value="STORE_IAP">인앱결제</option>
                                    <option value="INDIVIDUAL">개인 결제</option>
                                    <option value="WEB_PG">웹 결제</option>
                                    <option value="TRIAL">체험</option>
                                </select>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">구독일 조정 (일, 비우면 생략)</label>
                                <input
                                    type="number"
                                    className="w-full rounded border px-3 py-2"
                                    placeholder="예: 7 또는 -7"
                                    value={bulkComposeGrantDays}
                                    onChange={(e) => setBulkComposeGrantDays(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">할당량 한도 조정 (현재 과금 주 구독 행 기준)</label>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    <select
                                        className="w-full rounded border px-3 py-2"
                                        value={bulkComposeQuotaFeature}
                                        onChange={(e) => setBulkComposeQuotaFeature(e.target.value)}
                                    >
                                        <option value="">기능 선택 안 함</option>
                                        <option value="CLAIM_FAX">CLAIM_FAX (팩스)</option>
                                        <option value="GROUP_SMS">GROUP_SMS (문자)</option>
                                        <option value="HEALTH_ANALYSIS">HEALTH_ANALYSIS (분석)</option>
                                    </select>
                                    <input
                                        type="number"
                                        className="w-full rounded border px-3 py-2"
                                        placeholder="한도 증감 (예: 10, -5)"
                                        value={bulkComposeQuotaDelta}
                                        onChange={(e) => setBulkComposeQuotaDelta(e.target.value)}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-slate-500">기능과 증감값을 함께 넣어야 적용됩니다. 사용·예약을 밑돌 수 없습니다.</p>
                            </div>
                            <div>
                                <label className="mb-1 block font-medium text-slate-700">사유</label>
                                <textarea
                                    className="w-full rounded border px-3 py-2"
                                    rows={3}
                                    value={bulkComposeReason}
                                    onChange={(e) => setBulkComposeReason(e.target.value)}
                                    placeholder="운영자 일괄 적용"
                                />
                            </div>
                            <div className="rounded border bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                대상: billing 준비된 회원 <span className="font-semibold text-slate-900">{selectedSubscriptions.filter((s) => s.billingReady !== false).length}</span>명
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" className="rounded border px-3 py-2 text-sm" disabled={bulkComposeLoading} onClick={() => setBulkComposeOpen(false)}>취소</button>
                            <button type="button" className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={bulkComposeLoading} onClick={submitBulkCompose}>
                                {bulkComposeLoading ? '적용 중...' : '적용'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {withdrawnStoreSubscriptionsOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setWithdrawnStoreSubscriptionsOpen(false)}>
                    <div className="w-full max-w-5xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold">구독중인 탈퇴회원</h3>
                                <p className="mt-1 text-sm text-slate-500">탈퇴 처리된 계정에 활성 App Store 또는 Google Play 구독이 남아 있는 케이스입니다.</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                                    onClick={openWithdrawnStoreSubscriptionsModal}
                                    disabled={withdrawnStoreSubscriptionLoading}
                                >
                                    새로고침
                                </button>
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setWithdrawnStoreSubscriptionsOpen(false)}>닫기</button>
                            </div>
                        </div>

                        {withdrawnStoreSubscriptionError && (
                            <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{withdrawnStoreSubscriptionError}</div>
                        )}

                        <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            새 계정으로 구독 이전이 필요한 경우, 스토어 영수증과 본인 확인을 마친 뒤 운영자가 수동으로 처리합니다.
                        </div>

                        <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                            {withdrawnStoreSubscriptionLoading && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                    <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                                </div>
                            )}
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-4 py-3 text-left">회원</th>
                                        <th className="px-4 py-3 text-left">탈퇴일</th>
                                        <th className="px-4 py-3 text-left">스토어/상품</th>
                                        <th className="px-4 py-3 text-left">플랜</th>
                                        <th className="px-4 py-3 text-left">구독 만료</th>
                                        <th className="px-4 py-3 text-left">거래</th>
                                        <th className="px-4 py-3 text-left">상태</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {withdrawnStoreSubscriptions.map((item) => (
                                        <tr key={`${item.userId}-${item.subscriptionId}`} className="border-t align-top">
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">#{item.userId} {item.userName || '-'}</div>
                                                <div className="text-xs text-slate-500">{item.userEmail || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-700">{formatDateTimeMinute(item.withdrawnAt) || '-'}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{item.storeLabel || item.store || '-'}</div>
                                                <div className="text-xs text-slate-500">{item.productId || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div>{item.planName || '-'}</div>
                                                <div className="text-xs text-slate-500">{item.planCode || '-'} · 구독 #{item.subscriptionId}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div>{formatDate(item.endsAt) || '-'}</div>
                                                <div className="text-xs text-slate-500">{formatExpiryRemaining(item.endsAt)}</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-700">
                                                <div>{item.storeTransactionHint || '-'}</div>
                                                <div className="mt-1 text-slate-500">{item.orderNo || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <StatusBadge value={item.status} type="subscription" />
                                                <div className="mt-2 text-xs text-amber-700">{item.manualStatus || '확인 필요'}</div>
                                            </td>
                                        </tr>
                                    ))}
                                    {!withdrawnStoreSubscriptionLoading && withdrawnStoreSubscriptions.length === 0 && (
                                        <tr>
                                            <td className="px-4 py-8 text-center text-slate-500" colSpan={7}>활성 스토어 구독이 남아 있는 탈퇴회원이 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {iapNotificationModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setIapNotificationModalOpen(false)}>
                    <div className="w-full max-w-6xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold">스토어 알림 원문</h3>
                                <p className="mt-1 text-sm text-slate-500">최근 알림 {iapNotificationTotalCount}건 중 최신 30건을 확인합니다.</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50" onClick={loadIapNotificationEvents} disabled={iapNotificationLoading}>새로고침</button>
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setIapNotificationModalOpen(false)}>닫기</button>
                            </div>
                        </div>
                        <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                            {iapNotificationLoading && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                    <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                                </div>
                            )}
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-4 py-3 text-left">수신일시</th>
                                        <th className="px-4 py-3 text-left">스토어/상태</th>
                                        <th className="px-4 py-3 text-left">이벤트</th>
                                        <th className="px-4 py-3 text-left">거래/상품</th>
                                        <th className="px-4 py-3 text-left">원문</th>
                                        <th className="px-4 py-3 text-left">처리</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {iapNotificationEvents.map((event) => (
                                        <tr key={event.id} className="border-t align-top">
                                            <td className="px-4 py-3 text-xs text-slate-700">{formatDateTimeMinute(event.receivedAt) || '-'}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{event.storeLabel || event.store || '-'}</div>
                                                <StatusBadge value={event.status} />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div>{event.eventType || '-'}</div>
                                                <div className="text-xs text-slate-500">#{event.id}</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-slate-700">
                                                <div>{event.storeTransactionHint || event.originalTransactionId || event.purchaseToken || '-'}</div>
                                                <div className="mt-1 text-slate-500">{event.productId || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <pre className="max-h-40 max-w-xl overflow-auto rounded bg-slate-50 p-2 text-xs text-slate-700">{event.rawPayloadText || JSON.stringify(event.parsedPayload || event.rawPayload || {}, null, 2)}</pre>
                                                {event.errorMessage && <div className="mt-2 text-xs text-red-600">{event.errorMessage}</div>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <button
                                                    type="button"
                                                    className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
                                                    onClick={() => retryIapNotificationEvent(event.id)}
                                                    disabled={iapNotificationRetryingId === event.id}
                                                >
                                                    {iapNotificationRetryingId === event.id ? '재처리 중...' : '재처리'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {!iapNotificationLoading && iapNotificationEvents.length === 0 && (
                                        <tr>
                                            <td className="px-4 py-8 text-center text-slate-500" colSpan={6}>스토어 알림이 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {storeConflictModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setStoreConflictModalOpen(false)}>
                    <div className="w-full max-w-6xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-semibold">스토어 계정 충돌 의심</h3>
                                <p className="mt-1 text-sm text-slate-500">동일 스토어 거래 식별자가 여러 서비스 계정에 연결된 케이스입니다.</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50" onClick={openStoreAccountConflictsModal} disabled={storeConflictLoading}>새로고침</button>
                                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setStoreConflictModalOpen(false)}>닫기</button>
                            </div>
                        </div>
                        <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            총 {storeConflictSummary?.totalCount ?? storeConflictGroups.length}개 그룹입니다. 실제 이전/환불 처리는 영수증과 본인 확인 후 수동으로 처리합니다.
                        </div>
                        <div className="relative mt-4 min-h-[120px] overflow-x-auto rounded border">
                            {storeConflictLoading && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
                                    <span className="text-sm font-medium text-slate-600">목록 불러오는 중...</span>
                                </div>
                            )}
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-4 py-3 text-left">스토어/거래</th>
                                        <th className="px-4 py-3 text-left">연결 계정</th>
                                        <th className="px-4 py-3 text-left">상태</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {storeConflictGroups.map((group, idx) => (
                                        <tr key={group.conflictKey || idx} className="border-t align-top">
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{group.storeLabel || group.store || '-'}</div>
                                                <div className="text-xs text-slate-500">{group.storeTransactionHint || group.conflictKey || '-'}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {(group.items || []).map((item) => (
                                                    <div key={item.subscriptionId || `${item.ownerId}-${item.productId}`} className="mb-2 rounded border bg-white px-3 py-2 last:mb-0">
                                                        <div className="font-medium text-slate-900">{item.ownerName || '-'} <span className="text-xs text-slate-500">#{item.ownerId || '-'}</span></div>
                                                        <div className="text-xs text-slate-500">{item.ownerEmail || '-'}</div>
                                                        <div className="mt-1 text-xs text-slate-500">{item.planName || item.planCode || '-'} · {item.productId || '-'}</div>
                                                    </div>
                                                ))}
                                            </td>
                                            <td className="px-4 py-3">
                                                {(group.items || []).map((item) => (
                                                    <div key={item.subscriptionId || `${item.ownerId}-status`} className="mb-2 last:mb-0">
                                                        <StatusBadge value={item.status} type="subscription" />
                                                        <div className="mt-1 text-xs text-slate-500">만료 {formatDate(item.endsAt) || '-'}</div>
                                                    </div>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                    {!storeConflictLoading && storeConflictGroups.length === 0 && (
                                        <tr>
                                            <td className="px-4 py-8 text-center text-slate-500" colSpan={3}>스토어 계정 충돌 의심 케이스가 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {billingBootstrapModal && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => {
                    setBillingBootstrapModal(null)
                    setBillingBootstrapConfirmText('')
                }}>
                    <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold">누락 billing 생성</h3>
                        <div className="mt-4 space-y-4 text-sm">
                            <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                                운영환경에서도 실행 가능한 기능입니다. 기존 회원 중 `billing_account` 또는 기본 `subscription` 이 없는 사용자에게만 생성합니다.
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="mb-1 block text-slate-600">처리 상한</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20000"
                                        className="w-full rounded border px-3 py-2"
                                        value={billingBootstrapModal.limit}
                                        onChange={(e) => setBillingBootstrapModal((prev) => ({ ...prev, limit: Math.max(1, Math.min(20000, Number(e.target.value) || 5000)) }))}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-slate-600">실행 확인 문구</label>
                                    <input
                                        className="w-full rounded border px-3 py-2"
                                        value={billingBootstrapConfirmText}
                                        onChange={(e) => setBillingBootstrapConfirmText(e.target.value)}
                                        placeholder="BOOTSTRAP"
                                    />
                                </div>
                            </div>
                            <div className="text-xs text-slate-500">
                                권장 순서: 먼저 `누락 billing dry-run` 으로 누락 건수를 확인한 뒤 실제 생성을 실행하세요.
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => {
                                setBillingBootstrapModal(null)
                                setBillingBootstrapConfirmText('')
                            }}>
                                취소
                            </button>
                            <button className="rounded bg-brand-navy px-3 py-2 text-sm text-white disabled:opacity-50" disabled={billingBootstrapLoading} onClick={submitBillingBootstrap}>
                                {billingBootstrapLoading ? '생성 중...' : '생성 실행'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {billingBootstrapResult && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4" onClick={() => setBillingBootstrapResult(null)}>
                    <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded bg-white p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between gap-3">
                    <div>
                                <h3 className="text-lg font-semibold">{billingBootstrapResult.mode === 'dryRun' ? '누락 billing dry-run 결과' : '누락 billing 생성 결과'}</h3>
                                <p className="mt-1 text-sm text-slate-500">
                                    누락 대상 총 {billingBootstrapResult.payload?.totalMissingUsers || 0}명 중 이번 실행 {billingBootstrapResult.payload?.scannedMissingUsers || 0}명 처리
                                    {billingBootstrapResult.payload?.hasMore ? ` / 상한 ${billingBootstrapResult.payload?.limit || 0}건 적용` : ''}
                                </p>
                        </div>
                            <button className="rounded border px-3 py-2 text-sm" onClick={() => setBillingBootstrapResult(null)}>닫기</button>
                    </div>
                        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                            <InfoBlock compact label="누락 계정" value={`${billingBootstrapResult.payload?.missingAccounts || 0}건`} />
                            <InfoBlock compact label="누락 구독" value={`${billingBootstrapResult.payload?.missingSubscriptions || 0}건`} />
                            <InfoBlock compact label="생성 계정" value={`${billingBootstrapResult.payload?.createdAccounts || 0}건`} />
                            <InfoBlock compact label="생성 구독" value={`${billingBootstrapResult.payload?.createdSubscriptions || 0}건`} />
                        </div>
                        {(billingBootstrapResult.payload?.failedUsers || 0) > 0 && (
                            <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                                실패 {billingBootstrapResult.payload?.failedUsers || 0}건이 있어 결과 목록에서 확인이 필요합니다.
                    </div>
                        )}
                        <div className="mt-5 max-h-[45vh] overflow-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                    <tr>
                                        <th className="px-3 py-2 text-left">이름</th>
                                        <th className="px-3 py-2 text-left">이메일</th>
                                        <th className="px-3 py-2 text-left">누락 계정</th>
                                        <th className="px-3 py-2 text-left">누락 구독</th>
                                        <th className="px-3 py-2 text-left">결과</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(billingBootstrapResult.payload?.items || []).slice(0, 50).map((item) => (
                                        <tr key={`bootstrap-${item.userId}`} className="border-t">
                                            <td className="px-3 py-2">{item.userName || '-'}</td>
                                            <td className="px-3 py-2">{item.userEmail || '-'}</td>
                                            <td className="px-3 py-2">{item.missingAccount ? '예' : '아니오'}</td>
                                            <td className="px-3 py-2">{item.missingSubscription ? '예' : '아니오'}</td>
                                            <td className="px-3 py-2">{item.success === false ? (item.error || '실패') : '성공'}</td>
                                        </tr>
                                    ))}
                                    {(billingBootstrapResult.payload?.items || []).length === 0 && (
                                        <tr>
                                            <td className="px-3 py-8 text-center text-slate-500" colSpan={5}>누락된 회원이 없습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {(billingBootstrapResult.payload?.items || []).length > 50 && (
                            <div className="mt-3 text-xs text-slate-500">
                                결과는 상위 50건만 표시했습니다.
                </div>
            )}
                    </div>
                </div>
            )}
        </section>
    )
}

function DashboardMetricCard({ label, value, change = null, meta = '', alert = false }) {
    const numericChange = Number(change)
    const hasChange = change !== null && change !== undefined && Number.isFinite(numericChange)
    const changeTone = numericChange > 0 ? 'text-emerald-700' : numericChange < 0 ? 'text-rose-700' : 'text-slate-500'
    return (
        <div className={`min-h-[92px] rounded border bg-white p-3 ${alert ? 'border-amber-300' : 'border-slate-200'}`}>
            <div className="truncate text-xs font-medium text-slate-500" title={label}>{label}</div>
            <div className="mt-1 truncate text-xl font-semibold text-slate-950" title={String(value)}>{value}</div>
            <div className="mt-1 flex min-h-4 items-center gap-1.5 truncate text-[11px] text-slate-500">
                {hasChange && <span className={`font-medium ${changeTone}`}>{numericChange > 0 ? '+' : ''}{numericChange.toFixed(1)}%</span>}
                {meta && <span className="truncate">{meta}</span>}
            </div>
        </div>
    )
}

function DashboardPanel({ title, meta = '', className = '', children }) {
    return (
        <section className={`min-w-0 rounded border border-slate-200 bg-white p-4 ${className}`}>
            <div className="flex min-h-6 items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
                {meta && <span className="truncate text-xs text-slate-500">{meta}</span>}
            </div>
            <div className="mt-3">{children}</div>
        </section>
    )
}

function DashboardCompactList({ items = [], empty = '' }) {
    if (items.length === 0) {
        return <div className="py-6 text-center text-sm text-slate-500">{empty}</div>
    }
    return (
        <div className="divide-y divide-slate-100">
            {items.map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <span className="min-w-0 truncate text-slate-600">{item.label}</span>
                    <span className={`shrink-0 text-right font-medium ${item.alert ? 'text-amber-700' : 'text-slate-900'}`}>
                        {item.value}
                        {item.subvalue && <span className="ml-1 text-[11px] font-normal text-slate-400">{item.subvalue}</span>}
                    </span>
                </div>
            ))}
        </div>
    )
}

function DashboardInlineMetric({ label, value }) {
    return (
        <div className="min-w-0 px-2">
            <div className="truncate text-[11px] text-slate-500">{label}</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{Number(value || 0).toLocaleString()}</div>
        </div>
    )
}
function DashboardComparisonRow({ label, baseline, change }) {
    const numericChange = Number(change || 0)
    const changeTone = numericChange > 0 ? 'text-emerald-700' : numericChange < 0 ? 'text-rose-700' : 'text-slate-500'
    return (
        <div className="flex items-center justify-between gap-3 py-2.5">
            <div className="min-w-0">
                <div className="truncate text-xs font-medium text-slate-600">{label}</div>
                <div className="mt-0.5 truncate text-[11px] text-slate-400">기준 {currency(Number(baseline || 0))}</div>
            </div>
            <span className={`shrink-0 text-sm font-semibold ${changeTone}`}>
                {numericChange > 0 ? '+' : ''}{numericChange.toFixed(1)}%
            </span>
        </div>
    )
}

function DashboardProgressBar({ label, value, detail = '' }) {
    const numericValue = Math.max(0, Number(value || 0))
    const barWidth = Math.min(100, numericValue)
    return (
        <div>
            <div className="flex items-center justify-between gap-3 text-xs">
                <span className="text-slate-500">{label}</span>
                <strong className="text-slate-900">{formatDashboardPercent(numericValue)}</strong>
            </div>
            <div
                className="mt-1.5 h-1.5 overflow-hidden rounded bg-slate-100"
                role="progressbar"
                aria-label={label}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.min(100, numericValue)}
            >
                <div className="h-full rounded bg-teal-600" style={{ width: `${barWidth}%` }} />
            </div>
            {detail && <div className="mt-1 truncate text-[11px] text-slate-400">{detail}</div>}
        </div>
    )
}

function DashboardInlineValue({ label, value, subvalue = '' }) {
    return (
        <div className="min-w-0">
            <div className="truncate text-[11px] text-slate-500">{label}</div>
            <div className="mt-1 truncate text-sm font-semibold text-slate-900" title={String(value)}>{value}</div>
            {subvalue && <div className="mt-0.5 truncate text-[10px] text-slate-400">{subvalue}</div>}
        </div>
    )
}

function CompactDetail({ label, value }) {
    return (
        <div className="min-w-0 border-b border-slate-200/70 pb-2">
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd className="mt-1 break-words font-medium text-slate-800">{value || '-'}</dd>
        </div>
    )
}

function portOnePgProviderLabel(value) {
    const normalized = String(value || '').trim().toUpperCase()
    return {
        KCP: 'NHN KCP',
        NHN_KCP: 'NHN KCP',
        KPN: '한국결제네트웍스',
        KOREA_PAYMENT_NETWORKS: '한국결제네트웍스',
    }[normalized] || value || '-'
}

function formatPortOneChannel(recurring) {
    const provider = portOnePgProviderLabel(recurring?.pgProvider)
    const channelCode = String(recurring?.channelCode || '').trim()
    if (provider === '-' && !channelCode) return '-'
    if (!channelCode) return provider
    if (provider === '-') return channelCode
    return `${provider} · ${channelCode}`
}

function formatPortOneRecurringAmount(recurring) {
    const amount = Number(recurring?.amount)
    if (!Number.isFinite(amount)) return '-'
    const currencyCode = String(recurring?.currency || 'KRW').toUpperCase()
    return currencyCode === 'KRW'
        ? `₩${amount.toLocaleString()}`
        : `${amount.toLocaleString()} ${currencyCode}`
}

function formatPortOneAutoRenew(recurring) {
    if (recurring?.cancelAtPeriodEnd) return '현재 기간 종료 후 해지'
    return recurring?.autoRenewEnabled ? '사용' : '중지'
}

function formatPortOnePendingPlan(recurring) {
    const plan = recurring?.pendingPlanName || recurring?.pendingPlanCode
    if (!plan) return '-'
    const effectiveAt = formatDateTimeMinute(recurring?.pendingPlanEffectiveAt)
    return effectiveAt ? `${plan} · ${effectiveAt}` : plan
}

function portOneTerminationReasonLabel(value) {
    const normalized = String(value || '').trim().toUpperCase()
    return {
        USER_REQUEST: '사용자 해지 요청',
        USER_WITHDRAWAL: '회원 탈퇴',
        USER_WITHDRAWN: '탈퇴 회원',
        STORE_IAP_CHANNEL_SWITCH: '인앱결제로 전환',
        STORE_IAP_ACTIVE: '인앱결제 활성 구독 감지',
        ORG_CONTRACT_ACTIVATED: '조직계약 적용',
        ORG_CONTRACT_ACTIVE: '조직계약 활성 감지',
        FULL_REFUND: '전액 환불',
        ADMIN_FULL_REFUND: '관리자 전액 환불',
        PG_WEBHOOK_TERMINAL: 'PG 환불·취소 알림',
        ADMIN_BILLING_MODE_CHANGED: '관리자 과금모델 변경',
        ADMIN_SUBSCRIPTION_EXPIRED: '관리자 구독 만료 처리',
        ADMIN_SUBSCRIPTION_CANCELED: '관리자 구독 취소 처리',
        ADMIN_SUBSCRIPTION_REFUNDED: '관리자 환불 처리',
    }[normalized] || value || '-'
}

function orderProductLabel(order) {
    return [order?.productName, order?.planName]
        .map((value) => String(value || '').trim())
        .find(Boolean) || '-'
}

function orderPgLabel(order) {
    const pgProvider = String(order?.pgProvider || order?.gatewayPgProvider || '').trim()
    const normalized = pgProvider.toUpperCase()
    if (['APP_STORE', 'APPLE', 'APPLE_APP_STORE'].includes(normalized)) return 'App Store'
    if (['PLAY_STORE', 'GOOGLE_PLAY', 'GOOGLE'].includes(normalized)) return 'Google Play'
    if (pgProvider) return portOnePgProviderLabel(pgProvider)
    return order?.gatewayProvider ? gatewayProviderLabel(order.gatewayProvider) : '-'
}

function gatewayProviderLabel(value) {
    return String(value || '').toLowerCase() === 'portone' ? '포트원' : value || '-'
}

function gatewayPaymentModeLabel(value) {
    return {
        ONE_TIME: '단건결제',
        RECURRING: '정기결제',
    }[String(value || '').toUpperCase()] || value || '-'
}

function InfoBlock({ label, value, valueNode = null, compact = false }) {
    return (
        <div className={`rounded border ${compact ? 'p-3' : 'p-4'}`}>
            <div className="text-slate-500">{label}</div>
            <div className="mt-2 font-medium break-all">{valueNode || value}</div>
                        </div>
    )
}

function FormField({ label, children }) {
    return (
        <div>
            <label className="mb-1 block text-slate-600">{label}</label>
            {children}
                    </div>
    )
}

function AdminTable({ title, columns, rows, emptyText }) {
    return (
        <div className="mt-6 rounded border bg-white p-5">
            <h2 className="text-lg font-semibold">{title}</h2>
            <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            {columns.map((column) => <th key={column} className="px-4 py-3 text-left">{column}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={`${title}-${rowIndex}`} className="border-t">
                                {row.map((cell, cellIndex) => <td key={`${title}-${rowIndex}-${cellIndex}`} className="px-4 py-3">{cell}</td>)}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td className="px-4 py-8 text-center text-slate-500" colSpan={columns.length}>{emptyText}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                        </div>
                            </div>
    )
}

function TablePager({ totalCount, page, pageSize, totalPages, onPrev, onNext }) {
    if (totalCount <= 0) return null
    return (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
            <div>
                {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} / {totalCount}
                        </div>
            <div className="flex items-center gap-2">
                <button
                    className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
                    disabled={page <= 1}
                    onClick={onPrev}
                >
                    이전
                </button>
                <div className="min-w-[96px] text-center">
                    {page} / {totalPages} 페이지
                        </div>
                <button
                    className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
                    disabled={page >= totalPages}
                    onClick={onNext}
                >
                    다음
                </button>
                    </div>
                </div>
    )
}

function formatDashboardPercent(value) {
    const number = Number(value || 0)
    return `${number.toFixed(1)}%`
}

function compactDashboardCurrency(value) {
    const number = Number(value || 0)
    if (Math.abs(number) >= 100000000) return `${(number / 100000000).toFixed(1)}억`
    if (Math.abs(number) >= 10000) return `${Math.round(number / 10000).toLocaleString()}만`
    return number.toLocaleString()
}

function formatDashboardPeriodRange(period = {}) {
    const from = formatDate(period.from)
    const to = formatDate(period.to)
    if (!from || !to) return '한국시간 기준'
    return `${period.label || '선택 기간'} · ${from}~${to}`
}

function formatDashboardTrendDate(value) {
    const text = String(value || '')
    return text.length >= 10 ? text.slice(5).replace('-', '.') : text
}

function dashboardTrendLabel(value) {
    return {
        collectedAmount: '순수납',
        iapAmount: '개인 결제',
        orgPaidAmount: '조직 수납',
        refundAmount: '환불',
    }[value] || value
}

function formatDashboardChange(value, label) {
    const number = Number(value || 0)
    return `${label} ${number > 0 ? '+' : ''}${number.toFixed(1)}%`
}

function dashboardPaceLabel(value) {
    const number = Number(value || 0)
    if (number >= 105) return '비교 기간보다 빠름'
    if (number >= 95) return '비교 기간과 비슷함'
    return '비교 기간보다 느림'
}

function dashboardChannelLabel(value) {
    return {
        APP_STORE: 'App Store',
        PLAY_STORE: 'Play Store',
        WEB_PG: '웹 PG',
        WEB_PG_KCP: '웹 PG · NHN KCP',
        WEB_PG_KPN: '웹 PG · 한국결제네트웍스',
        ORG_CONTRACT: '조직 계약',
        MANUAL: '운영자·기타',
    }[value] || value || '-'
}
function currency(value) {
    if (typeof value !== 'number') return '-'
    return `₩${value.toLocaleString()}`
}

function csvEscape(value) {
    const normalized = String(value ?? '')
    if (normalized.includes('"') || normalized.includes(',') || normalized.includes('\n')) {
        return `"${normalized.replace(/"/g, '""')}"`
    }
    return normalized
}

function downloadCsvFile(rows, filename) {
    const csv = '\uFEFF' + rows.map((row) => row.map(csvEscape).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
}

function sortButtonClass(currentKey, columnKey) {
    const isActive = currentKey === columnKey
    return `inline-flex items-center gap-1 rounded px-2 py-1 transition ${isActive ? 'bg-slate-200 font-semibold text-slate-900' : 'hover:bg-slate-100 hover:text-slate-900'}`
}

function sortMark(currentKey, currentDirection, columnKey) {
    if (currentKey !== columnKey) return '↕'
    return currentDirection === 'asc' ? '▲' : '▼'
}

function formatSubscriptionExpiry(value) {
    if (!value) return '만료일 없음'
    return `${formatDate(value) || '-'} ${formatExpiryRemaining(value)}`
}

function formatExpiryRemaining(value) {
    if (!value) return '만료일 없음'
    const diff = diffInDays(value)
    if (diff === null) return '-'
    if (diff > 0) return `D-${diff}`
    if (diff === 0) return 'D-Day'
    return `만료 ${Math.abs(diff)}일 경과`
}

function diffInDays(value) {
    if (!value) return null
    const target = new Date(value)
    if (Number.isNaN(target.getTime())) return null
    const today = new Date()
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const startOfTarget = new Date(target.getFullYear(), target.getMonth(), target.getDate())
    return Math.round((startOfTarget.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24))
}

function renderExternalLink(url, label) {
    if (!url) return '-'
    return (
        <a className="text-xs text-brand-navy underline" href={url} target="_blank" rel="noreferrer">
            {label}
        </a>
    )
}

function prettyJson(value) {
    try {
        return JSON.stringify(value ?? {}, null, 2)
    } catch (e) {
        return String(value ?? '{}')
    }
}

function createInitialRewardForm() {
    return {
        rewardType: 'POINT_GRANT',
        targetRole: 'REDEEMER',
        value: '',
        expireDays: '',
    }
}

function createInitialPromotionForm() {
    return {
        promotionType: 'COUPON',
        triggerType: 'CODE_REDEEM',
        code: '',
        name: '',
        description: '',
        status: 'ACTIVE',
        perUserLimit: '',
        totalLimit: '',
        rewards: [createInitialRewardForm()],
    }
}

function formatRewardLabel(reward) {
    const type = reward?.rewardType || '-'
    const value = reward?.valueInt ?? reward?.value ?? 0
    const targetRole = reward?.targetRole || 'REDEEMER'
    const expireDays = reward?.valueJson?.expireDays
    const suffix = type.includes('POINT') ? 'P' : type.includes('DISCOUNT') ? (type === 'PERCENT_DISCOUNT' ? '%' : '원') : '일'
    const expireText = expireDays ? ` / 만료 ${expireDays}일` : ''
    return `${formatPromotionRewardTargetRoleLabel(targetRole)} · ${formatPromotionRewardTypeLabel(type)} ${value}${suffix}${expireText}`
}

function formatCouponReservation(order) {
    const couponCode = order?.pricingContext?.couponCode || order?.couponReservation?.code
    if (!couponCode) return '-'
    return `${couponCode} / ${formatCouponReservationStatus(order?.couponReservation?.status)}`
}

function formatCouponReservationStatus(status) {
    if (status === 'RESERVED') return '예약'
    if (status === 'USED') return '사용'
    if (status === 'CANCELLED') return '해제'
    if (status === 'EXPIRED') return '만료'
    return status || '-'
}

function StatusBadge({ value, type = 'default' }) {
    const text = formatBillingStatusLabel(value, type)
    const tone = billingStatusToneClass(value, type)
    return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${tone}`}>{text}</span>
}

function QuotaSummaryList({ item }) {
    const summary = Array.isArray(item?.quotaSummary) ? item.quotaSummary : []
    const visible = summary.filter((row) => row?.enabled !== false && row?.quotaMode !== 'NONE').slice(0, 4)
    if (visible.length === 0) {
        return (
            <>
                <div>팩스 잔여 {formatLimit(item.claimFaxRemaining)} / 예약 {formatLimit(item.claimFaxReserved)} / 사용 {formatLimit(item.claimFaxUsed)}</div>
                <div className="mt-1">문자 잔여 {formatLimit(item.groupSmsRemaining)} / 분석 잔여 {formatLimit(item.healthAnalysisRemaining)}</div>
            </>
        )
    }
    return (
        <div className="space-y-1">
            {visible.map((row) => (
                <div key={row.featureCode} className="flex min-w-[16rem] items-center justify-between gap-3">
                    <span className="max-w-[9rem] truncate text-slate-700">{row.featureName || row.featureCode}</span>
                    <span className="whitespace-nowrap text-slate-900">{formatQuotaUsage(row)}</span>
                </div>
            ))}
            {summary.length > visible.length && (
                <div className="text-slate-400">외 {summary.length - visible.length}개 기능</div>
            )}
        </div>
    )
}

function CustomerMemoStorageQuotaSummary({ item }) {
    const quota = item?.customerMemoStorageQuota || {}
    const limitBytes = item?.customerMemoStorageQuotaBytes ?? quota.maxUserAttachmentBytes
    const usedBytes = item?.customerMemoStorageUsedBytes ?? quota.userAttachmentBytes
    const remainingBytes = item?.customerMemoStorageRemainingBytes ?? quota.userAttachmentRemainingBytes
    return (
        <div className="min-w-[11rem] space-y-1">
            <div>한도 {formatStorageSize(limitBytes)}</div>
            <div className="text-slate-500">사용 {formatStorageSize(usedBytes)} / 잔여 {formatStorageSize(remainingBytes)}</div>
            <div className="text-slate-400">{item?.customerMemoStorageQuotaSourceLabel || quota.quotaSourceLabel || '-'}</div>
        </div>
    )
}
function formatQuotaUsage(row) {
    if (row.quotaMode === 'UNLIMITED') {
        return `무제한 / 사용 ${row.usedValue ?? 0}`
    }
    return `잔여 ${formatLimit(row.remainingValue ?? 0)} / 예약 ${row.reservedValue ?? 0} / 사용 ${row.usedValue ?? 0}`
}

function formatLimit(value) {
    return value === -1 ? '무제한' : value
}
function formatStorageSize(value) {
    const bytes = Number(value)
    if (!Number.isFinite(bytes) || bytes < 0) return '-'
    if (bytes === 0) return '0B'
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let size = bytes
    let unitIndex = 0
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex += 1
    }
    const digits = size >= 10 || unitIndex === 0 ? 0 : 1
    return `${size.toFixed(digits)}${units[unitIndex]}`
}

function storageQuotaGbDraft(bytes) {
    const n = Number(bytes)
    if (!Number.isFinite(n) || n <= 0) return ''
    return String(Math.max(1, Math.round(n / (1024 * 1024 * 1024))))
}
